import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { SectionedHtml } from "./ResponseDocument";
import { useAuth } from "../../context/AuthContext";
import { useNotification } from "../../context/NotificationContext";
import { useTheme } from "../../context/ThemeContext";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Info,
  PanelLeft,
  PanelLeftClose,
  MessageSquare,
  Search,
  FolderPlus,
  MoreHorizontal,
  Settings,
  Moon,
  Sun,
  Paperclip,
  Loader,
  Check,
  FileText,
  X,
  XCircle,
  Pencil,
  Trash2,
  ArrowRight,
  ChevronDown,
  Lock,
  Folder,
  Archive,
  Plus,
} from "lucide-react";
import axios from "axios";
import { useIntersectionObserver } from "../../hooks/useIntersectionObserver";
import LazySection from "../LazySection";
import {
  TabContentSkeleton,
  HeaderSkeleton,
  QueryInputSkeleton,
  ModelButtonsSkeleton,
  SkeletonLoader,
  SidebarChatSkeleton,
} from "../SkeletonLoader";
import "react-quill/dist/quill.snow.css";
import "./Dashboard.css";
import "../../styles/research-redesign.css";
import "../../styles/research-workspace.css";
import Header from "../Header";
import SEO from "../SEO";

// Component imports
import QueryInput from "./QueryInput";
import VideoWidget from "../VideoWidget";
import Footer from "../Footer";
import ModelSelector from "./ModelSelector";
import ResponsesGrid from "./ResponsesGrid";
import ResearchStageNav from "./ResearchStageNav";
import ResearchModelAvatars from "./ResearchModelAvatars";
import ResearchProgress from "./ResearchProgress";
import ChatSidebar from "./ChatSidebar";
import SearchDialog from "./SearchDialog";
import EditorModal from "./EditorModal";
// import ComparisonTab from "./ComparisonTab";
import SynthesisTab from "./SynthesisTab";

// Hook imports
import { useChats } from "../../hooks/useChats";
import { useAIQuery } from "../../hooks/useAIQuery";
import { useResponses } from "../../hooks/useResponses";
import { useFileUpload } from "../../hooks/useFileUpload";
import { useChatFiles } from "../../hooks/useChatFiles";
import { useEditor } from "../../hooks/useEditor";

// Utility imports
import {
  convertTextToHtml,
  removeCitationMarkers,
} from "../../utils/textProcessing";
import {
  formatHistoryTimestamp,
  getHistoryDateStr,
  groupChatsByDate,
} from "../../utils/dateUtils";
import { handleDownloadPDF, handleDownloadDoc } from "../../utils/exportUtils";
import { needsProfileCompletion } from "../../utils/profileCompletion";
import {
  shouldBlockSubscriptionAccess,
  isSubscriptionPaymentWarning,
  getSubscriptionPaymentMessage,
  getMaxFilesPerQuery,
} from "../../utils/subscriptionAccess";

// Service imports
import {
  updateChat,
  fetchChatsService,
  loadChatService,
  deleteChatService,
  pollChat,
} from "../../services/chatService";
import { fetchProjects, createProject, updateProject, archiveProject, deleteProject, fetchArchivedProjects } from "../../services/projectService";
import { restoreFile, deleteFile } from "../../services/fileService";
import NewProjectModal from "../NewProjectModal";
import RenameChatDialog from "./RenameChatDialog";
import RenameProjectDialog from "./RenameProjectDialog";
import DeleteProjectDialog from "./DeleteProjectDialog";
import ArchivedProjectsView from "./ArchivedProjectsView";
import ProjectContextMenu from "./ProjectContextMenu";
import AddToProjectDialog from "./AddToProjectDialog";
import UsageCard from "./UsageCard";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const USAGE_REFRESH_EVENT = "lawfusion-ai-usage-refresh";
const USAGE_REFRESH_STORAGE_KEY = "lawfusion-ai-usage-refresh-ts";
const shouldAutoFocusQueryInput = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const coarsePointer =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;
  const touchCapable =
    navigator.maxTouchPoints > 0 ||
    "ontouchstart" in window;

  return !(coarsePointer || touchCapable);
};

const DEFAULT_COMPARISON_QUERY =
  "Analyze the AI responses and provide a tactical comparative analysis for litigation planning. Identify where the models agree and disagree on legal strategy, risk assessment, and recommended actions. Rank critical vulnerabilities by litigation impact and recommend which position is strongest based on legal principles and factual support. Be direct and actionable—focus on what needs to be strengthened, corrected, or mitigated. Identify which AI provided the most useful analysis for this specific legal issue.";
const DEFAULT_SYNTHESIS_QUERY =
  "Produce a final legal synthesis for litigation planning using ONLY the AI responses from the Initial Query and, if available, the Comparative Analysis. Do not introduce new facts, cases, statutes, or legal theories not mentioned by the models. Analyze the issues, apply the relevant legal principles, and provide a clear, attorney-ready assessment of the client's position. Identify the primary and secondary vulnerabilities, assess whether the position is STRONG, MODERATE, or WEAK, and provide specific corrective actions to strengthen the case. Be direct and practical—focus on what will materially impact litigation outcome. Structure the response using IRAC+ format with clear section headings and lawyer-friendly language.";

export default function Dashboard() {
  const { user, logout, isAuthenticated } = useAuth();
  // Convenience flag — anonymous (free tier) users have no auth token
  const isLoggedIn = !!isAuthenticated;
  // True while the user is actively within their trial window (testers excluded)
  const isTrial =
    !user?.is_tester &&
    (user?.subscriptionStatus === "trialing" || !isAuthenticated);
  const { showError, showSuccess, showWarning, showInfo, confirm } =
    useNotification();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // The research workspace lives at /research.
  const workspaceBasePath = "/research";
  useEffect(() => {
    const hasPaymentIssue = shouldBlockSubscriptionAccess(user);
    const hasActiveAccess =
      !hasPaymentIssue &&
      (user?.is_tester ||
        user?.subscriptionStatus === "active" ||
        user?.subscriptionStatus === "trialing" ||
        (user?.aiMonthlyLimit || 0) > 0 ||
        (user?.adminAllocatedActionsTotal || 0) > 0);

    // Enforce flow order: pricing/access gate first, profile completion second.
    if (isAuthenticated && hasActiveAccess && needsProfileCompletion(user)) {
      navigate("/complete-profile", { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    let isActive = true;
    let statsStream = null;

    const fetchResearchAnalysesStats = () => {
      fetch(`${API_URL}/stats/research-analyses-completed`, {
        credentials: "omit",
      })
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((data) => {
          if (!isActive) return;
          if (typeof data?.count === "number" && data.count >= 0) {
            setResearchAnalysesCount(data.count);
            setResearchAnalysesIsMock(!!data.isMock);
            setResearchAnalysesFetchFailed(false);
          } else {
            setResearchAnalysesCount(null);
            setResearchAnalysesIsMock(false);
            setResearchAnalysesFetchFailed(true);
          }
        })
        .catch(() => {
          if (!isActive) return;
          setResearchAnalysesCount(null);
          setResearchAnalysesIsMock(false);
          setResearchAnalysesFetchFailed(true);
        });
    };

    fetchResearchAnalysesStats();
    statsStream = new EventSource(`${API_URL}/stats/live`);
    statsStream.addEventListener("stats", (event) => {
      if (!isActive) return;
      try {
        const data = JSON.parse(event.data);
        if (
          typeof data?.researchAnalysesCompleted === "number" &&
          data.researchAnalysesCompleted >= 0
        ) {
          setResearchAnalysesCount(data.researchAnalysesCompleted);
          setResearchAnalysesIsMock(!!data.researchAnalysesIsMock);
          setResearchAnalysesFetchFailed(false);
        }
      } catch (_) {
        // Keep last known value if stream payload is malformed.
      }
    });
    statsStream.onerror = () => {
      if (!isActive) return;
      fetchResearchAnalysesStats();
    };

    return () => {
      isActive = false;
      if (statsStream) {
        statsStream.close();
      }
    };
  }, []);

  const { id } = useParams(); // Get chat ID from URL parameter
  // Below 900px the rail is an off-canvas drawer (research-workspace.css), so
  // it starts closed there. matchMedia, not innerWidth: on iOS Safari
  // innerWidth reports the zoomed layout width, which can read as "desktop"
  // on a phone and open the drawer over the whole page.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => !window.matchMedia("(max-width: 900px)").matches,
  );
  const [accountOpen, setAccountOpen] = useState(false);
  const [subscriptionPaymentBlocked, setSubscriptionPaymentBlocked] =
    useState(false);
  const [subscriptionPaymentMessage, setSubscriptionPaymentMessage] =
    useState("");
  const paymentFailedToastShownRef = useRef(false);
  const paymentGraceToastShownRef = useRef(false);

  useEffect(() => {
    if (!user) {
      setSubscriptionPaymentBlocked(false);
      paymentFailedToastShownRef.current = false;
    }
  }, [user]);

  const [projects, setProjects] = useState([]);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null); // null = show all chats
  const [projectsDropdownOpen, setProjectsDropdownOpen] = useState(false);
  const projectsDropdownRef = useRef(null);

  // Project action dialogs
  const [projectContextMenu, setProjectContextMenu] = useState(null); // { project, x, y }
  const [renameProjectDialog, setRenameProjectDialog] = useState(null); // project object
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);
  const [projectsExpanded, setProjectsExpanded] = useState(true);

  const [selectedModels, setSelectedModels] = useState(() => {
    // Guest users: never read localStorage — a previous logged-in session may have
    // stored a paid model (e.g. Grok) which would be invalid for free-tier users.
    const hasToken = !!localStorage.getItem("token");
    if (!hasToken) return ["ChatGPT", "Gemini"];
    try {
      const saved = localStorage.getItem("userSelectedModels");
      return saved ? JSON.parse(saved) : ["ChatGPT", "Gemini"];
    } catch (e) {
      return ["ChatGPT", "Gemini"];
    }
  });
  const [comparisonModel, setComparisonModel] = useState(() =>
    isLoggedIn ? "Gemini" : "ChatGPT",
  );
  const [synthesisModel, setSynthesisModel] = useState("ChatGPT");
  const [hoverComparison, setHoverComparison] = useState(false);
  const [hoverSynthesis, setHoverSynthesis] = useState(false);
  const tabsInfoRef = useRef(null);
  const [activeTab, setActiveTab] = useState("comparison");
  // Workspace stage. Navigation state only — mirrors activeTab for stages 2/3.
  const [activeStage, setActiveStage] = useState("initial");

  // Close tab info tooltips when tapping anywhere outside the tabs list.
  // Hover-only tooltips are invisible on touch devices, so the "i" icon now
  // toggles the tooltip on tap (see the info-icon buttons below).
  useEffect(() => {
    if (!hoverComparison && !hoverSynthesis) return;
    const handleOutside = (event) => {
      if (!tabsInfoRef.current) return;
      if (!tabsInfoRef.current.contains(event.target)) {
        setHoverComparison(false);
        setHoverSynthesis(false);
      }
    };
    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, [hoverComparison, hoverSynthesis]);

  const toggleTabInfo = (which) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (which === "comparison") {
      setHoverComparison((prev) => !prev);
      setHoverSynthesis(false);
    } else {
      setHoverSynthesis((prev) => !prev);
      setHoverComparison(false);
    }
  };

  const [query, setQuery] = useState("");
  const [researchAnalysesCount, setResearchAnalysesCount] = useState(null);
  const [researchAnalysesIsMock, setResearchAnalysesIsMock] = useState(false);
  const [researchAnalysesFetchFailed, setResearchAnalysesFetchFailed] =
    useState(false);
  const [lastSubmittedQuery, setLastSubmittedQuery] = useState(""); // Track the last submitted query for change detection
  const [lastSubmittedFiles, setLastSubmittedFiles] = useState(null); // Track the last submitted files for change detection

  // Initialize hooks - need to be in order
  const { responses, setResponses, getResponsePlain, getResponseHtml } =
    useResponses();

  // Derived, presentation-only counters for the workspace header and context rail.
  const completedResponseCount = Object.values(responses || {}).filter(
    (r) => r && !r.loading && !r.error && r.text && r.text.trim() !== "",
  ).length;
  const hasAnyResponse = Object.values(responses || {}).some(
    (r) => r && (r.loading || r.error || (r.text && r.text.trim() !== "")),
  );

  // Chat files hook - will be initialized with a temporary state, then synced with useChats
  const [tempCurrentChatId, setTempCurrentChatId] = useState(null);
  const {
    chatFiles,
    setChatFiles,
    setChatFilesList,
    getCurrentChatFiles,
    setCurrentChatFile,
    removeCurrentChatFile,
    handleFileRemove: handleChatFileRemove,
  } = useChatFiles(tempCurrentChatId);

  // Counter to track how many files have been successfully uploaded for the current query
  // (used only for trial enforcement — resets when a new query is submitted)
  const trialUploadCountRef = useRef(0);
  // Separate counter for anonymous (guest) users — tracks total uploads for the session
  // so they cannot bypass the 1-file limit by starting a new query.
  const anonUploadCountRef = useRef(0);

  // File upload hook with callback
  const handleFileUploaded = (fileData, fileType) => {
    setCurrentChatFile(fileType, fileData);
    trialUploadCountRef.current += 1; // track per-query uploads (trial)
    anonUploadCountRef.current += 1; // track session-wide uploads (guest)
  };
  const { fileUploading, handleFileUpload: handleFileUploadRaw } =
    useFileUpload(handleFileUploaded);

  // File upload guards:
  //  • Anonymous (guest) users: max 1 file for the entire session.
  //  • Trial users: max 1 file per query + max 10 files total.
  const handleFileUpload = (files, fileType) => {
    const incomingCount = Array.isArray(files) ? files.length : 1;

    // Anonymous / guest users: hard cap of 1 file per session
    if (!isLoggedIn) {
      if (anonUploadCountRef.current >= 1) {
        showWarning(
          "File uploads are limited to one document. Sign up for more.",
          "Guest Limit",
        );
        return;
      }
      // If someone drops multiple files at once, silently take only the first
      if (incomingCount > 1) {
        files = Array.isArray(files) ? files.slice(0, 1) : files;
      }
      handleFileUploadRaw(files, fileType);
      return;
    }

    if (isTrial) {
      // Free trial limit on total documents across all projects
      if ((user?.trialDocumentsUploaded || 0) + incomingCount > 10) {
        showWarning(
          "You have reached the maximum of 10 uploaded documents for your trial.",
          "Trial Limit",
        );
        return;
      }

      const maxFiles = getMaxFilesPerQuery(user);
      const totalAfter = trialUploadCountRef.current + incomingCount;

      if (trialUploadCountRef.current >= maxFiles) {
        // Already have maximum allowed files — block entirely
        showWarning(
          user?.is_tester
            ? "Testers are limited to two documents within the same query."
            : "Uploading two documents within the same query is available on paid plans.",
          "Trial Limit",
        );
        return;
      }

      if (totalAfter > maxFiles) {
        // Multi-file drop: cap to max allowed
        files = Array.isArray(files) ? files.slice(0, maxFiles - trialUploadCountRef.current) : files;
      }
    }
    handleFileUploadRaw(files, fileType);
  };

  // Comparison and Synthesis state - must be declared before useEditor hook
  const [comparisonQuery, setComparisonQuery] = useState(
    DEFAULT_COMPARISON_QUERY,
  ); // Query/prompt for comparative analysis
  const [comparisonResult, setComparisonResult] = useState(""); // Response from comparative analysis
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonGenerated, setComparisonGenerated] = useState(false); // Track if result is from API
  const [comparisonHistory, setComparisonHistory] = useState([]); // Array of { model, result, timestamp }
  const [selectedComparisonHistoryIndex, setSelectedComparisonHistoryIndex] =
    useState(null); // Track which historical entry is selected
  const [comparisonHistoryOpen, setComparisonHistoryOpen] = useState(false);
  const [synthesisHistoryOpen, setSynthesisHistoryOpen] = useState(false);
  const [executiveSummaryLoading, setExecutiveSummaryLoading] = useState(false);
  const currentChatIdRef = useRef(null);
  const [synthesisQuery, setSynthesisQuery] = useState(DEFAULT_SYNTHESIS_QUERY); // Query/prompt for final synthesis
  const [synthesisResult, setSynthesisResult] = useState(""); // Response from final synthesis
  const [synthesisLoading, setSynthesisLoading] = useState(false);
  const [synthesisGenerated, setSynthesisGenerated] = useState(false); // Track if result is from API
  const [synthesisHistory, setSynthesisHistory] = useState([]); // Array of { model, result, timestamp }
  const [selectedSynthesisHistoryIndex, setSelectedSynthesisHistoryIndex] =
    useState(null); // Track which historical entry is selected
  const [synthesisModelsAnalyzed, setSynthesisModelsAnalyzed] = useState([]); // Models included in latest synthesis
  const [
    synthesisExecutiveSummaryLoading,
    setSynthesisExecutiveSummaryLoading,
  ] = useState(false);

  // Editor hook - must be called after comparison/synthesis state is declared
  const {
    isOpen: editorOpen,
    content: editorContent,
    updateContent: setEditorContent,
    title: editorTitle,
    meta: editorMeta,
    openEditor: handleOpenEditor,
    closeEditor: handleCloseEditor,
    saveEditor: saveEditorData,
  } = useEditor();

  // Wrapper function to save editor content to appropriate state
  const handleEditorSave = useCallback(
    (content, plainText) => {
      if (!editorMeta || !editorMeta.type) return;

      const { type, model } = editorMeta;
      const chatId = currentChatIdRef.current;

      const persistUpdate = (updateData) => {
        if (chatId && chatId !== "new") {
          updateChat(chatId, updateData).catch((err) =>
            console.error(`Error saving edited ${type} to backend:`, err),
          );
        }
      };

      if (type === "model" && model) {
        // getResponseHtml() checks data.formatted FIRST — if formatted exists,
        // data.text is completely ignored. So we MUST update formatted with the
        // new HTML from the editor, otherwise the old formatted content wins.
        // plainText is provided by EditorModal as the second argument.
        const updatedText = plainText || content;
        setResponses((prev) => {
          const newResponses = {
            ...prev,
            [model]: {
              ...prev[model],
              text: updatedText,
              formatted: content,
            },
          };

          // Build the responses payload with { text, formatted } for the edited model
          // so the backend stores the formatted HTML alongside the plain text.
          const backendResponses = {};
          Object.entries(newResponses).forEach(([m, data]) => {
            if (data && typeof data === "object") {
              backendResponses[m] = {
                text: data.text || "",
                formatted: data.formatted || "",
              };
            } else if (typeof data === "string") {
              backendResponses[m] = data;
            }
          });

          persistUpdate({ responses: backendResponses });
          return newResponses;
        });
      } else if (type === "comparison" || type === "executive_summary") {
        // Update comparison result
        setComparisonResult(content);
        setComparisonGenerated(false); // Mark as manually edited

        const updateData = {
          comparisonResult: content,
          comparisonGenerated: false,
        };

        // If we're editing a specific history entry, update it too
        if (
          selectedComparisonHistoryIndex !== null &&
          comparisonHistory[selectedComparisonHistoryIndex]
        ) {
          const newHistory = [...comparisonHistory];
          newHistory[selectedComparisonHistoryIndex] = {
            ...newHistory[selectedComparisonHistoryIndex],
            result: content,
            timestamp: new Date().toISOString(),
          };
          setComparisonHistory(newHistory);
          updateData.comparisonHistory = newHistory;
        }

        persistUpdate(updateData);
      } else if (type === "synthesis") {
        // Update synthesis result
        setSynthesisResult(content);
        setSynthesisGenerated(false); // Mark as manually edited

        const updateData = {
          synthesisResult: content,
          synthesisGenerated: false,
        };

        // If we're editing a specific history entry, update it too
        if (
          selectedSynthesisHistoryIndex !== null &&
          synthesisHistory[selectedSynthesisHistoryIndex]
        ) {
          const newHistory = [...synthesisHistory];
          newHistory[selectedSynthesisHistoryIndex] = {
            ...newHistory[selectedSynthesisHistoryIndex],
            result: content,
            timestamp: new Date().toISOString(),
          };
          setSynthesisHistory(newHistory);
          updateData.synthesisHistory = newHistory;
        }

        persistUpdate(updateData);
      }

      handleCloseEditor();
    },
    [
      editorMeta,
      setResponses,
      setComparisonResult,
      setSynthesisResult,
      setComparisonGenerated,
      setSynthesisGenerated,
      handleCloseEditor,
      selectedComparisonHistoryIndex,
      comparisonHistory,
      setComparisonHistory,
      selectedSynthesisHistoryIndex,
      synthesisHistory,
      setSynthesisHistory,
    ],
  );

  const handleCreateExecutiveSummary = useCallback(async () => {
    if (!comparisonResult?.trim()) {
      showWarning("No comparative analysis to summarize", "No Data");
      return;
    }
    const textToSummarize = comparisonResult.trim();
    const token = localStorage.getItem("token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    setExecutiveSummaryLoading(true);
    setComparisonResult(""); // Clear so streaming text appears in same field
    try {
      const response = await fetch(`${API_URL}/ai/executive-summary-stream`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          comparativeAnalysisText: textToSummarize,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        showError(
          data.message || "Failed to generate executive summary",
          "Executive Summary",
        );
        return;
      }
      if (!response.body || typeof response.body.getReader !== "function") {
        showError(
          "Connection was interrupted while the app was in the background. Please try again.",
          "Connection Interrupted",
        );
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = null;
      let finalSummary = null;
      let streamError = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (currentEvent === "chunk" && data.fullText != null) {
                setComparisonResult(data.fullText);
              } else if (currentEvent === "done") {
                finalSummary = data.summary || data.text || "";
                setComparisonResult(finalSummary);
              } else if (currentEvent === "error") {
                streamError = new Error(
                  data.message || "Error generating executive summary",
                );
                break;
              }
            } catch (e) {
              // skip non-JSON lines
            }
            currentEvent = null;
          } else if (line.trim() === "") {
            currentEvent = null;
          }
        }
        if (streamError) break;
      }

      if (streamError) {
        showError(streamError.message, "Executive Summary");
        return;
      }
      const summary = finalSummary || "";
      if (summary) {
        const timestamp = new Date().toISOString();
        // Model whose Comparative Analysis this Executive Summary is summarizing
        // Use the currently selected history entry (via "Previous" dropdown), not the most recent
        const selectedEntry =
          selectedComparisonHistoryIndex !== null
            ? comparisonHistory[selectedComparisonHistoryIndex]
            : null;
        const sourceModel =
          (selectedEntry?.type !== "executive_summary" ? selectedEntry?.model : null) ||
          comparisonHistory.find((e) => e.type !== "executive_summary")?.model ||
          "Unknown";
        const historyEntry = {
          type: "executive_summary",
          model: "Executive Summary",
          sourceModel,
          result: summary,
          timestamp,
        };
        const newHistory = [historyEntry, ...comparisonHistory];
        setComparisonHistory(newHistory);
        setComparisonResult(summary);
        setSelectedComparisonHistoryIndex(0);
        setComparisonGenerated(false);
        const chatId = currentChatIdRef.current;
        if (chatId) {
          try {
            await updateChat(chatId, {
              comparisonHistory: newHistory,
              comparisonResult: summary,
            });
          } catch (err) {
            console.error("Error saving executive summary to chat:", err);
          }
        }
      }
      // Refresh AI usage counter after successful executive summary
      try {
        const config = { withCredentials: true };
        const t = localStorage.getItem("token");
        if (t) config.headers = { Authorization: `Bearer ${t}` };
        const usageRes = await axios.get(`${API_URL}/ai/usage`, config);
        setAiUsage(usageRes.data);
      } catch (_) { /* usage refresh is best-effort */ }
    } catch (err) {
      console.error("Executive summary error:", err);
      showError(
        err.message || "Unable to generate executive summary",
        "Executive Summary",
      );
    } finally {
      setExecutiveSummaryLoading(false);
    }
  }, [
    comparisonResult,
    comparisonHistory,
    showWarning,
    showError,
    handleOpenEditor,
    updateChat,
    selectedComparisonHistoryIndex,
  ]);

  const handleCreateSynthesisExecutiveSummary = useCallback(async () => {
    if (!synthesisResult?.trim()) {
      showWarning("No final synthesis to summarize", "No Data");
      return;
    }
    const textToSummarize = synthesisResult.trim();
    const token = localStorage.getItem("token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    setSynthesisExecutiveSummaryLoading(true);
    setSynthesisResult(""); // Clear so streaming appears in same field
    try {
      const response = await fetch(
        `${API_URL}/ai/executive-summary-from-synthesis-stream`,
        {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({ finalSynthesisText: textToSummarize }),
        },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        showError(
          data.message || "Failed to generate executive summary",
          "Executive Summary",
        );
        return;
      }
      if (!response.body || typeof response.body.getReader !== "function") {
        showError(
          "Connection was interrupted while the app was in the background. Please try again.",
          "Connection Interrupted",
        );
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = null;
      let finalSummary = null;
      let streamError = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (currentEvent === "chunk" && data.fullText != null) {
                setSynthesisResult(data.fullText);
              } else if (currentEvent === "done") {
                finalSummary = data.summary || data.text || "";
                setSynthesisResult(finalSummary);
              } else if (currentEvent === "error") {
                streamError = new Error(
                  data.message || "Error generating executive summary",
                );
                break;
              }
            } catch (e) {
              // skip non-JSON lines
            }
            currentEvent = null;
          } else if (line.trim() === "") {
            currentEvent = null;
          }
        }
        if (streamError) break;
      }

      if (streamError) {
        showError(streamError.message, "Executive Summary");
        return;
      }
      if (finalSummary) {
        const timestamp = new Date().toISOString();
        // Model whose Final Synthesis this Executive Summary is summarizing
        // Use the currently selected history entry (via "Previous" dropdown), not the most recent
        const selectedSynthEntry =
          selectedSynthesisHistoryIndex !== null
            ? synthesisHistory[selectedSynthesisHistoryIndex]
            : null;
        const sourceModel =
          (selectedSynthEntry?.type !== "executive_summary" ? selectedSynthEntry?.model : null) ||
          synthesisHistory.find((e) => e.type !== "executive_summary")?.model ||
          "Unknown";
        const historyEntry = {
          type: "executive_summary",
          model: "Executive Summary",
          sourceModel,
          result: finalSummary,
          timestamp,
        };
        const newHistory = [historyEntry, ...synthesisHistory];
        setSynthesisHistory(newHistory);
        setSynthesisResult(finalSummary);
        setSelectedSynthesisHistoryIndex(0);
        setSynthesisGenerated(false);
        const chatId = currentChatIdRef.current;
        if (chatId) {
          try {
            await updateChat(chatId, {
              synthesisHistory: newHistory,
              synthesisResult: finalSummary,
            });
          } catch (err) {
            console.error(
              "Error saving synthesis executive summary to chat:",
              err,
            );
          }
        }
      }
      // Refresh AI usage counter after successful executive summary
      try {
        const config = { withCredentials: true };
        const t = localStorage.getItem("token");
        if (t) config.headers = { Authorization: `Bearer ${t}` };
        const usageRes = await axios.get(`${API_URL}/ai/usage`, config);
        setAiUsage(usageRes.data);
      } catch (_) { /* usage refresh is best-effort */ }
    } catch (err) {
      console.error("Synthesis executive summary error:", err);
      showError(
        err.message || "Unable to generate executive summary",
        "Executive Summary",
      );
    } finally {
      setSynthesisExecutiveSummaryLoading(false);
    }
  }, [
    synthesisResult,
    synthesisHistory,
    showWarning,
    showError,
    updateChat,
    selectedSynthesisHistoryIndex,
  ]);

  // Chat management hook
  const {
    chats,
    setChats,
    loadingChats,
    loadingChat,
    loadingMore,
    chatsHasMore,
    loadMoreChats,
    currentChatId,
    setCurrentChatId,
    fetchChats,
    loadChat,
    createNewChat,
    saveChatTitle,
    addChatToProject,
    deleteChat,
    deletingChatId,
  } = useChats({
    user,
    setQuery,
    setLastSubmittedQuery,
    setLastSubmittedFiles,
    setResponses,
    setSelectedModels,
    setComparisonQuery,
    setComparisonResult,
    setComparisonGenerated,
    setComparisonHistory,
    setSelectedComparisonHistoryIndex,
    setSynthesisQuery,
    setSynthesisResult,
    setSynthesisGenerated,
    setSynthesisHistory,
    setSelectedSynthesisHistoryIndex,
    setComparisonModel,
    setSynthesisModel,
    setChatFiles, // Use the raw setter for restoring files
    getCurrentChatFiles,
    setCurrentChatFile,
    setSynthesisModelsAnalyzed,
  });

  useEffect(() => {
    currentChatIdRef.current = currentChatId;
  }, [currentChatId]);

  // Keep file bucket key in sync with chat id before paint (useEffect would lag one frame).
  useLayoutEffect(() => {
    setTempCurrentChatId(currentChatId);
  }, [currentChatId]);

  // AI Usage state - must be declared before fetchAIUsage
  const [aiUsage, setAiUsage] = useState({
    totalTokensUsed: 0,
    tokenLimit: 0,
    aiActionsUsed: 0,
    aiMonthlyLimit: 0,
  });

  // fetchAIUsage function - must be declared before useAIQuery hook
  const fetchAIUsage = useCallback(
    async (signal = null) => {
      if (!user) return;

      try {
        const token = localStorage.getItem("token");

        const config = {
          withCredentials: true, // Ensure cookies are sent
        };
        if (token) {
          config.headers = { Authorization: `Bearer ${token}` };
        }
        if (signal) {
          config.signal = signal;
        }

        const response = await axios.get(`${API_URL}/ai/usage`, config);
        setAiUsage(response.data);
        console.log("AI Usage:", response.data);
      } catch (error) {
        if (
          error.response?.status === 401 ||
          error.name === "AbortError" ||
          error.name === "CanceledError"
        ) {
          return;
        }
        console.error("Error fetching AI usage:", error);
      }
    },
    [user],
  );

  // Refresh AI usage UI instantly after upload completes (cross-component event).
  useEffect(() => {
    const handler = () => fetchAIUsage();
    const storageHandler = (e) => {
      if (e.key === USAGE_REFRESH_STORAGE_KEY && e.newValue) {
        fetchAIUsage();
      }
    };

    window.addEventListener(USAGE_REFRESH_EVENT, handler);
    window.addEventListener("storage", storageHandler);
    return () => {
      window.removeEventListener(USAGE_REFRESH_EVENT, handler);
      window.removeEventListener("storage", storageHandler);
    };
  }, [fetchAIUsage]);

  // AI Query hook
  const {
    queryLoading,
    setQueryLoading,
    handleSubmitQuery: handleSubmitQueryFromHook,
    queryLockedAfterStop,
    stopInitialQuery,
    resetQueryLockAfterStop,
  } = useAIQuery({
    id,
    setQuery,
    setLastSubmittedQuery,
    setLastSubmittedFiles,
    setResponses,
    setSelectedModels,
    setCurrentChatId,
    setChats,
    setComparisonQuery,
    setComparisonResult,
    setComparisonGenerated,
    setSynthesisQuery,
    setSynthesisResult,
    setSynthesisGenerated,
    setChatFiles,
    getCurrentChatFiles,
    fetchAIUsage,
    // So newly created chats are tagged with the current project
    getSelectedProjectId: () => selectedProjectId,
    handleAnonLimitError: (errData) => {
      // Match Debate Mode: for any guest/free-limit block, show the same modal.
      // useAIQuery already shows the toast, so avoid duplicating notifications here.
      // For the rolling daily cap the backend supplies the exact copy, so use it.
      setAnonSignupMessage(
        errData?.limitType === "ANON_DAILY_LIMIT_REACHED"
          ? errData.message || ""
          : "",
      );
      setShowAnonSignupModal(true);
    },
    handleTrialLimitError: (errData) => {
      const limitType = errData.limitType || "TRIAL_LIMIT_REACHED";

      switch (limitType) {
        case "TRIAL_DAILY_LIMIT":
          showInfo(
            errData.message ||
            "You've reached your daily trial limit. Come back tomorrow!",
            "Daily Limit",
          );
          // The refused action never reached the usage refresh, so pull the
          // counters now: the Executive Summary tooltips read them.
          fetchAIUsage();
          break;
        case "TRIAL_NO_SYNTHESIS":
          showWarning(
            errData.message ||
            "Synthesis is not available during the trial. Please upgrade to unlock.",
            "Feature Locked",
          );
          break;
        case "TRIAL_DEBATE_ROUNDS":
          showWarning(
            errData.message ||
            "You've reached the maximum debate rounds for a free trial.",
            "Trial Limit",
          );
          break;
        case "TRIAL_EXPIRED":
        case "TRIAL_TOTAL_LIMIT":
        default:

          navigate("/pricing");
          break;
      }
    },
  });

  // ------------------------------------------------------------------
  // Anon free tier: signup modal state + local usage count
  // ------------------------------------------------------------------
  const [showAnonSignupModal, setShowAnonSignupModal] = useState(false);
  const [showQueryStoppedDialog, setShowQueryStoppedDialog] = useState(false);
  const [anonSignupMessage, setAnonSignupMessage] = useState("");
  // How many queries the anon user has submitted this session/browser
  const anonUsesLeft = useMemo(() => {
    if (isLoggedIn) return null; // only relevant for anon
    try {
      const used = parseInt(localStorage.getItem("anon_uses") || "0", 10);
      return Math.max(0, 3 - used);
    } catch (e) {
      return 3;
    }
  }, [isLoggedIn, queryLoading]); // recalculate after each query

  // ------------------------------------------------------------------
  // Trial Mode Upgrade state and function
  // ------------------------------------------------------------------




  // Comparison models available — guests get the low-cost consumer models.
  // "ChatGPT" maps to gpt-4o-mini; for guests "Claude" resolves server-side to
  // Haiku 4.5 rather than Sonnet 4.5. See the matching lock on /compare-stream.
  const availableComparisonModels = isLoggedIn
    ? ["ChatGPT", "Claude", "Gemini", "Perplexity", "Grok"]
    : ["ChatGPT", "Claude"];

  // Default Comparative Analysis to the only model the free tier can actually
  // run: guests get ChatGPT (gpt-4o-mini), signed-up trial users keep Claude.
  // `isTrial` is true for guests too, so this has to key off login state.
  // One-shot via ref so a later manual click on another model (for
  // browsing/upsell) sticks.
  const trialDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (isTrial && !trialDefaultAppliedRef.current) {
      setComparisonModel(isLoggedIn ? "Claude" : "ChatGPT");
      trialDefaultAppliedRef.current = true;
    } else if (!isTrial) {
      trialDefaultAppliedRef.current = false;
    }
  }, [isTrial, isLoggedIn]);

  // Guest gate: a guest who has already used the free daily analysis sees
  // the sign-up prompt instead of a submit that the backend would reject.
  // No "review your question" pop-up here — that is a consumer-side feature
  // (LegalQuestion.js, on the second question of the day), not a research or
  // debate one. Everyone else submits straight away.
  const handleSubmitQuery = async () => {
    if (!isLoggedIn) {
      try {
        const usageRes = await fetch(`${API_URL}/ai/anon-usage`, {
          credentials: "include",
        });
        if (usageRes.ok) {
          const usageData = await usageRes.json();
          if (usageData?.guest && (usageData.blocked || usageData.dailyExceeded)) {
            setAnonSignupMessage(usageData.message || "");
            setShowAnonSignupModal(true);
            return;
          }
        }
      } catch (e) {
        // Fail open — the backend still enforces the limit on submit.
      }
    }

    await runSubmitQuery();
  };

  // Performs the actual submission once the guest gate (if any) has passed.
  const runSubmitQuery = async () => {

    // Streaming happens in stage 1 — make sure it is the visible stage.
    setActiveStage("initial");
    if (isTrial && (user?.trialDailyActionsUsed || 0) >= 20) {
      showWarning(
        "You have reached your daily limit of 20 AI Actions for the free trial.",
        "Trial Limit",
      );
      return;
    }

    // UI-level guard for paid users: block immediately when monthly AI actions are exhausted.
    // (Backend still enforces limits; this prevents users from starting a run only to hit 403 later.)
    if (isLoggedIn) {
      try {
        const config = { withCredentials: true };
        const t = localStorage.getItem("token");
        if (t) config.headers = { Authorization: `Bearer ${t}` };
        const usageRes = await axios.get(`${API_URL}/ai/usage`, config);
        const usage = usageRes.data || {};
        setAiUsage(usage);

        const used = Number(usage.aiActionsUsed ?? usage.actionsUsed ?? 0) || 0;
        const limit =
          Number(usage.aiMonthlyLimit ?? usage.actionLimit ?? 0) || 0;
        const isUnlimited = limit >= 1000000 || limit === Infinity;

        if (!isUnlimited && limit > 0 && used >= limit) {
          showWarning(
            `AI action limit reached. You have used ${used} of ${limit} actions. Please upgrade your plan to continue.`,
            "Limit Reached",
          );
          navigate("/pricing");
          return;
        }
      } catch (_) {
        // Fail open: backend will still block if exhausted.
      }
    }

    // Reset trial upload counter — each new query gets a fresh 1-file allowance
    trialUploadCountRef.current = 0;
    await handleSubmitQueryFromHook(
      query,
      selectedModels,
      responses,
      currentChatId,
      chats,
      id,
      navigate,
      lastSubmittedQuery,
      lastSubmittedFiles,
    );
  };

  // When navigating to dashboard without a chat id, clear query lock so user can run a new query
  useEffect(() => {
    if (!id) {
      resetQueryLockAfterStop();
    }
  }, [id, resetQueryLockAfterStop]);

  // File refs
  const comparisonFileInputRef = useRef(null);
  const synthesisFileInputRef = useRef(null);
  const comparisonQueryTextareaRef = useRef(null);
  const synthesisQueryTextareaRef = useRef(null);
  const mainQueryFileInputRef = useRef(null);

  const modelResponseRefs = useRef({});
  const comparisonResponseRef = useRef(null);
  const synthesisResponseRef = useRef(null);

  // Track user scroll state for each response container
  const userScrolledRefs = useRef({}); // { model: boolean } or 'comparison'/'synthesis'
  const lastScrollTopRefs = useRef({}); // { model: number } or 'comparison'/'synthesis'

  const setComparisonQueryTextareaRef = (node) => {
    comparisonQueryTextareaRef.current = node;
  };
  const setSynthesisQueryTextareaRef = (node) => {
    synthesisQueryTextareaRef.current = node;
  };

  // Chat management state
  const [openMenuId, setOpenMenuId] = useState(null); // Track which chat's menu is open
  const [menuPosition, setMenuPosition] = useState({}); // Track menu positions for each chat
  const [renameDialogChatId, setRenameDialogChatId] = useState(null);
  const [renameDialogAnchor, setRenameDialogAnchor] = useState(null);
  const [addToProjectDialogChatId, setAddToProjectDialogChatId] =
    useState(null);
  const [addToProjectDialogAnchor, setAddToProjectDialogAnchor] =
    useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // Search query for filtering chats
  const [isSearchOpen, setIsSearchOpen] = useState(false); // Track if search is open
  const [searchDialogLoaded, setSearchDialogLoaded] = useState(false); // Track if search dialog is loaded
  const chatListRef = useRef(null); // Ref for chat list container
  const wasNearBottomRef = useRef(false); // Trigger one load per bottom-crossing
  const [mobileScrollIndicator, setMobileScrollIndicator] = useState({
    visible: false,
    top: 0,
    height: 0,
  });
  const mainContentRef = useRef(null); // Ref for main content (layout)
  const menuRefs = useRef({}); // Refs for menu elements
  const sidebarRef = useRef(null); // Ref for sidebar element
  /** Avoids duplicate loadChat when sidebar/search navigation already kicked off loading */
  const researchChatNavFromSidebarRef = useRef(null);
  const [chatTitleMaxLength, setChatTitleMaxLength] = useState(26); // Reduced by 10% - smaller text shows fewer characters

  const isAnyAILoading = queryLoading || comparisonLoading || synthesisLoading;

  // No AI Actions left for this account: the trial's daily 20, or a paid
  // plan's monthly allowance. Used to word the Executive Summary tooltips —
  // "Run Comparative Analysis first" is wrong advice when the comparison
  // itself was refused for lack of allocation.
  const aiActionsExhausted = useMemo(() => {
    if (!isLoggedIn) return false;
    const used = Number(aiUsage?.aiActionsUsed ?? aiUsage?.actionsUsed ?? 0) || 0;
    const limit = Number(aiUsage?.aiMonthlyLimit ?? aiUsage?.actionLimit ?? 0) || 0;
    const allowanceExhausted = limit > 0 && limit < 1000000 && used >= limit;
    if (isTrial) {
      // /ai/usage is re-fetched after every action; the auth user object is
      // a login-time snapshot, so it only knew today's count if the page was
      // loaded after the last action. QA #19: 20/20 used, tooltip still read
      // "Run Comparative Analysis first" because the snapshot said 0.
      const dailyUsed =
        Number(
          aiUsage?.trialDailyActionsUsed ?? user?.trialDailyActionsUsed ?? 0,
        ) || 0;
      const dailyLimit = Number(aiUsage?.trialDailyLimit) || 20;
      return dailyUsed >= dailyLimit || allowanceExhausted;
    }
    return allowanceExhausted;
  }, [isLoggedIn, isTrial, user, aiUsage]);
  const AI_ACTIONS_EXHAUSTED_MESSAGE = isTrial
    ? "You've used today's 20 free-trial AI Actions. Upgrade to a plan to create an Executive Summary."
    : "You've used all the AI Actions on your plan. Upgrade your plan to create an Executive Summary.";

  const submitDisabled = useMemo(() => {
    const trimmed = query.trim();
    const isQueryValid =
      trimmed.length >= 8 &&
      /[a-zA-Z]/.test(trimmed) &&
      /[a-zA-Z0-9]/.test(trimmed);
    return !isQueryValid || selectedModels.length === 0 || isAnyAILoading;
  }, [query, selectedModels.length, isAnyAILoading]);

  // After initial query completes and results are displayed, transition out of active input:
  // lock main query (read-only, submit disabled). User can start a new chat via sidebar.
  const hasNewModelsToRun = useMemo(() => {
    if (!lastSubmittedQuery) return false;
    return selectedModels.some((model) => {
      const r = responses[model];
      return !r || !r.text || r.text.trim() === "" || r.error;
    });
  }, [selectedModels, responses, lastSubmittedQuery]);

  const initialQueryCompleteLock = useMemo(
    () => Boolean(id && !queryLoading && lastSubmittedQuery),
    [id, queryLoading, lastSubmittedQuery],
  );

  const mainQueryInputLocked = queryLockedAfterStop || initialQueryCompleteLock;

  // Nothing has been run in this workspace yet. Mirrors the test ResponsesGrid
  // uses for its empty state, so the two can never disagree about whether the
  // workspace is empty.
  const researchHasAnyResult = selectedModels.some((model) => {
    const d = responses[model];
    return !!d && (d.error || d.loading || (d.text && d.text.trim() !== ""));
  });

  // While the workspace is empty the composer sits in the middle of the
  // screen, under the "Ask a legal question to begin" copy, where a new
  // visitor looks first. The moment a run starts it drops back to its usual
  // place, docked to the bottom. Not while a chat is still loading: its
  // responses are about to arrive, and centering first would only make the
  // composer jump.
  const composerCentered =
    activeStage === "initial" &&
    !loadingChat &&
    !queryLoading &&
    !queryLockedAfterStop &&
    !researchHasAnyResult;

  // Models that have already completed Comparative Analysis successfully for this query (disable their buttons)
  const comparisonModelsCompleted = useMemo(
    () =>
      new Set(
        comparisonHistory
          .filter((e) => e?.model && (e?.result?.trim() ?? "") !== "")
          .map((e) => e.model),
      ),
    [comparisonHistory],
  );

  // Models that have already completed Final Synthesis successfully for this query (disable their buttons)
  const synthesisModelsCompleted = useMemo(
    () =>
      new Set(
        synthesisHistory
          .filter((e) => e?.model && (e?.result?.trim() ?? "") !== "")
          .map((e) => e.model),
      ),
    [synthesisHistory],
  );

  // Whether Comparative Analysis or Final Synthesis has been run — locks model selection & re-run
  const analysisStarted = comparisonModelsCompleted.size > 0 || synthesisModelsCompleted.size > 0;
  // Allow submit when new models are pending and analysis hasn't started yet
  const submitLocked = mainQueryInputLocked && !(hasNewModelsToRun && !analysisStarted);

  // Executive summary already generated for the currently viewed Comparative Analysis model
  const comparisonHasExecutiveSummary = useMemo(() => {
    // Determine which model's analysis is currently being viewed
    const selectedEntry =
      selectedComparisonHistoryIndex !== null
        ? comparisonHistory[selectedComparisonHistoryIndex]
        : null;
    // If viewing an executive summary entry itself, it's already summarized
    if (selectedEntry?.type === "executive_summary") return true;
    const viewingModel = selectedEntry?.model || comparisonModel;
    // Check if any executive summary exists for this model
    return comparisonHistory.some(
      (e) => e.type === "executive_summary" && e.sourceModel === viewingModel,
    );
  }, [comparisonHistory, selectedComparisonHistoryIndex, comparisonModel]);

  // Executive summary already generated for the currently viewed Final Synthesis model
  const synthesisHasExecutiveSummary = useMemo(() => {
    const selectedEntry =
      selectedSynthesisHistoryIndex !== null
        ? synthesisHistory[selectedSynthesisHistoryIndex]
        : null;
    if (selectedEntry?.type === "executive_summary") return true;
    const viewingModel = selectedEntry?.model || synthesisModel;
    return synthesisHistory.some(
      (e) => e.type === "executive_summary" && e.sourceModel === viewingModel,
    );
  }, [synthesisHistory, selectedSynthesisHistoryIndex, synthesisModel]);

  // Editor functions are now provided by useEditor hook

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  // Helper to check if user is near bottom
  const isNearBottom = (element, threshold = 100) => {
    if (!element) return true; // Default to true if element doesn't exist yet
    const distanceFromBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceFromBottom <= threshold;
  };

  // Set up scroll tracking for model responses
  useEffect(() => {
    if (!queryLoading) return;

    const scrollHandlers = [];

    Object.keys(responses).forEach((model) => {
      const el = modelResponseRefs.current[model];
      if (!el) return;

      const scrollableEl = getScrollableElement(el);
      if (!scrollableEl) return;

      const handleScroll = () => {
        if (!scrollableEl) return;

        const currentScrollTop = scrollableEl.scrollTop;
        const scrollHeight = scrollableEl.scrollHeight;
        const clientHeight = scrollableEl.clientHeight;
        const isAtBottom = isNearBottom(scrollableEl);

        // If user scrolled up (not at bottom), mark as user-interacted
        if (
          !isAtBottom &&
          currentScrollTop < (lastScrollTopRefs.current[model] || 0)
        ) {
          userScrolledRefs.current[model] = true;
        } else if (isAtBottom) {
          // If user scrolls back to bottom, reset the flag
          userScrolledRefs.current[model] = false;
        }

        lastScrollTopRefs.current[model] = currentScrollTop;
      };

      scrollableEl.addEventListener("scroll", handleScroll, { passive: true });
      scrollHandlers.push({ element: scrollableEl, handler: handleScroll });
    });

    return () => {
      scrollHandlers.forEach(({ element, handler }) => {
        element.removeEventListener("scroll", handler);
      });
    };
  }, [responses, queryLoading]);

  // Helper to find the actual scrollable element (might be the element itself or a child)
  const getScrollableElement = (element) => {
    if (!element) return null;

    // Check if element itself is scrollable
    const style = window.getComputedStyle(element);
    if (
      style.overflowY === "auto" ||
      style.overflowY === "scroll" ||
      style.overflow === "auto" ||
      style.overflow === "scroll"
    ) {
      return element;
    }

    // Check children for scrollable element
    const children = element.children;
    for (let i = 0; i < children.length; i++) {
      const childStyle = window.getComputedStyle(children[i]);
      if (
        childStyle.overflowY === "auto" ||
        childStyle.overflowY === "scroll" ||
        childStyle.overflow === "auto" ||
        childStyle.overflow === "scroll"
      ) {
        return children[i];
      }
    }

    // Default to the element itself
    return element;
  };

  // Auto-scroll to latest text during streaming - always scroll when streaming
  useEffect(() => {
    if (!queryLoading) return;

    // Function to scroll a specific model's response to bottom (only if user hasn't scrolled up)
    const scrollToBottom = (model, force = false) => {
      const el = modelResponseRefs.current[model];
      if (!el) return;

      // Find the actual scrollable element
      const scrollableEl = getScrollableElement(el);
      if (!scrollableEl) return;

      // Only auto-scroll if user hasn't manually scrolled up
      if (userScrolledRefs.current[model] && !force) {
        return;
      }

      // Only scroll if user is near bottom
      if (!isNearBottom(scrollableEl) && !force) {
        return;
      }

      // Scroll to bottom
      const scrollHeight = scrollableEl.scrollHeight;
      scrollableEl.scrollTop = scrollHeight;
      lastScrollTopRefs.current[model] = scrollHeight;
    };

    // Scroll all models immediately
    Object.keys(responses).forEach((model) => {
      scrollToBottom(model, true);
    });

    // Set up MutationObserver to watch for DOM changes and auto-scroll
    const observers = [];
    Object.keys(responses).forEach((model) => {
      const responseData = responses[model];
      // Only set up observer for models that are loading or have content
      if (!responseData || (!responseData.loading && !responseData.text))
        return;

      const el = modelResponseRefs.current[model];
      if (!el) return;

      const scrollableEl = getScrollableElement(el);
      if (!scrollableEl) return;

      // Watch the inner div that contains the HTML content (updated via dangerouslySetInnerHTML)
      const innerContentDiv = scrollableEl.querySelector("div");
      const targetToObserve = innerContentDiv || scrollableEl;

      // Create observer to watch for content changes
      const observer = new MutationObserver((mutations) => {
        // Scroll to bottom whenever content changes
        // Use multiple timing strategies to ensure scroll happens
        requestAnimationFrame(() => {
          scrollToBottom(model);
          // Double-check after a microtask
          Promise.resolve().then(() => {
            scrollToBottom(model);
          });
        });
      });

      // Observe changes to child nodes and subtree - watch the inner content div
      observer.observe(targetToObserve, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: false,
      });

      // Also observe the scrollable element itself
      if (targetToObserve !== scrollableEl) {
        observer.observe(scrollableEl, {
          childList: true,
          subtree: false,
        });
      }

      observers.push({ observer, element: scrollableEl });
    });

    // Use a more frequent interval during streaming
    const scrollInterval = setInterval(() => {
      Object.keys(responses).forEach((model) => {
        scrollToBottom(model);
      });
    }, 50); // Check every 50ms during streaming for more responsiveness

    // Cleanup
    return () => {
      observers.forEach(({ observer }) => observer.disconnect());
      clearInterval(scrollInterval);
    };
  }, [responses, queryLoading]);

  // Immediate scroll on response text changes during streaming
  useEffect(() => {
    if (!queryLoading) return;

    // Scroll all models immediately when text changes (only if user hasn't scrolled up)
    const scrollAllToBottom = () => {
      Object.keys(responses).forEach((model) => {
        const el = modelResponseRefs.current[model];
        if (!el) return;

        const scrollableEl = getScrollableElement(el);
        if (!scrollableEl) return;

        // Only auto-scroll if user hasn't manually scrolled up and is near bottom
        if (userScrolledRefs.current[model] || !isNearBottom(scrollableEl)) {
          return;
        }

        // Scroll to bottom
        const scrollHeight = scrollableEl.scrollHeight;
        scrollableEl.scrollTop = scrollHeight;
        lastScrollTopRefs.current[model] = scrollHeight;
      });
    };

    // Scroll immediately with multiple timing strategies
    scrollAllToBottom();

    // Scroll after DOM updates
    requestAnimationFrame(() => {
      scrollAllToBottom();
      // Also scroll after a short delay to catch late updates
      setTimeout(() => {
        scrollAllToBottom();
      }, 10);
      setTimeout(() => {
        scrollAllToBottom();
      }, 50);
    });
  }, [responses, queryLoading]);

  // Set up scroll tracking for comparison
  useEffect(() => {
    if (!comparisonResponseRef.current) return;

    const scrollableEl = getScrollableElement(comparisonResponseRef.current);
    if (!scrollableEl) return;

    const handleScroll = () => {
      if (!scrollableEl) return;

      const currentScrollTop = scrollableEl.scrollTop;
      const isAtBottom = isNearBottom(scrollableEl);

      // If user scrolled up (not at bottom), mark as user-interacted
      if (
        !isAtBottom &&
        currentScrollTop < (lastScrollTopRefs.current["comparison"] || 0)
      ) {
        userScrolledRefs.current["comparison"] = true;
      } else if (isAtBottom) {
        // If user scrolls back to bottom, reset the flag
        userScrolledRefs.current["comparison"] = false;
      }

      lastScrollTopRefs.current["comparison"] = currentScrollTop;
    };

    scrollableEl.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollableEl.removeEventListener("scroll", handleScroll);
    };
  }, [activeTab, comparisonLoading, executiveSummaryLoading, comparisonResult]);

  // Auto-scroll for Comparative Analysis & Executive Summary - enhanced with MutationObserver
  useEffect(() => {
    if (
      (!comparisonLoading && !executiveSummaryLoading) ||
      !comparisonResponseRef.current
    ) {
      return;
    }

    const scrollableEl = getScrollableElement(comparisonResponseRef.current);
    if (!scrollableEl) return;

    // Function to scroll to bottom (only if user hasn't scrolled up)
    const scrollToBottom = () => {
      if (comparisonResponseRef.current && scrollableEl) {
        // During streaming, keep auto-scrolling unless the user explicitly scrolls up.
        if (userScrolledRefs.current["comparison"]) {
          return;
        }
        scrollableEl.scrollTop = scrollableEl.scrollHeight;
        lastScrollTopRefs.current["comparison"] = scrollableEl.scrollTop;
      }
    };

    // Scroll immediately when loading starts
    scrollToBottom();

    // Observe the scrollable container itself. The HTML content is injected directly into it,
    // and watching nested nodes can break if the first nested node is replaced mid-stream.
    const targetToObserve = scrollableEl;

    // Create observer to watch for content changes
    const observer = new MutationObserver(() => {
      // Scroll immediately when content changes
      scrollToBottom();
      requestAnimationFrame(() => {
        scrollToBottom();
        // Double-check after microtask
        Promise.resolve().then(() => {
          scrollToBottom();
        });
      });
    });

    // Observe the inner content div (where dangerouslySetInnerHTML updates)
    if (targetToObserve) {
      observer.observe(targetToObserve, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // Use interval as backup during streaming
    const scrollInterval = setInterval(() => {
      scrollToBottom();
    }, 50); // Check every 50ms during streaming

    return () => {
      observer.disconnect();
      clearInterval(scrollInterval);
    };
  }, [comparisonLoading, executiveSummaryLoading, comparisonResult]);

  // Immediate scroll on comparison result changes (comparison or executive summary streaming)
  useEffect(() => {
    if (
      (!comparisonLoading && !executiveSummaryLoading) ||
      !comparisonResponseRef.current
    ) {
      return;
    }

    const scrollableEl = getScrollableElement(comparisonResponseRef.current);
    if (!scrollableEl) return;

    const scrollToBottom = () => {
      // Let users read/scroll older content during streaming unless they return near bottom.
      if (userScrolledRefs.current["comparison"] || !isNearBottom(scrollableEl)) {
        return;
      }
      scrollableEl.scrollTop = scrollableEl.scrollHeight;
      lastScrollTopRefs.current["comparison"] = scrollableEl.scrollTop;
    };

    // Scroll immediately when result changes
    scrollToBottom();

    // Scroll after DOM updates with multiple timing strategies
    requestAnimationFrame(() => {
      scrollToBottom();
      setTimeout(() => {
        scrollToBottom();
      }, 0);
      setTimeout(() => {
        scrollToBottom();
      }, 10);
    });
  }, [comparisonResult, comparisonLoading, executiveSummaryLoading]);

  // Set up scroll tracking for synthesis
  useEffect(() => {
    if (!synthesisResponseRef.current) return;

    const scrollableEl = getScrollableElement(synthesisResponseRef.current);
    if (!scrollableEl) return;

    const handleScroll = () => {
      if (!scrollableEl) return;

      const currentScrollTop = scrollableEl.scrollTop;
      const isAtBottom = isNearBottom(scrollableEl);

      // If user scrolled up (not at bottom), mark as user-interacted
      if (
        !isAtBottom &&
        currentScrollTop < (lastScrollTopRefs.current["synthesis"] || 0)
      ) {
        userScrolledRefs.current["synthesis"] = true;
      } else if (isAtBottom) {
        // If user scrolls back to bottom, reset the flag
        userScrolledRefs.current["synthesis"] = false;
      }

      lastScrollTopRefs.current["synthesis"] = currentScrollTop;
    };

    scrollableEl.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollableEl.removeEventListener("scroll", handleScroll);
    };
  }, [
    activeTab,
    synthesisLoading,
    synthesisExecutiveSummaryLoading,
    synthesisResult,
  ]);

  // Auto-scroll for Synthesis (and Executive Summary streaming) - enhanced with MutationObserver
  useEffect(() => {
    if (
      (!synthesisLoading && !synthesisExecutiveSummaryLoading) ||
      !synthesisResponseRef.current
    )
      return;

    const scrollableEl = getScrollableElement(synthesisResponseRef.current);
    if (!scrollableEl) return;

    // Function to scroll to bottom (only if user hasn't scrolled up)
    const scrollToBottom = () => {
      if (synthesisResponseRef.current && scrollableEl) {
        // During streaming, keep auto-scrolling unless the user explicitly scrolls up.
        if (userScrolledRefs.current["synthesis"]) {
          return;
        }
        scrollableEl.scrollTop = scrollableEl.scrollHeight;
        lastScrollTopRefs.current["synthesis"] = scrollableEl.scrollTop;
      }
    };

    // Scroll immediately when loading starts
    scrollToBottom();

    // Observe the scrollable container itself. The HTML content is injected directly into it,
    // and watching nested nodes can break if the first nested node is replaced mid-stream.
    const targetToObserve = scrollableEl;

    // Create observer to watch for content changes
    const observer = new MutationObserver(() => {
      // Scroll immediately when content changes
      scrollToBottom();
      requestAnimationFrame(() => {
        scrollToBottom();
        // Double-check after microtask
        Promise.resolve().then(() => {
          scrollToBottom();
        });
      });
    });

    // Observe the inner content div (where dangerouslySetInnerHTML updates)
    if (targetToObserve) {
      observer.observe(targetToObserve, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // Use interval as backup during streaming
    const scrollInterval = setInterval(() => {
      scrollToBottom();
    }, 50); // Check every 50ms during streaming

    return () => {
      observer.disconnect();
      clearInterval(scrollInterval);
    };
  }, [synthesisLoading, synthesisExecutiveSummaryLoading, synthesisResult]);

  // Immediate scroll on synthesis result changes
  useEffect(() => {
    if (
      (!synthesisLoading && !synthesisExecutiveSummaryLoading) ||
      !synthesisResponseRef.current
    )
      return;

    const scrollableEl = getScrollableElement(synthesisResponseRef.current);
    if (!scrollableEl) return;

    const scrollToBottom = () => {
      // During streaming, keep auto-scrolling unless the user explicitly scrolls up.
      if (userScrolledRefs.current["synthesis"]) {
        return;
      }
      scrollableEl.scrollTop = scrollableEl.scrollHeight;
      lastScrollTopRefs.current["synthesis"] = scrollableEl.scrollTop;
    };

    // Scroll immediately when result changes
    scrollToBottom();

    // Scroll after DOM updates with multiple timing strategies
    requestAnimationFrame(() => {
      scrollToBottom();
      setTimeout(() => {
        scrollToBottom();
      }, 0);
      setTimeout(() => {
        scrollToBottom();
      }, 10);
    });
  }, [synthesisResult, synthesisLoading, synthesisExecutiveSummaryLoading]);

  useEffect(() => {
    const calculateMaxLength = () => {
      const width = window.innerWidth;

      if (width <= 500) {
        return 10;
      } else if (width <= 800) {
        return 20;
      } else if (width <= 1400) {
        return 25;
      } else {
        return 40;
      }
    };

    setChatTitleMaxLength(calculateMaxLength());

    const handleResize = () => {
      setChatTitleMaxLength(calculateMaxLength());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load usage, pricing check, chats, and projects in parallel for faster first paint
  useEffect(() => {
    if (!user) return; // Only run startup data fetching for logged-in users

    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchData = async () => {
      if (abortController.signal.aborted) return;
      await fetchAIUsage(abortController.signal);
      if (abortController.signal.aborted) return;

      // Check if user should be redirected to pricing (first-time user)
      // Only do this for authenticated users; anon users are never redirected
      await checkAndRedirectToPricing(abortController.signal);
      if (abortController.signal.aborted) return;

      await fetchChats(abortController.signal);
      if (abortController.signal.aborted) return;
      // Dashboard should only see dashboard-mode projects
      const projectsList = await fetchProjects(
        "dashboard",
        abortController.signal,
      );
      if (!abortController.signal.aborted && Array.isArray(projectsList)) {
        setProjects(projectsList);
      }

      try {
        const savedProjectId = localStorage.getItem(
          "dashboardSelectedProjectId",
        );
        if (
          savedProjectId &&
          projectsList?.some((p) => p._id === savedProjectId)
        ) {
          setSelectedProjectId(savedProjectId);
        }
      } catch (e) {
        // ignore localStorage errors
      }
    };

    fetchData();

    return () => {
      abortController.abort();
    };
  }, [user]);

  useEffect(() => {
    return () => {
      Object.values(chatFiles).forEach((files) => {
        if (files.mainQueryFile?.fileUrl && !files.mainQueryFile?.s3Url) {
          URL.revokeObjectURL(files.mainQueryFile.fileUrl);
        }
        if (files.comparisonFile?.fileUrl && !files.comparisonFile?.s3Url) {
          URL.revokeObjectURL(files.comparisonFile.fileUrl);
        }
        if (files.synthesisFile?.fileUrl && !files.synthesisFile?.s3Url) {
          URL.revokeObjectURL(files.synthesisFile.fileUrl);
        }
      });
    };
  }, [chatFiles]);

  useEffect(() => {
    if (!user) return;

    // LOG: Page refresh/URL change
    if (id) {
      console.log(
        "[PAGE REFRESH/URL CHANGE] ============================================",
      );
      console.log(
        "[PAGE REFRESH/URL CHANGE] Timestamp:",
        new Date().toISOString(),
      );
      console.log("[PAGE REFRESH/URL CHANGE] URL id:", id);
      console.log("[PAGE REFRESH/URL CHANGE] currentChatId:", currentChatId);
      console.log("[PAGE REFRESH/URL CHANGE] chats.length:", chats.length);
      console.log("[PAGE REFRESH/URL CHANGE] loadingChats:", loadingChats);
      console.log("[PAGE REFRESH/URL CHANGE] queryLoading:", queryLoading);
      console.log(
        "[PAGE REFRESH/URL CHANGE] ============================================",
      );
    }

    if (!id) {
      if (currentChatId) {
        setCurrentChatId(null);
        setResponses({});
        setSelectedModels(["ChatGPT", "Gemini"]);
        setQuery("");
        setComparisonResult("");
        setComparisonGenerated(false);
        setSynthesisResult("");
        setSynthesisGenerated(false);
        setChatFiles((prev) => {
          const updated = { ...prev };
          delete updated["new"];
          return updated;
        });
      }
      return;
    }

    // When we have a URL id and chats list is empty (e.g. refresh or direct link),
    // load the chat by id immediately so the user sees content without waiting for fetchChats.
    if (chats.length === 0) {
      // Load by URL id immediately – don't wait for fetchChats so refresh shows data ASAP.
      // Don't load if query is in progress (would overwrite streaming state).
      if (!queryLoading && id) {
        console.log(
          "[useEffect] Chats empty, loading chat by URL id from backend:",
          id,
        );
        loadChat(id, currentChatId, responses, query).catch((error) => {
          if (
            error.response?.status === 404 ||
            error.message === "Chat not found"
          ) {
            // LOG: Chat not found on refresh
            console.log(
              "[CHAT NOT FOUND] ============================================",
            );
            console.log(
              "[CHAT NOT FOUND] Timestamp:",
              new Date().toISOString(),
            );
            console.log("[CHAT NOT FOUND] Requested chatId:", id);
            console.log("[CHAT NOT FOUND] currentChatId:", currentChatId);
            console.log(
              "[CHAT NOT FOUND] Error status:",
              error.response?.status,
            );
            console.log("[CHAT NOT FOUND] Error message:", error.message);
            console.log(
              "[CHAT NOT FOUND] Removing from sidebar and navigating to dashboard",
            );
            console.log(
              "[CHAT NOT FOUND] ============================================",
            );

            console.warn(
              "[useEffect] Chat not found on refresh, removing from sidebar and navigating:",
              id,
            );
            // Remove chat from sidebar if it exists
            setChats((prev) =>
              prev.filter((c) => {
                const chatId16 = c.chatId16 || c.chatId || c._id;
                return chatId16 !== id && c._id !== id && c.chatId !== id;
              }),
            );
            // Clear state
            setCurrentChatId(null);
            setResponses({});
            setQuery("");
            setSelectedModels(["ChatGPT", "Gemini"]);
            // Navigate away
            navigate(workspaceBasePath, { replace: true });
          } else if (error.response?.status !== 404) {
            console.error("[useEffect] Error loading chat from URL:", error);
          }
        });
      }
      return;
    }

    const chat = chats.find((c) => {
      const chatId16 = c.chatId16 || c.chatId || c._id;
      return chatId16 === id;
    });

    if (chat) {
      const chatIdToLoad = chat.chatId || chat._id;
      // Don't load temp chats from backend - they're only in local state
      const isTempChat =
        chatIdToLoad &&
        (chatIdToLoad.startsWith("temp_") || chat._id?.startsWith("temp_"));

      if (
        !isTempChat &&
        researchChatNavFromSidebarRef.current &&
        researchChatNavFromSidebarRef.current === String(id)
      ) {
        return;
      }

      if (isTempChat) {
        // For temp chats, just set the state from the chat object without calling loadChat
        // Don't reset responses if we're already on this chat and have responses
        if (currentChatId !== chatIdToLoad) {
          setCurrentChatId(chatIdToLoad);
          setQuery(chat.query || "");
          setSelectedModels(chat.selectedModels || selectedModels);
          // Only reset responses if we don't have any for this chat
          if (Object.keys(responses).length === 0) {
            setResponses({});
          }
        } else {
          // Ensure selectedModels matches the temp chat
          if (
            chat.selectedModels &&
            JSON.stringify(chat.selectedModels) !==
            JSON.stringify(selectedModels)
          ) {
            setSelectedModels(chat.selectedModels);
          }
        }
      } else {
        // Always load chat from backend to get full data (including responses, query, files)
        // The chats array might only have metadata, not full response data
        // Check if we're already on this chat by comparing different ID formats
        const chatId16 = chat.chatId16 || chat.chatId || chat._id;
        const isAlreadyOnThisChat =
          currentChatId === chatIdToLoad ||
          currentChatId === chatId16 ||
          currentChatId === id ||
          (currentChatId && String(currentChatId) === String(chatIdToLoad)) ||
          (currentChatId && String(currentChatId) === String(chatId16));

        // Also check if we have the essential data - if not, we need to reload
        // Note: files might not exist for all chats, so we only check query and responses
        // IMPORTANT: Check if query matches the chat's query to avoid unnecessary reloads
        const currentQueryMatches =
          chat.query && query && query.trim() === chat.query.trim();
        const hasQuery = query && query.trim().length > 0;
        const hasResponses = Object.keys(responses).length > 0;
        // If we're on this chat but missing essential data OR query doesn't match, reload
        const needsReload =
          isAlreadyOnThisChat &&
          (!hasQuery || !hasResponses || !currentQueryMatches);

        // If we just created this chat (it's in sidebar with correct data), don't reload immediately
        // Check if chat was just added (recent timestamp) and has the data we need
        const chatJustCreated =
          chat.updatedAt && Date.now() - chat.updatedAt < 10000; // Within last 10 seconds
        const hasChatData =
          chat.query && chat.selectedModels && chat.selectedModels.length > 0;

        // If query is currently loading, don't interfere - let the query process continue
        // Do NOT overwrite selectedModels from chat here: the sidebar chat may be stale
        // (e.g. user added Gemini/Claude and submitted; chat still has only ChatGPT).
        // The hook just set selectedModels and responses with loading placeholders;
        // preserve them so newly added models stay visible in the UI.
        if (queryLoading) {
          // CRITICAL: Preserve the query - don't reset it while query is loading
          // Only update if query is empty and chat has a query
          if (!query || !query.trim()) {
            if (chat.query && chat.query.trim()) {
              console.log(
                "[useEffect] Query loading - setting query from chat (query was empty):",
                chat.query,
              );
              setQuery(chat.query);
              setLastSubmittedQuery(chat.query);
            }
          }
          return; // Don't proceed with loadChat while query is loading
        }

        if (
          (!isAlreadyOnThisChat || needsReload) &&
          !comparisonLoading &&
          !synthesisLoading
        ) {
          // If chat was just created and has data, use it directly instead of reloading
          if (chatJustCreated && hasChatData && !hasResponses) {
            console.log("Using just-created chat data without reloading:", {
              chatIdToLoad,
              currentChatId,
              id,
              chatId16,
              chatSelectedModels: chat.selectedModels,
            });
            // Set state from chat object directly
            setCurrentChatId(chatId16);
            setQuery(chat.query || "");
            setLastSubmittedQuery(chat.query || "");
            // CRITICAL: Use chat's selectedModels, not current state
            if (
              chat.selectedModels &&
              Array.isArray(chat.selectedModels) &&
              chat.selectedModels.length > 0
            ) {
              setSelectedModels(chat.selectedModels);
            }
            // Don't reset responses if query is loading
            if (!queryLoading) {
              setResponses({});
            }
          } else {
            console.log("Loading chat from backend:", {
              chatIdToLoad,
              currentChatId,
              id,
              chatId16,
              isAlreadyOnThisChat,
              hasQuery,
              hasResponses,
              currentQueryMatches,
              chatQuery: chat.query,
              currentQuery: query,
              needsReload,
            });
            loadChat(chatIdToLoad, currentChatId, responses, query).catch(
              (error) => {
                if (
                  error.response?.status === 404 ||
                  error.message === "Chat not found"
                ) {
                  // LOG: Chat not found
                  console.log(
                    "[CHAT NOT FOUND] ============================================",
                  );
                  console.log(
                    "[CHAT NOT FOUND] Timestamp:",
                    new Date().toISOString(),
                  );
                  console.log(
                    "[CHAT NOT FOUND] Requested chatId:",
                    chatIdToLoad,
                  );
                  console.log("[CHAT NOT FOUND] URL id:", id);
                  console.log("[CHAT NOT FOUND] currentChatId:", currentChatId);
                  console.log(
                    "[CHAT NOT FOUND] Error status:",
                    error.response?.status,
                  );
                  console.log("[CHAT NOT FOUND] Error message:", error.message);
                  console.log(
                    "[CHAT NOT FOUND] Removing from sidebar and navigating to dashboard",
                  );
                  console.log(
                    "[CHAT NOT FOUND] ============================================",
                  );

                  console.warn(
                    "[useEffect] Chat not found, removing from sidebar and navigating:",
                    chatIdToLoad,
                  );
                  // Remove chat from sidebar
                  setChats((prev) =>
                    prev.filter((c) => {
                      const chatId16 = c.chatId16 || c.chatId || c._id;
                      return (
                        chatId16 !== id &&
                        c._id !== chatIdToLoad &&
                        c.chatId !== chatIdToLoad
                      );
                    }),
                  );
                  // Clear state
                  setCurrentChatId(null);
                  setResponses({});
                  setQuery("");
                  setSelectedModels(["ChatGPT", "Gemini"]);
                  // Navigate away
                  navigate(workspaceBasePath, { replace: true });
                } else {
                  console.error(
                    "[useEffect] Error loading chat from URL:",
                    error,
                  );
                  navigate(workspaceBasePath, { replace: true });
                }
              },
            );
          }
        } else if (isAlreadyOnThisChat) {
          // If we're already on this chat, ensure query and selectedModels match
          if (!currentQueryMatches && chat.query) {
            // If query doesn't match, update it without reloading
            console.log("Updating query to match chat:", chat.query);
            setQuery(chat.query);
            setLastSubmittedQuery(chat.query);
            // Reset files tracking when query is updated
            const currentFiles = getCurrentChatFiles();
            setLastSubmittedFiles(
              currentFiles
                ? {
                  mainQueryFiles: currentFiles.mainQueryFiles || [],
                }
                : null,
            );
          } else if (!hasQuery && chat.query) {
            // If we don't have a query but chat has one, set it
            console.log(
              "Setting query from chat object (no query in state):",
              chat.query,
            );
            setQuery(chat.query);
            setLastSubmittedQuery(chat.query);
            // Reset files tracking when query is set
            const currentFiles = getCurrentChatFiles();
            setLastSubmittedFiles(
              currentFiles
                ? {
                  mainQueryFiles: currentFiles.mainQueryFiles || [],
                }
                : null,
            );
          }

          // Ensure selectedModels matches the chat's selectedModels
          // CRITICAL: Always use chat's selectedModels, not current state
          if (
            chat.selectedModels &&
            Array.isArray(chat.selectedModels) &&
            chat.selectedModels.length > 0
          ) {
            const chatModelsStr = JSON.stringify(chat.selectedModels.sort());
            const currentModelsStr = JSON.stringify(selectedModels.sort());
            if (chatModelsStr !== currentModelsStr) {
              console.log(
                "[useEffect] Updating selectedModels to match chat:",
                chat.selectedModels,
                "Current:",
                selectedModels,
              );
              setSelectedModels(chat.selectedModels);
            }
          } else if (
            !chat.selectedModels ||
            !Array.isArray(chat.selectedModels) ||
            chat.selectedModels.length === 0
          ) {
            // If chat doesn't have selectedModels, but we have them in state, don't reset
            // This prevents resetting to defaults when chat data is incomplete
            console.log(
              "[useEffect] Chat missing selectedModels, keeping current state:",
              selectedModels,
            );
          }
        }
      }
    } else {
      // Check if it's a temp chat ID
      const isTempChat = id && id.startsWith("temp_");
      if (isTempChat) {
        // Don't try to load temp chats from backend
        return;
      }

      // Chat not found in chats array, but we have an ID - load it from backend
      const isAlreadyOnThisChat = currentChatId === id;
      if (
        !isAlreadyOnThisChat &&
        !queryLoading &&
        !comparisonLoading &&
        !synthesisLoading
      ) {
        console.log("Chat not in array, loading from backend:", id);
        loadChat(id, currentChatId, responses, query).catch((error) => {
          if (
            error.response?.status === 404 ||
            error.message === "Chat not found"
          ) {
            console.warn(
              "[useEffect] Chat not found, removing from sidebar and navigating:",
              id,
            );
            // Remove chat from sidebar
            setChats((prev) =>
              prev.filter((c) => {
                const chatId16 = c.chatId16 || c.chatId || c._id;
                return chatId16 !== id && c._id !== id && c.chatId !== id;
              }),
            );
            // Clear state
            setCurrentChatId(null);
            setResponses({});
            setQuery("");
            setSelectedModels(["ChatGPT", "Gemini"]);
            // Navigate away
            navigate(workspaceBasePath, { replace: true });
          } else {
            console.error("[useEffect] Error loading chat from URL:", error);
            navigate(workspaceBasePath, { replace: true });
          }
        });
      } else if (isAlreadyOnThisChat && Object.keys(responses).length === 0) {
        // If we're on this chat but have no responses, force reload
        console.log(
          "On chat but no responses, forcing reload from backend:",
          id,
        );
        loadChat(id, currentChatId, responses, query).catch((error) => {
          if (
            error.response?.status === 404 ||
            error.message === "Chat not found"
          ) {
            console.warn(
              "[useEffect] Chat not found, removing from sidebar and navigating:",
              id,
            );
            // Remove chat from sidebar
            setChats((prev) =>
              prev.filter((c) => {
                const chatId16 = c.chatId16 || c.chatId || c._id;
                return chatId16 !== id && c._id !== id && c.chatId !== id;
              }),
            );
            // Clear state
            setCurrentChatId(null);
            setResponses({});
            setQuery("");
            setSelectedModels(["ChatGPT", "Gemini"]);
            // Navigate away
            navigate(workspaceBasePath, { replace: true });
          } else {
            console.error("[useEffect] Error loading chat from URL:", error);
            navigate(workspaceBasePath, { replace: true });
          }
        });
      }
    }
  }, [id, chats, user]); // Re-run only when URL param, chats, or user changes

  // When user opens a different chat or New chat (id from URL changes or becomes undefined), always show Comparative Analysis tab
  useEffect(() => {
    setActiveTab("comparison");
  }, [id]);

  // New Chat (sidebar, deleting the open chat, switching project) clears the
  // results and drops the chat id but left the stage pointer wherever it
  // was, so from Comparative Analysis / Final Synthesis the workspace showed
  // an empty stage-2/3 panel with no composer and looked like the navigation
  // had failed (QA #33). An empty workspace has only stage 1.
  useEffect(() => {
    if (!id && !currentChatId) setActiveStage("initial");
  }, [id, currentChatId]);

  // Debug: Track query state changes
  useEffect(() => {
    console.log(
      "[Query State Change] Current query value:",
      query,
      "Length:",
      query?.length || 0,
      "currentChatId:",
      currentChatId,
      "id:",
      id,
    );
  }, [query, currentChatId, id]);

  useEffect(() => {
    // Only reset if we're truly on a new/empty chat (no id, no currentChatId, no responses with content)
    if (
      !id &&
      !currentChatId &&
      !queryLoading &&
      !comparisonLoading &&
      !synthesisLoading
    ) {
      // Check if there are any responses with actual content
      const hasResponsesWithContent = Object.keys(responses).some((model) => {
        const responseData = responses[model];
        return (
          responseData &&
          ((responseData.text && responseData.text.trim() !== "") ||
            responseData.error)
        );
      });

      // Only reset if there are no meaningful responses
      if (!hasResponsesWithContent) {
        if (Object.keys(responses).length > 0) {
          setResponses({});
        }
      }
    }
  }, [id, currentChatId, queryLoading, comparisonLoading, synthesisLoading]);

  useEffect(() => {
    if (queryLoading) {
      const failsafeTimeout = setTimeout(() => {
        console.warn("Query loading timeout - resetting queryLoading state");
        setQueryLoading(false);
      }, 120000);

      return () => clearTimeout(failsafeTimeout);
    }
  }, [queryLoading]);

  const checkAndRedirectToPricing = async (signal = null) => {
    if (!user) return;

    try {
      const token = localStorage.getItem("token");
      const config = {
        withCredentials: true,
      };
      if (token) {
        config.headers = { Authorization: `Bearer ${token}` };
      }
      if (signal) {
        config.signal = signal;
      }

      // Fetch subscription status
      const subscriptionResponse = await axios.get(
        `${API_URL}/payments/subscription-status`,
        config,
      );
      const subscription = subscriptionResponse.data;

      const mergedUser = {
        ...user,
        subscriptionStatus: subscription.subscriptionStatus,
        subscriptionAccessUntil: subscription.subscriptionAccessUntil,
      };
      const paymentBlocked =
        subscription.paymentAccessBlocked ??
        shouldBlockSubscriptionAccess(mergedUser);
      const paymentWarning = isSubscriptionPaymentWarning(mergedUser);
      const paymentMessage = getSubscriptionPaymentMessage(user, subscription);

      setSubscriptionPaymentBlocked(!!paymentBlocked);
      setSubscriptionPaymentMessage(paymentMessage || "");

      if (paymentBlocked) {
        if (!paymentFailedToastShownRef.current) {
          paymentFailedToastShownRef.current = true;
          showWarning(
            paymentMessage ||
              "Subscription suspended — update your payment method to restore access.",
            "Payment Required",
          );
        }
        return;
      }
      paymentFailedToastShownRef.current = false;

      if (paymentWarning) {
        if (!paymentGraceToastShownRef.current) {
          paymentGraceToastShownRef.current = true;
          showWarning(
            paymentMessage ||
              "Payment failed — update billing to avoid losing access.",
            "Payment Failed",
          );
        }
      } else {
        paymentGraceToastShownRef.current = false;
      }

      // Check if user is first-time (no subscription, no trial, no usage allotted)
      const hasActiveSubscription =
        subscription.subscriptionStatus === "active" ||
        subscription.subscriptionStatus === "trialing";
      const hasUsageAllotted =
        (subscription.aiMonthlyLimit || 0) > 0 ||
        (subscription.subscriptionActionsLimit || 0) > 0 ||
        (user?.aiMonthlyLimit || 0) > 0 ||
        (user?.subscriptionActionsLimit || 0) > 0;
      const hasAdminAllocatedActions =
        (subscription.adminAllocatedActionsTotal || 0) > 0 ||
        (user?.adminAllocatedActionsTotal || 0) > 0;
      const isFirstTimeUser =
        !hasActiveSubscription && !hasUsageAllotted && !hasAdminAllocatedActions;

      if (isFirstTimeUser) {
        // Redirect to pricing page
        navigate("/pricing", { replace: true });
        showInfo(
          "Please subscribe to a plan or get usage allotted from admin to use AI features.",
          "Subscription Required",
        );
      }
    } catch (error) {
      if (
        error.response?.status === 401 ||
        error.name === "AbortError" ||
        error.name === "CanceledError"
      ) {
        return;
      }
      console.error("Error checking subscription status:", error);
    }
  };

  const calculateMenuPosition = React.useCallback((chatId, buttonElement) => {
    if (!buttonElement) {
      console.warn("No button element provided for menu position calculation");
      return { top: 0, left: 0, visible: false };
    }

    const buttonRect = buttonElement.getBoundingClientRect();
    const menuWidth = 130;
    const menuHeight = 90;
    const verticalPadding = 8; // Space between button and menu
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const horizontalPadding = 12; // Minimum distance from viewport edges

    const buttonCenterX = buttonRect.left + buttonRect.width / 2;

    let left = buttonCenterX - menuWidth / 2;

    let top = buttonRect.bottom + verticalPadding;

    if (left < horizontalPadding) {
      left = horizontalPadding;
    }

    if (left + menuWidth > viewportWidth - horizontalPadding) {
      left = viewportWidth - menuWidth - horizontalPadding;
    }

    if (top + menuHeight > viewportHeight - horizontalPadding) {
      top = buttonRect.top - menuHeight - verticalPadding;

      if (top < horizontalPadding) {
        top = viewportHeight - menuHeight - horizontalPadding;
      }
    }

    if (top < horizontalPadding) {
      top = horizontalPadding;
    }

    const isVisibleInViewport =
      top >= horizontalPadding &&
      left >= horizontalPadding &&
      top + menuHeight <= viewportHeight - horizontalPadding &&
      left + menuWidth <= viewportWidth - horizontalPadding;

    return { top, left, visible: isVisibleInViewport };
  }, []);

  const handleMenuToggle = (chatId, e) => {
    e.stopPropagation();
    const buttonElement = e.currentTarget;

    if (openMenuId === chatId) {
      setOpenMenuId(null);
      setMenuPosition((prev) => {
        const newPos = { ...prev };
        delete newPos[chatId];
        return newPos;
      });
    } else {
      setOpenMenuId(null);
      setTimeout(() => {
        const position = calculateMenuPosition(chatId, buttonElement);
        setMenuPosition((prev) => ({ ...prev, [chatId]: position }));
        setOpenMenuId(chatId);
      }, 50);
    }
  };

  useEffect(() => {
    const handleHeaderMenuToggle = (event) => {
      if (!event?.target?.closest?.(".header-mobile-toggle")) return;
      setSidebarOpen(false);
    };

    document.addEventListener("click", handleHeaderMenuToggle, true);
    return () => {
      document.removeEventListener("click", handleHeaderMenuToggle, true);
    };
  }, []);

  useEffect(() => {
    if (!openMenuId) return;

    const updateMenuPosition = () => {
      const buttonElement = document.querySelector(
        `[data-chat-id="${openMenuId}"]`,
      );
      if (buttonElement) {
        const position = calculateMenuPosition(openMenuId, buttonElement);
        setMenuPosition((prev) => ({ ...prev, [openMenuId]: position }));
      }
    };

    const timeoutId = setTimeout(updateMenuPosition, 10);

    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);
    const chatList = chatListRef.current;
    if (chatList) {
      chatList.addEventListener("scroll", updateMenuPosition, {
        passive: true,
      });
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
      if (chatList) {
        chatList.removeEventListener("scroll", updateMenuPosition);
      }
    };
  }, [openMenuId]);

  useEffect(() => {
    if (!openMenuId) return;

    const handleClickOutside = (event) => {
      const menuElement = event.target.closest(".chat-menu-dropdown-portal");
      const buttonElement = event.target.closest("[data-chat-id]");
      const chatItem = event.target.closest(".sidebar-chat-item");

      if (menuElement) return;

      if (buttonElement?.getAttribute("data-chat-id") === openMenuId) return;

      setOpenMenuId(null);
      setMenuPosition((prev) => {
        const newPos = { ...prev };
        delete newPos[openMenuId];
        return newPos;
      });
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [openMenuId]);

  // Mobile-friendly infinite scroll for chat sidebar.
  // Use scroll position instead of IntersectionObserver (more reliable in transformed containers).
  useEffect(() => {
    if (!sidebarOpen) return;

    const listEl = chatListRef.current;
    if (!listEl) return;

    const BOTTOM_THRESHOLD_PX = 80;
    const onScroll = () => {
      if (
        !chatsHasMore ||
        loadingMore ||
        loadingChats ||
        !!searchQuery.trim()
      ) {
        return;
      }

      const distanceToBottom =
        listEl.scrollHeight - listEl.scrollTop - listEl.clientHeight;
      const isNearBottom = distanceToBottom <= BOTTOM_THRESHOLD_PX;

      if (isNearBottom && !wasNearBottomRef.current) {
        loadMoreChats();
      }

      wasNearBottomRef.current = isNearBottom;
    };

    listEl.addEventListener("scroll", onScroll, { passive: true });
    return () => listEl.removeEventListener("scroll", onScroll);
  }, [
    sidebarOpen,
    chats.length,
    chatsHasMore,
    loadingMore,
    loadingChats,
    searchQuery,
    loadMoreChats,
  ]);

  // Reset bottom-crossing latch when reopening sidebar or changing routes.
  // Prevents stale "near-bottom" state from blocking pagination after navigation.
  useEffect(() => {
    wasNearBottomRef.current = false;
  }, [sidebarOpen, location.pathname]);

  // Keep loading chat pages until the sidebar list becomes scrollable.
  // This mirrors desktop behavior when the viewport is tall on mobile Safari.
  useEffect(() => {
    if (!sidebarOpen) return;
    if (loadingChats || loadingMore || !chatsHasMore || searchQuery.trim()) return;

    const listEl = chatListRef.current;
    if (!listEl) return;

    const canScroll = listEl.scrollHeight > listEl.clientHeight + 4;
    if (!canScroll) {
      loadMoreChats();
    }
  }, [
    sidebarOpen,
    chats.length,
    chatsHasMore,
    loadingChats,
    loadingMore,
    searchQuery,
    loadMoreChats,
  ]);

  // Close projects dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        projectsDropdownRef.current &&
        !projectsDropdownRef.current.contains(e.target)
      ) {
        setProjectsDropdownOpen(false);
      }
    };
    if (projectsDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside, true);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside, true);
    }
  }, [projectsDropdownOpen]);

  const filteredChats = chats.filter((chat) => {
    if (
      chat.chatType === "debate" ||
      (chat.debateRounds &&
        Array.isArray(chat.debateRounds) &&
        chat.debateRounds.length > 0)
    ) {
      return false;
    }

    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const title = (chat.title || "").toLowerCase();
    const chatQuery = (chat.query || "").toLowerCase();
    const responses = Object.values(chat.responses || {})
      .map((r) => (typeof r === "string" ? r : r?.text || ""))
      .join(" ")
      .toLowerCase();
    return title.includes(q) || chatQuery.includes(q) || responses.includes(q);
  });

  const isChatStoppedPersisted = useCallback((chat) => {
    if (typeof window === "undefined" || !chat) return false;
    const possibleIds = [chat._id, chat.chatId, chat.chatId16, chat.id].filter(
      Boolean,
    );
    return possibleIds.some(
      (chatId) => localStorage.getItem(`stopped_chat_${chatId}`) === "true",
    );
  }, []);

  // Group chats by project for sidebar (chats with no project first with no label, then each project)
  const projectSections = [
    {
      key: "none",
      label: "",
      chats: filteredChats.filter((c) => !c.projectId),
    },
  ];
  projects.forEach((p) => {
    projectSections.push({
      key: p._id,
      label: p.name,
      chats: filteredChats.filter((c) => c.projectId === p._id),
    });
  });

  // When a project is selected from dropdown, show only that project's chats
  const chatsForSelectedProject =
    selectedProjectId != null
      ? filteredChats.filter((c) => c.projectId === selectedProjectId)
      : null;
  const selectedProjectName = selectedProjectId
    ? projects.find((p) => p._id === selectedProjectId)?.name || "Project"
    : null;

  // Date utility functions are now imported from utils/dateUtils.js

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isSearchOpen) {
        if (e.key === "Escape") {
          setIsSearchOpen(false);
          setSearchQuery("");
        }
      } else if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchDialogLoaded(true);
        setIsSearchOpen(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isSearchOpen]);

  const searchInputRef = useRef(null);
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isSearchOpen]);

  const handleChatSelect = async (chatId) => {
    const chat = chats.find((c) => (c.chatId || c._id) === chatId);
    const chatId16 = chat ? chat.chatId16 || chat.chatId || chat._id : chatId;
    const idToLoad = chat ? chat.chatId || chat._id : chatId;

    researchChatNavFromSidebarRef.current = String(chatId16);
    try {
      setIsSearchOpen(false);
      setSearchQuery("");
      if (isMobileScreen && sidebarOpen) {
        setSidebarOpen(false);
      }
      navigate(`${workspaceBasePath}/${chatId16}`, { replace: true });
      await loadChat(idToLoad, currentChatId, responses, query);
    } finally {
      researchChatNavFromSidebarRef.current = null;
    }
  };

  // Calculate progress based on AI Actions (primary) or Tokens (fallback)
  const aiProgress =
    (aiUsage.aiMonthlyLimit || 100) > 0
      ? Math.min(
        ((aiUsage.aiActionsUsed || 0) / (aiUsage.aiMonthlyLimit || 100)) *
        100,
        100,
      )
      : (aiUsage.tokenLimit || 2000000) > 0
        ? Math.min(
          ((aiUsage.totalTokensUsed || 0) / (aiUsage.tokenLimit || 2000000)) *
          100,
          100,
        )
        : 0;

  const toggleModel = async (model) => {
    if (queryLoading || comparisonLoading || synthesisLoading) {
      return;
    }

    let newSelectedModels = [];
    let shouldUpdate = false;

    if (selectedModels.includes(model)) {
      // After initial query, prevent deselecting models that already have responses
      const r = responses[model];
      if (initialQueryCompleteLock && r && r.text && r.text.trim() !== "" && !r.error) {
        return;
      }
      newSelectedModels = selectedModels.filter((m) => m !== model);
      shouldUpdate = true;
    } else {
      const maxAllowed = !isLoggedIn ? 2 : 4;
      if (selectedModels.length >= maxAllowed) {
        return;
      }
      newSelectedModels = [...selectedModels, model];
      shouldUpdate = true;

      if (responses[model] && responses[model].error) {
        setResponses((prev) => ({
          ...prev,
          [model]: { ...prev[model], error: null },
        }));
      }
    }

    if (shouldUpdate) {
      setSelectedModels(newSelectedModels);

      // Only persist for authenticated users. Guest choices must not be saved
      // to localStorage \u2014 a previously logged-in session may have stored a paid
      // model (e.g. Grok) that would then override the free-tier defaults on next visit.
      if (isLoggedIn) {
        localStorage.setItem(
          "userSelectedModels",
          JSON.stringify(newSelectedModels),
        );
      }

      if (currentChatId && currentChatId !== "new") {
        try {
          await updateChat(currentChatId, {
            selectedModels: newSelectedModels,
          });
        } catch (error) {
          console.error("Background save failed:", error);
        }
      }
    }
  };

  const removeModel = async (model) => {
    const confirmed = await confirm({
      title: "Remove Response",
      message: `Are you sure you want to remove ${model}'s response? This will clear the response analysis for this model.`,
      confirmText: "Remove",
      cancelText: "Cancel",
      type: "danger",
    });
    if (!confirmed) return;

    const newSelectedModels = selectedModels.filter((m) => m !== model);
    // CRITICAL: Only keep responses for models that will remain selected after removal
    const newResponses = {};
    newSelectedModels.forEach((m) => {
      if (responses[m]) {
        newResponses[m] = responses[m];
      }
    });

    setSelectedModels(newSelectedModels);
    setResponses(newResponses);

    if (currentChatId && currentChatId !== "new") {
      try {
        const backendResponses = {};
        Object.entries(newResponses).forEach(([m, data]) => {
          if (typeof data === "string") {
            backendResponses[m] = data;
          } else if (data && typeof data === "object") {
            // Preserve formatted HTML if it exists (from editor edits)
            backendResponses[m] = data.formatted
              ? { text: data.text || "", formatted: data.formatted }
              : data.text || "";
          }
        });

        await updateChat(currentChatId, {
          selectedModels: newSelectedModels,
          responses: backendResponses,
        });
      } catch (error) {
        console.error("Error updating chat after deletion:", error);
        showError("Failed to save changes to server");
      }
    }
  };

  const handleLogout = async () => {
    navigate("/login", { replace: true });
    await logout();
  };

  // handleFileUpload is now provided by useFileUpload hook

  const handleFileRemove = (fileType, file = null) => {
    handleChatFileRemove(fileType, file);

    // Allow the user to upload another file after removing one
    if (trialUploadCountRef.current > 0) {
      trialUploadCountRef.current -= 1;
    }
    if (anonUploadCountRef.current > 0) {
      anonUploadCountRef.current -= 1;
    }
  };

  const handleRunComparison = async () => {
    // Prevent double-clicks — block immediately
    if (comparisonLoading) return;

    const responsesArray = Object.entries(responses)
      .filter(
        ([model, data]) =>
          selectedModels.includes(model) &&
          getResponsePlain(data).trim() !== "",
      )
      .map(([model, data]) => ({ model, text: getResponsePlain(data) }));

    if (responsesArray.length < 2) {
      return;
    }

    if (queryLoading) {
      return;
    }

    if (!query || query.trim() === "") {
      return;
    }

    setComparisonLoading(true);

    // Check subscription only for logged-in, non-trial users.
    // Anonymous (free-tier) users are limited to 3 uses/week enforced by the backend.
    // Trial users have their own limits enforced by the backend (checkTrialLimits).
    if (isLoggedIn && !isTrial) {
      try {
        const token = localStorage.getItem("token");
        const config = {
          withCredentials: true,
        };
        if (token) {
          config.headers = { Authorization: `Bearer ${token}` };
        }

        const subscriptionResponse = await axios.get(
          `${API_URL}/payments/subscription-status`,
          config,
        );
        const subscription = subscriptionResponse.data;

        const mergedSubUser = {
          ...user,
          subscriptionStatus: subscription.subscriptionStatus,
          subscriptionAccessUntil: subscription.subscriptionAccessUntil,
        };
        if (
          subscription.paymentAccessBlocked ??
          shouldBlockSubscriptionAccess(mergedSubUser)
        ) {
          showError(
            getSubscriptionPaymentMessage(user, subscription) ||
              "Subscription suspended — update your payment method to restore access.",
            "Payment Required",
          );
          return;
        }

        const hasActiveSubscription =
          subscription.subscriptionStatus === "active" ||
          subscription.subscriptionStatus === "trialing";
        const hasUsageAllotted =
          (subscription.aiMonthlyLimit || 0) > 0 ||
          (subscription.subscriptionActionsLimit || 0) > 0 ||
          (user?.aiMonthlyLimit || 0) > 0 ||
          (user?.subscriptionActionsLimit || 0) > 0;
        const hasAdminAllocatedActions =
          (subscription.adminAllocatedActionsTotal || 0) > 0 ||
          (user?.adminAllocatedActionsTotal || 0) > 0;

        if (
          !hasActiveSubscription &&
          !hasUsageAllotted &&
          !hasAdminAllocatedActions
        ) {
          showError(
            "Please subscribe to a plan or get usage allotted from admin to use AI features.",
            "Subscription Required",
          );
          navigate("/pricing", { replace: true });
          return;
        }

        if (!hasUsageAllotted && !hasAdminAllocatedActions) {
          showError(
            "No AI action allocation. Please subscribe to a plan or contact support to get usage allotted.",
            "No Usage Allotted",
          );
          navigate("/pricing", { replace: true });
          return;
        }
      } catch (error) {
        console.error("Error checking subscription before comparison:", error);
        showError(
          "Unable to verify subscription status. Please try again.",
          "Verification Error",
        );
        return;
      }
    }

    const operationId = `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    setComparisonLoading(true);

    const customPromptToSend = comparisonQuery.trim() || null;

    const recoverComparisonProgress = async () => {
      const resumeChatId = currentChatIdRef.current || currentChatId;
      if (!isLoggedIn || !resumeChatId) return false;
      for (let attempt = 0; attempt < 45; attempt++) {
        try {
          const chat = await pollChat(resumeChatId);
          const text = chat?.comparisonResult || "";
          if (text.trim()) {
            setComparisonResult(removeCitationMarkers(text));
            setComparisonGenerated(true);
            return true;
          }
        } catch (_) {
          // Keep retrying while backend persists more text.
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      return false;
    };

    try {
      let authToken = localStorage.getItem("token");
      if (!authToken) {
        try {
          const tokenResponse = await axios.get(`${API_URL}/auth/token`);
          authToken = tokenResponse.data.token;
          if (authToken) {
            localStorage.setItem("token", authToken);
          }
        } catch (tokenError) {
          console.warn("Could not get token from backend:", tokenError);
        }
      }

      const headers = {
        "Content-Type": "application/json",
      };
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const requestBody = {
        originalQuestion: query,
        responses: responsesArray,
        comparisonModel: comparisonModel,
        chatId: currentChatId,
        operationId: operationId, // Group all actions from this button press
        customPrompt: customPromptToSend,
        fileText: getCurrentChatFiles().comparisonFile?.fileText || null,
        fileName: getCurrentChatFiles().comparisonFile?.fileName || null,
        attachedFiles: {
          mainQueryFile: getCurrentChatFiles().mainQueryFile
            ? {
              fileName: getCurrentChatFiles().mainQueryFile.fileName,
              s3Key: getCurrentChatFiles().mainQueryFile.s3Key,
              s3Url: getCurrentChatFiles().mainQueryFile.s3Url,
              fileType: getCurrentChatFiles().mainQueryFile.fileType,
            }
            : null,
          comparisonFile: getCurrentChatFiles().comparisonFile
            ? {
              fileName: getCurrentChatFiles().comparisonFile.fileName,
              s3Key: getCurrentChatFiles().comparisonFile.s3Key,
              s3Url: getCurrentChatFiles().comparisonFile.s3Url,
              fileType: getCurrentChatFiles().comparisonFile.fileType,
            }
            : null,
          synthesisFile: getCurrentChatFiles().synthesisFile
            ? {
              fileName: getCurrentChatFiles().synthesisFile.fileName,
              s3Key: getCurrentChatFiles().synthesisFile.s3Key,
              s3Url: getCurrentChatFiles().synthesisFile.s3Url,
              fileType: getCurrentChatFiles().synthesisFile.fileType,
            }
            : null,
        },
      };

      // Debug logging for deployment
      console.log("Compare request to:", `${API_URL}/ai/compare-stream`);
      console.log("Request body:", {
        originalQuestion: requestBody.originalQuestion ? "present" : "MISSING",
        responses: requestBody.responses?.length || 0,
        comparisonModel: requestBody.comparisonModel || "MISSING",
      });

      const response = await fetch(`${API_URL}/ai/compare-stream`, {
        method: "POST",
        headers: headers,
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = `Server error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
          console.error("Backend error response:", errorData);
        } catch (e) {
          console.error("Could not parse error response:", e);
        }
        throw new Error(errorMessage);
      }

      if (!response.body || typeof response.body.getReader !== "function") {
        const recovered = await recoverComparisonProgress();
        if (recovered) {
          setComparisonLoading(false);
          return;
        }
        throw new Error(
          "Connection was interrupted while the app was in the background. Please try again.",
        );
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = null;
      let finalData = null;
      let streamError = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));

              if (currentEvent === "chunk") {
                setComparisonResult(removeCitationMarkers(data.fullText || ""));
                setComparisonGenerated(true);
              } else if (currentEvent === "start") {
                setComparisonResult("");
                setComparisonGenerated(false);
              } else if (currentEvent === "done") {
                setComparisonResult(removeCitationMarkers(data.text || ""));
                setComparisonGenerated(true);
              } else if (currentEvent === "complete") {
                finalData = data;
              } else if (currentEvent === "error") {
                // Limit exceeded checks removed - unlimited access enabled
                streamError = new Error(
                  data.message || "Error processing comparison",
                );
                break; // Exit the loop on error
              }
            } catch (e) {
              console.error("Error parsing SSE data:", e, line);
            }
            currentEvent = null;
          } else if (line.trim() === "") {
            currentEvent = null;
          }
        }

        if (streamError) {
          break; // Exit the outer loop on error
        }
      }

      if (streamError) {
        const recovered = await recoverComparisonProgress();
        if (recovered) {
          setComparisonLoading(false);
          return;
        }
        setComparisonLoading(false);
        // Suppress backend userId errors for anonymous users — the backend tries to
        // read req.user.userId to save/log the result but the route still processes
        // the comparison correctly before hitting that code path.
        const isUserIdError =
          streamError.message &&
          streamError.message.includes("Cannot read properties of undefined");
        if (!isLoggedIn && isUserIdError) {
          // The comparison result was already streamed — record it in history
          // so the button disables correctly for free-tier users.
          setComparisonGenerated(true);
          setComparisonHistory((prev) => [
            {
              model: comparisonModel,
              query: comparisonQuery || "",
              result: "(completed)",
              timestamp: new Date().toISOString(),
            },
            ...prev,
          ]);
          return;
        }
        showError(streamError.message, "Comparison Error");
        return;
      }

      if (finalData) {
        setComparisonResult(
          removeCitationMarkers(finalData.comparisonResult || ""),
        );
        setComparisonGenerated(true);
        setSelectedComparisonHistoryIndex(null);

        // Get the models that were actually used for comparison - prefer from backend, fallback to responsesArray
        const modelsUsed =
          finalData.modelsAnalyzed || responsesArray.map((r) => r.model);

        const historyEntry = {
          model: comparisonModel,
          query: comparisonQuery || "",
          result: removeCitationMarkers(finalData.comparisonResult || ""),
          timestamp: finalData.timestamp || new Date().toISOString(),
          modelsAnalyzed: modelsUsed, // Store which models were analyzed
        };
        setComparisonHistory((prev) => [historyEntry, ...prev]);

        await fetchAIUsage();
      }
    } catch (error) {
      let errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error running comparison";

      if (error.message && error.message.includes("Failed to fetch")) {
        errorMessage =
          "Unable to connect to the backend API. Please check your network connection and ensure the backend is running.";
      } else if (errorMessage.includes("400")) {
        errorMessage =
          "Invalid request. Make sure you have run an initial query first and have at least 2 AI responses.";
      }

      if (
        !isLoggedIn &&
        errorMessage.includes("Cannot read properties of undefined")
      ) {
        // Record in history so button disables for free-tier users
        setComparisonGenerated(true);
        setComparisonHistory((prev) => [
          {
            model: comparisonModel,
            query: comparisonQuery || "",
            result: "(completed)",
            timestamp: new Date().toISOString(),
          },
          ...prev,
        ]);
        return;
      }

      const recovered = await recoverComparisonProgress();
      if (recovered) {
        return;
      }

      showError(errorMessage, "Comparison Error");
    } finally {
      setComparisonLoading(false);
    }
  };

  const handleRunSynthesis = async () => {
    if (!comparisonResult?.trim()) {
      showWarning(
        "Please run Comparative Analysis first before generating Final Synthesis.",
        "Comparative Analysis Required",
      );
      return;
    }

    // Include all models that have a response, not just currently selected ones
    const responsesArray = Object.entries(responses)
      .filter(([, data]) => getResponsePlain(data).trim() !== "")
      .map(([model, data]) => ({ model, text: getResponsePlain(data) }));

    let originalQuestion = query?.trim();
    if (!originalQuestion && responsesArray.length > 0) {
      originalQuestion = "Synthesize the following AI responses";
      console.log("Using fallback query for synthesis:", originalQuestion);
    }

    if (!originalQuestion) {
      showWarning(
        "Cannot run synthesis - no query or responses available",
        "Missing Data",
      );
      return;
    }

    if (queryLoading) {
      showWarning(
        "Please wait for the initial query to complete",
        "Query In Progress",
      );
      return;
    }

    // No subscription/token/AI action check for final synthesis - allow run regardless of limits

    const operationId = `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    setSynthesisLoading(true);

    const customPromptToSend = synthesisQuery.trim() || null;

    const recoverSynthesisProgress = async () => {
      const resumeChatId = currentChatIdRef.current || currentChatId;
      if (!isLoggedIn || !resumeChatId) return false;
      for (let attempt = 0; attempt < 45; attempt++) {
        try {
          const chat = await pollChat(resumeChatId);
          const text = chat?.synthesisResult || "";
          if (text.trim()) {
            setSynthesisResult(removeCitationMarkers(text));
            setSynthesisGenerated(true);
            return true;
          }
        } catch (_) {
          // Keep retrying while backend persists more text.
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      return false;
    };

    try {
      let authToken = localStorage.getItem("token");
      if (!authToken) {
        try {
          const tokenResponse = await axios.get(`${API_URL}/auth/token`);
          authToken = tokenResponse.data.token;
          if (authToken) {
            localStorage.setItem("token", authToken);
          }
        } catch (tokenError) {
          console.warn("Could not get token from backend:", tokenError);
        }
      }

      const headers = {
        "Content-Type": "application/json",
      };
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const response = await fetch(`${API_URL}/ai/synthesize-stream`, {
        method: "POST",
        headers: headers,
        credentials: "include",
        body: JSON.stringify({
          originalQuestion: originalQuestion,
          responses: responsesArray,
          comparisonResult: comparisonResult || "",
          synthesisModel: synthesisModel,
          chatId: currentChatId,
          operationId: operationId, // Group all actions from this button press
          customPrompt: customPromptToSend,
          fileText: getCurrentChatFiles().synthesisFile?.fileText || null,
          fileName: getCurrentChatFiles().synthesisFile?.fileName || null,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Server error: ${response.status} ${response.statusText}`,
        );
      }

      if (!response.body || typeof response.body.getReader !== "function") {
        const recovered = await recoverSynthesisProgress();
        if (recovered) {
          setSynthesisLoading(false);
          return;
        }
        throw new Error(
          "Connection was interrupted while the app was in the background. Please try again.",
        );
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = null;
      let finalData = null;
      let streamError = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));

              if (currentEvent === "chunk") {
                setSynthesisResult(removeCitationMarkers(data.fullText || ""));
                setSynthesisGenerated(true);
              } else if (currentEvent === "start") {
                setSynthesisResult("");
                setSynthesisGenerated(false);
              } else if (currentEvent === "done") {
                setSynthesisResult(removeCitationMarkers(data.text || ""));
                setSynthesisGenerated(true);
              } else if (currentEvent === "complete") {
                finalData = data;
              } else if (currentEvent === "error") {
                streamError = new Error(
                  data.message || "Error processing synthesis",
                );
                break; // Exit the loop on error
              }
            } catch (e) {
              console.error("Error parsing SSE data:", e, line);
            }
            currentEvent = null;
          } else if (line.trim() === "") {
            currentEvent = null;
          }
        }

        if (streamError) {
          break; // Exit the outer loop on error
        }
      }

      if (streamError) {
        const recovered = await recoverSynthesisProgress();
        if (recovered) {
          setSynthesisLoading(false);
          return;
        }
        setSynthesisLoading(false);
        showError(streamError.message, "Synthesis Error");
        return;
      }

      if (finalData) {
        setSynthesisResult(
          removeCitationMarkers(finalData.synthesisResult || ""),
        );
        setSynthesisGenerated(true);
        setSelectedSynthesisHistoryIndex(null);

        // Get the models that were actually used for synthesis - prefer from backend, fallback to responsesArray
        const modelsUsed =
          finalData.modelsAnalyzed || responsesArray.map((r) => r.model);

        const historyEntry = {
          model: synthesisModel,
          query: synthesisQuery || "",
          result: removeCitationMarkers(finalData.synthesisResult || ""),
          timestamp: finalData.timestamp || new Date().toISOString(),
          modelsAnalyzed: modelsUsed, // Store which models were analyzed
        };
        setSynthesisHistory((prev) => [historyEntry, ...prev]);
        if (modelsUsed?.length) setSynthesisModelsAnalyzed(modelsUsed);

        await fetchAIUsage();
      }
    } catch (error) {
      console.error("Synthesis error:", error);
      const recovered = await recoverSynthesisProgress();
      if (recovered) {
        return;
      }
      const errorMessage =
        error.response?.data?.message || "Error running synthesis";
      showError(errorMessage, "Synthesis Error");
    } finally {
      setSynthesisLoading(false);
    }
  };

  const getUserInitials = () => {
    if (user?.name) {
      const names = user.name.split(" ");
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  const renderUserAvatar = () => {
    if (user?.avatarUrl) {
      return (
        <img
          src={user.avatarUrl}
          alt="avatar"
          className="account-avatar-image"
        />
      );
    }
    return getUserInitials();
  };

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1024);
  const [isMediumScreen, setIsMediumScreen] = useState(
    window.innerWidth >= 500 && window.innerWidth <= 900,
  );
  const [isMobileScreen, setIsMobileScreen] = useState(window.innerWidth < 500);
  const [isPhoneScreen, setIsPhoneScreen] = useState(window.innerWidth <= 768);
  const [showComparisonEditTooltipMobile, setShowComparisonEditTooltipMobile] =
    useState(false);
  const [showComparisonEditTooltipDesktop, setShowComparisonEditTooltipDesktop] =
    useState(false);
  const comparisonEditTooltipTimerRef = useRef(null);
  const [
    showComparisonRunTooltipMobile,
    setShowComparisonRunTooltipMobile,
  ] = useState(false);
  const [
    showComparisonExecSummaryTooltipMobile,
    setShowComparisonExecSummaryTooltipMobile,
  ] = useState(false);
  const [
    showSynthesisExecSummaryTooltipMobile,
    setShowSynthesisExecSummaryTooltipMobile,
  ] = useState(false);
  const comparisonRunBtnRef = useRef(null);
  const comparisonExecSummaryWrapRef = useRef(null);
  const synthesisExecSummaryWrapRef = useRef(null);
  const comparisonEditExportWrapRef = useRef(null);
  const [headerHeightPx, setHeaderHeightPx] = useState(
    window.innerWidth < 500 ? 50 : 69,
  );

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const small = width <= 1024;
      const medium = width >= 500 && width <= 900;
      const mobile = width < 500;
      const phone = width <= 768;

      setIsSmallScreen(small);
      setIsMediumScreen(medium);
      setIsMobileScreen(mobile);
      setIsPhoneScreen(phone);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [sidebarOpen]);

  // Keep sidebar offset in sync with the real rendered header height.
  useEffect(() => {
    const updateHeaderHeight = () => {
      const headerEl = document.querySelector(
        ".dashboard-container .landing-header-wrapper",
      );
      const fallback = isMobileScreen ? 50 : 69;
      const measured = headerEl?.getBoundingClientRect?.().height || 0;
      setHeaderHeightPx(measured > 0 ? Math.round(measured) : fallback);
    };

    updateHeaderHeight();
    const timeoutId = setTimeout(updateHeaderHeight, 150);
    window.addEventListener("resize", updateHeaderHeight);
    window.addEventListener("orientationchange", updateHeaderHeight);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateHeaderHeight);
      window.removeEventListener("orientationchange", updateHeaderHeight);
    };
  }, [isMobileScreen]);

  useEffect(() => {
    return () => {
      if (comparisonEditTooltipTimerRef.current) {
        clearTimeout(comparisonEditTooltipTimerRef.current);
      }
    };
  }, []);

  // Mobile-only: keep tooltips open until user taps elsewhere.
  useEffect(() => {
    if (!isMobileScreen) return;
    const anyOpen =
      showComparisonEditTooltipMobile ||
      showComparisonRunTooltipMobile ||
      showComparisonExecSummaryTooltipMobile ||
      showSynthesisExecSummaryTooltipMobile;
    if (!anyOpen) return;

    const handlePointerDownCapture = (e) => {
      const target = e.target;
      const isInsideComparisonRunBtn =
        comparisonRunBtnRef.current &&
        target instanceof Node &&
        comparisonRunBtnRef.current.contains(target);
      const isInsideComparisonExecWrap =
        comparisonExecSummaryWrapRef.current &&
        target instanceof Node &&
        comparisonExecSummaryWrapRef.current.contains(target);
      const isInsideSynthesisExecWrap =
        synthesisExecSummaryWrapRef.current &&
        target instanceof Node &&
        synthesisExecSummaryWrapRef.current.contains(target);
      const isInsideComparisonEditExportWrap =
        comparisonEditExportWrapRef.current &&
        target instanceof Node &&
        comparisonEditExportWrapRef.current.contains(target);

      if (isInsideComparisonEditExportWrap) {
        setShowComparisonRunTooltipMobile(false);
        setShowComparisonExecSummaryTooltipMobile(false);
        setShowSynthesisExecSummaryTooltipMobile(false);
        return;
      }
      if (isInsideComparisonExecWrap) {
        setShowComparisonEditTooltipMobile(false);
        setShowComparisonRunTooltipMobile(false);
        setShowSynthesisExecSummaryTooltipMobile(false);
        return;
      }
      if (isInsideComparisonRunBtn) {
        setShowComparisonEditTooltipMobile(false);
        setShowComparisonExecSummaryTooltipMobile(false);
        setShowSynthesisExecSummaryTooltipMobile(false);
        return;
      }
      if (isInsideSynthesisExecWrap) {
        setShowComparisonEditTooltipMobile(false);
        setShowComparisonRunTooltipMobile(false);
        setShowComparisonExecSummaryTooltipMobile(false);
        return;
      }

      setShowComparisonEditTooltipMobile(false);
      setShowComparisonRunTooltipMobile(false);
      setShowComparisonExecSummaryTooltipMobile(false);
      setShowSynthesisExecSummaryTooltipMobile(false);
    };

    document.addEventListener("pointerdown", handlePointerDownCapture, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDownCapture, true);
    };
  }, [
    isMobileScreen,
    showComparisonEditTooltipMobile,
    showComparisonRunTooltipMobile,
    showComparisonExecSummaryTooltipMobile,
    showSynthesisExecSummaryTooltipMobile,
  ]);

  // Show a visible scroll thumb on mobile for the sidebar chat list.
  useEffect(() => {
    if (!isMobileScreen || !sidebarOpen) {
      setMobileScrollIndicator((prev) => ({ ...prev, visible: false }));
      return;
    }

    const listEl = chatListRef.current;
    if (!listEl) return;

    const updateIndicator = () => {
      const { scrollTop, scrollHeight, clientHeight } = listEl;
      if (scrollHeight <= clientHeight + 1) {
        setMobileScrollIndicator((prev) => ({ ...prev, visible: false }));
        return;
      }

      const height = Math.max(28, (clientHeight / scrollHeight) * clientHeight);
      const maxTop = Math.max(0, clientHeight - height);
      const top =
        maxTop === 0
          ? 0
          : (scrollTop / Math.max(1, scrollHeight - clientHeight)) * maxTop;

      setMobileScrollIndicator({ visible: true, top, height });
    };

    updateIndicator();
    listEl.addEventListener("scroll", updateIndicator, { passive: true });
    window.addEventListener("resize", updateIndicator);

    return () => {
      listEl.removeEventListener("scroll", updateIndicator);
      window.removeEventListener("resize", updateIndicator);
    };
  }, [isMobileScreen, sidebarOpen, loadingChats, loadingMore, chats.length, searchQuery]);

  // Prevent background page from scrolling when the mobile sidebar drawer is open.
  useEffect(() => {
    if (!isMobileScreen || !sidebarOpen) return;

    const scrollY = window.scrollY;
    const htmlEl = document.documentElement;
    const bodyEl = document.body;

    const previousHtmlOverflow = htmlEl.style.overflow;
    const previousHtmlOverscroll = htmlEl.style.overscrollBehavior;
    const previousBodyOverflow = bodyEl.style.overflow;
    const previousBodyPosition = bodyEl.style.position;
    const previousBodyTop = bodyEl.style.top;
    const previousBodyWidth = bodyEl.style.width;
    const previousBodyOverscroll = bodyEl.style.overscrollBehavior;
    const previousBodyTouchAction = bodyEl.style.touchAction;

    htmlEl.style.overflow = "hidden";
    htmlEl.style.overscrollBehavior = "none";
    bodyEl.style.overflow = "hidden";
    bodyEl.style.position = "fixed";
    bodyEl.style.top = `-${scrollY}px`;
    bodyEl.style.width = "100%";
    bodyEl.style.overscrollBehavior = "none";
    bodyEl.style.touchAction = "none";

    return () => {
      htmlEl.style.overflow = previousHtmlOverflow;
      htmlEl.style.overscrollBehavior = previousHtmlOverscroll;
      bodyEl.style.overflow = previousBodyOverflow;
      bodyEl.style.position = previousBodyPosition;
      bodyEl.style.top = previousBodyTop;
      bodyEl.style.width = previousBodyWidth;
      bodyEl.style.overscrollBehavior = previousBodyOverscroll;
      bodyEl.style.touchAction = previousBodyTouchAction;
      window.scrollTo(0, scrollY);
    };
  }, [isMobileScreen, sidebarOpen]);

  const focusMainQueryInput = useCallback((delayMs = 0) => {
    window.setTimeout(() => {
      const input = document.querySelector(".query-textarea-slim");
      input?.focus();
    }, delayMs);
  }, []);

  const handleNewChatClick = useCallback(
    ({ closeSearch = false } = {}) => {
      createNewChat();

      if (closeSearch) {
        setIsSearchOpen(false);
        setSearchQuery("");
      }

      if (isMobileScreen && sidebarOpen) {
        setSidebarOpen(false);
        focusMainQueryInput(320);
        return;
      }

      focusMainQueryInput(0);
    },
    [
      createNewChat,
      focusMainQueryInput,
      isMobileScreen,
      sidebarOpen,
      setIsSearchOpen,
      setSearchQuery,
    ],
  );

  const handleCreateProject = async (name) => {
    if (isTrial && projects.length >= 1) {
      showWarning("Free trial users are limited to 1 project.", "Trial Limit");
      return;
    }
    const newProject = await createProject(name);
    showSuccess("Project created");

    // Auto-select the newly created project
    if (newProject && newProject._id) {
      setSelectedProjectId(newProject._id);
      try {
        localStorage.setItem("dashboardSelectedProjectId", newProject._id);
      } catch (e) {
        // ignore
      }
    }

    let moved = false;
    if (currentChatId && currentChatId !== "new") {
      const shouldMove = await confirm({
        title: "Move Chat to Project",
        message: `Would you like to move the current active chat into the new project "${name}"?`,
        confirmText: "Move Chat",
        cancelText: "No, Start New Chat",
        type: "info",
      });
      if (shouldMove) {
        await addChatToProject(currentChatId, newProject._id);
        moved = true;
      }
    }

    if (!moved) {
      createNewChat();
    }

    const list = await fetchProjects("dashboard");
    setProjects(list || []);
    fetchChats();
  };

  const handleSidebarProjectsClick = useCallback((e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    if (!sidebarOpen) {
      setSidebarOpen(true);
      setProjectsDropdownOpen(true);
      return;
    }
    setProjectsDropdownOpen((prev) => !prev);
  }, [sidebarOpen]);

  const handleSelectProject = useCallback(
    (projectId) => {
      setSelectedProjectId(projectId ?? null);

      try {
        if (projectId) {
          localStorage.setItem("dashboardSelectedProjectId", projectId);
        } else {
          localStorage.removeItem("dashboardSelectedProjectId");
        }
      } catch (e) {
        // ignore localStorage errors
      }

      // If user switches to a project that does not contain the currently open chat,
      // reset the main panel so stale chat content does not remain visible/locked.
      if (!projectId) return;
      if (!currentChatId && !id) return;

      const activeChatRef = currentChatId || id;
      const activeChat = chats.find((chat) => {
        const ids = [chat.chatId16, chat.chatId, chat._id].filter(Boolean);
        return ids.some((value) => String(value) === String(activeChatRef));
      });

      if (!activeChat || activeChat.projectId !== projectId) {
        createNewChat();
      }
    },
    [setSelectedProjectId, currentChatId, id, chats, createNewChat],
  );

  const handleRenameProject = async (projectId, newName) => {
    try {
      await updateProject(projectId, newName);
      // Optimistically update local state
      setProjects((prev) =>
        prev.map((p) => (p._id === projectId ? { ...p, name: newName } : p))
      );
      showSuccess("Project renamed");
    } catch (err) {
      console.error("Error renaming project:", err);
      showError(err?.response?.data?.message || "Failed to rename project", "Rename Error");
    }
  };

  const handleArchiveProject = async (project) => {
    const isCurrentlyArchived = !!project.archived;
    try {
      await archiveProject(project._id, !isCurrentlyArchived);
      // Remove from active list when archiving, or refresh
      if (!isCurrentlyArchived) {
        setProjects((prev) => prev.filter((p) => p._id !== project._id));
        if (selectedProjectId === project._id) {
          setSelectedProjectId(null);
          try { localStorage.removeItem("dashboardSelectedProjectId"); } catch (e) { /* ignore */ }
        }
        showSuccess(`"${project.name}" archived`);
      } else {
        // Unarchiving — reload active projects
        const list = await fetchProjects("dashboard");
        setProjects(list || []);
        showSuccess(`"${project.name}" restored`);
      }
    } catch (err) {
      console.error("Error archiving project:", err);
      showError(err?.response?.data?.message || "Failed to archive project", "Archive Error");
    }
  };

  const handleDeleteProject = async (project) => {
    if (!project) return;

    const confirmed = await confirm({
      title: "Delete Project",
      message: `Are you sure you want to permanently delete "${project.name}"? Chats inside this project will be moved to All Chats and not deleted. This action cannot be undone.`,
      confirmText: "Delete Project",
      cancelText: "Cancel",
      type: "danger",
    });

    if (!confirmed) return;

    setDeletingProjectId(project._id);
    try {
      await deleteProject(project._id);
      setProjects((prev) => prev.filter((p) => p._id !== project._id));
      if (selectedProjectId === project._id) {
        setSelectedProjectId(null);
        try {
          localStorage.removeItem("dashboardSelectedProjectId");
        } catch (e) {
          /* ignore */
        }
      }
      showSuccess(`"${project.name}" deleted`);
      fetchChats(); // chats have been moved to uncategorized
    } catch (err) {
      console.error("Error deleting project:", err);
      showError(
        err?.response?.data?.message || "Failed to delete project",
        "Delete Error",
      );
    } finally {
      setDeletingProjectId(null);
    }
  };



  return (
    <motion.div
      className={`dashboard-container ${darkMode ? "dark" : ""}`}
      style={{ "--header-height": `${headerHeightPx}px` }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <SEO title="Research Mode" path="/research" description="Run multi-model AI legal research with ChatGPT, Claude, Gemini, Grok, and Perplexity. Compare responses and generate executive summaries." noIndex={true} />
      {/* Header - show immediately so page feels fast; don't block on chat load */}
      <Header />

      {subscriptionPaymentBlocked && isLoggedIn && (
        <div
          role="alertdialog"
          aria-labelledby="subscription-lock-title"
          aria-describedby="subscription-lock-desc"
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            top: `${headerHeightPx}px`,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            padding: "1.5rem",
            textAlign: "center",
            background: darkMode
              ? "rgba(15, 23, 42, 0.92)"
              : "rgba(248, 250, 252, 0.96)",
            zIndex: 9998,
          }}
        >
          <Lock
            size={40}
            style={{ color: darkMode ? "#94a3b8" : "#64748b" }}
            aria-hidden
          />
          <h2
            id="subscription-lock-title"
            style={{
              margin: 0,
              fontSize: "1.25rem",
              fontWeight: 700,
              color: darkMode ? "#f1f5f9" : "#0f172a",
            }}
          >
            Payment Required
          </h2>
          <p
            id="subscription-lock-desc"
            style={{
              margin: 0,
              maxWidth: "28rem",
              fontSize: "0.95rem",
              lineHeight: 1.5,
              color: darkMode ? "#cbd5e1" : "#475569",
            }}
          >
            {subscriptionPaymentMessage ||
              "Subscription suspended — update your payment method to restore access."}
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.75rem",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              className="subscription-cta"
              onClick={() => navigate("/settings?tab=billing")}
            >
              Update billing
            </button>
            <button
              type="button"
              className="subscription-cta"
              style={{ background: "transparent", border: "1px solid currentColor" }}
              onClick={() => navigate("/pricing")}
            >
              View plans
            </button>
          </div>
        </div>
      )}

      <div
        className={`dashboard-main-wrapper ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
      >
        {/* The drawer overlays the page up to 900px (isMediumScreen covers
            501–900), so the tap-to-close backdrop has to exist there too. */}
        {(isMobileScreen || isMediumScreen) && sidebarOpen && (
          <button
            type="button"
            className="dashboard-mobile-backdrop"
            aria-label="Close sidebar"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        {/* Sidebar */}
        <motion.aside
          ref={sidebarRef}
          animate={{
            width: sidebarOpen
              ? isMediumScreen
                ? "30vw"
                : isSmallScreen && !isMobileScreen
                  ? "20vw"
                  : isMobileScreen
                    ? "40vw"
                    : "16vw"
              : isSmallScreen
                ? "10vw"
                : "5vw",
            x: 0,
            opacity: 1,
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className={`sidebar ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
          initial={{ x: 0, opacity: 0 }}
          style={{
            width: sidebarOpen
              ? isMediumScreen
                ? "30vw"
                : isSmallScreen && !isMobileScreen
                  ? "20vw"
                  : isMobileScreen
                    ? "40vw"
                    : "16vw"
              : isSmallScreen
                ? "10vw"
                : "5vw",
            minWidth: sidebarOpen
              ? isMediumScreen
                ? "30vw"
                : isSmallScreen && !isMobileScreen
                  ? "20vw"
                  : isMobileScreen
                    ? "40vw"
                    : "16vw"
              : isSmallScreen
                ? "10vw"
                : "5vw",
            maxWidth: sidebarOpen
              ? isMediumScreen
                ? "30vw"
                : isSmallScreen && !isMobileScreen
                  ? "20vw"
                  : isMobileScreen
                    ? "40vw"
                    : "16vw"
              : isSmallScreen
                ? "10vw"
                : "5vw",
            height: isMobileScreen
              ? `calc(100dvh - ${headerHeightPx}px)`
              : `calc(100dvh - ${headerHeightPx}px)`,
            overflow: "hidden",
            position: "fixed",
            left: 0,
            top: `${headerHeightPx}px`,
            zIndex: isMobileScreen ? 1100 : 900,
          }}
        >
          <div
            className="sidebar-content"
            style={{ paddingTop: 0, marginTop: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <motion.nav
              className="sidebar-nav"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{
                display: "flex",
                flexDirection: "column",
                flex: "1 1 0%",
                minHeight: 0,
                overflowX: "visible",
                overflowY: "hidden",
                position: "relative",
                paddingTop: "0.5rem",
              }}
            >
              {/* Collapse button - separate row when collapsed, on right when open */}
              {!sidebarOpen && (
                <motion.div
                  variants={itemVariants}
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  <button
                    className="sidebar-toggle-btn sidebar-collapse-btn-sidebar"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSidebarOpen(!sidebarOpen);
                    }}
                    title="Open sidebar"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "0.5rem",
                      color: darkMode ? "#cbd5e1" : "#1e3a8a",
                      borderRadius: "0.5rem",
                      minWidth: "44px",
                      minHeight: "44px",
                      transition: "background-color 0.2s",
                      flexShrink: 0,
                      zIndex: 100,
                    }}
                    aria-label="Toggle sidebar"
                  >
                    <PanelLeft size={20} />
                  </button>
                </motion.div>
              )}

              {/* New Chat with collapse button on the right when open */}
              <motion.div
                variants={itemVariants}
                style={{
                  flexShrink: 0,
                  display: sidebarOpen ? "flex" : "block",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                {sidebarOpen && (
                  <motion.div
                    className="sidebar-item"
                    onClick={() => handleNewChatClick()}
                    style={{
                      cursor: "pointer",
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <MessageSquare size={20} />
                    <span className="sidebar-item-label">New Chat</span>
                  </motion.div>
                )}
                {sidebarOpen && (
                  <button
                    className="sidebar-toggle-btn sidebar-collapse-btn-sidebar"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSidebarOpen(!sidebarOpen);
                    }}
                    title="Close sidebar"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "0.5rem",
                      color: darkMode ? "#cbd5e1" : "#1e3a8a",
                      borderRadius: "0.5rem",
                      minWidth: "44px",
                      minHeight: "44px",
                      transition: "background-color 0.2s",
                      flexShrink: 0,
                      zIndex: 100,
                    }}
                    aria-label="Toggle sidebar"
                  >
                    <PanelLeftClose size={20} />
                  </button>
                )}
                {!sidebarOpen && (
                  <motion.div
                    className="sidebar-item"
                    onClick={() => handleNewChatClick()}
                    style={{ cursor: "pointer" }}
                    title="New Chat"
                  >
                    <MessageSquare size={20} />
                  </motion.div>
                )}
              </motion.div>
              {isLoggedIn && (
                <motion.div variants={itemVariants} style={{ flexShrink: 0 }}>
                    <motion.div
                    className="sidebar-item"
                    onPointerDown={(e) => {
                      if (isMobileScreen) {
                        e.stopPropagation();
                        setSearchDialogLoaded(true);
                        setIsSearchOpen(true);
                      }
                    }}
                    onClick={(e) => {
                      if (!isMobileScreen) {
                        setSearchDialogLoaded(true);
                        setIsSearchOpen(true);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                    title={(!sidebarOpen && !isMobileScreen) ? "Search Chats" : undefined}
                  >
                    <Search size={20} />
                    {sidebarOpen && (
                      <span className="sidebar-item-label">Search Chats</span>
                    )}
                  </motion.div>
                </motion.div>
              )}

              {isLoggedIn && (
                <div
                  ref={projectsDropdownRef}
                  className="sidebar-projects-wrap"
                  style={{ position: 'relative', flexShrink: 0 }}
                >
                  <motion.div variants={itemVariants}>
                    <motion.div
                      className="sidebar-item"
                      onPointerDown={(e) => {
                        if (isMobileScreen) {
                          handleSidebarProjectsClick(e);
                        }
                      }}
                      onClick={(e) => {
                        if (!isMobileScreen) {
                          handleSidebarProjectsClick(e);
                        }
                      }}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        width: "100%",
                      }}
                      title={(!sidebarOpen && !isMobileScreen) ? "New Project" : undefined}
                    >
                      <FolderPlus size={20} />
                      {sidebarOpen && (
                        <>
                          <span
                            className="sidebar-item-label"
                            style={{
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {selectedProjectName || "Projects"}
                          </span>
                          <ChevronDown
                            size={16}
                            style={{
                              transform: projectsDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s',
                              flexShrink: 0,
                              opacity: 0.8
                            }}
                          />
                        </>
                      )}
                    </motion.div>
                  </motion.div>

                  <AnimatePresence>
                    {projectsDropdownOpen && sidebarOpen && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: "0.5rem",
                          right: "0.5rem",
                          backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                          border: `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`,
                          borderRadius: "0.75rem",
                          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                          zIndex: 1000,
                          padding: "0.5rem",
                          marginTop: "0.25rem",
                          maxHeight: "300px",
                          overflowY: "auto"
                        }}
                      >
                        <div
                          className={`sidebar-chat-item ${selectedProjectId === null ? "sidebar-chat-item-active" : ""}`}
                          onClick={() => {
                            handleSelectProject(null);
                            setProjectsDropdownOpen(false);
                          }}
                          style={{ padding: "0.5rem", borderRadius: "0.5rem", marginBottom: "0.25rem" }}
                        >
                          <p style={{ fontWeight: 700, fontSize: "0.875rem", margin: 0 }}>All Chats</p>
                        </div>
                        {projects.map((p) => (
                          <div
                            key={p._id}
                            className={`sidebar-chat-item ${selectedProjectId === p._id ? "sidebar-chat-item-active" : ""}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "0.5rem",
                              borderRadius: "0.5rem",
                              marginBottom: "0.25rem"
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                handleSelectProject(p._id);
                                setProjectsDropdownOpen(false);
                              }}
                              style={{
                                flex: 1,
                                cursor: "pointer",
                                overflow: "hidden",
                                background: "transparent",
                                border: "none",
                                textAlign: "left",
                                padding: 0,
                              }}
                            >
                              <p style={{ fontWeight: 700, fontSize: "0.875rem", margin: 0 }}>{p.name}</p>
                            </button>
                            <div 
                              className="chat-menu-container"
                              data-menu-open={projectContextMenu?.project?._id === p._id}
                            >
                              <button
                                type="button"
                                className="project-menu-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setProjectContextMenu({
                                    project: p,
                                    x: rect.right + 4,
                                    y: rect.top,
                                  });
                                }}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: "0.25rem",
                                  display: "flex",
                                  alignItems: "center",
                                  color: "inherit",
                                }}
                              >
                                <MoreHorizontal size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                        <div
                          className="sidebar-item"
                          onClick={() => { setShowNewProjectModal(true); setProjectsDropdownOpen(false); }}
                          style={{ padding: "0.5rem", borderRadius: "0.5rem", cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                          <Plus size={16} />
                          <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>New Project</span>
                        </div>
                        <div
                          className="sidebar-item"
                          onClick={() => { setShowArchivedProjects(true); setProjectsDropdownOpen(false); }}
                          style={{ padding: "0.5rem", borderRadius: "0.5rem", cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                          <Archive size={16} />
                          <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Archived</span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
              {isLoggedIn && sidebarOpen && (
                <motion.div
                  className="sidebar-divider"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{ margin: "1rem 0" }}
                >
                  <p className="sidebar-chats-label">
                    Previous Chats{" "}
                    {selectedProjectId && (
                      <span style={{ fontSize: "1em", fontWeight: "bold" }}>
                        {" "}
                        -{" "}
                        {projects.find((p) => p._id === selectedProjectId)
                          ?.name || "Project"}
                      </span>
                    )}
                  </p>
                </motion.div>
              )}
              {isLoggedIn && sidebarOpen && (
                <div
                  ref={chatListRef}
                  className="sidebar-chats-list"
                  style={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  {loadingChats ? (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <SidebarChatSkeleton key={i} />
                      ))}
                    </>
                  ) : selectedProjectId != null ? (
                    chatsForSelectedProject?.length === 0 ? (
                      <div className="no-chats-message">
                        No chats in this project
                      </div>
                    ) : (
                      (chatsForSelectedProject || []).map((chat) => {
                        const chatIdForState = chat.chatId || chat._id;
                        const chatId16 =
                          chat.chatId16 || chat.chatId || chat._id;
                        const isActive =
                          currentChatId === chat.chatId ||
                          currentChatId === chat._id ||
                          currentChatId === chatIdForState ||
                          currentChatId === chatId16 ||
                          (currentChatId &&
                            String(currentChatId) === String(chat.chatId)) ||
                          (currentChatId &&
                            String(currentChatId) === String(chat._id)) ||
                          (currentChatId &&
                            String(currentChatId) === String(chatId16));
                        const isDeleting =
                          deletingChatId === chat._id ||
                          deletingChatId === chat.chatId ||
                          deletingChatId === chatId16;
                        const isResearchChat =
                          chat.chatType !== "debate" &&
                          !(
                            chat.debateRounds &&
                            Array.isArray(chat.debateRounds) &&
                            chat.debateRounds.length > 0
                          );
                        const isCurrentlyProcessing =
                          isResearchChat &&
                          queryLoading &&
                          (currentChatId === chat._id ||
                            currentChatId === chat.chatId ||
                            currentChatId === chatId16);
                        const isStoppedInBetween =
                          isResearchChat &&
                          !isCurrentlyProcessing &&
                          ((isActive && queryLockedAfterStop) ||
                            isChatStoppedPersisted(chat));
                        return (
                          <motion.div
                            key={chat._id}
                            className={`sidebar-chat-item ${isActive ? "sidebar-chat-item-active" : ""} ${isDeleting ? "sidebar-chat-item-deleting" : ""}`}
                            onClick={() => {
                              if (!isDeleting) {
                                handleChatSelect(chat.chatId || chat._id);
                              }
                            }}
                            whileHover={
                              !isDeleting
                                ? {
                                  backgroundColor: isActive
                                    ? "rgba(37, 99, 235, 0.35)"
                                    : darkMode
                                      ? "rgba(71, 85, 105, 0.6)"
                                      : "rgba(191, 219, 254, 0.7)",
                                  borderColor: isActive
                                    ? "rgba(37, 99, 235, 0.6)"
                                    : darkMode
                                      ? "rgba(148, 163, 184, 0.5)"
                                      : "rgba(147, 197, 253, 0.8)",
                                }
                                : {}
                            }
                            transition={{
                              type: "spring",
                              stiffness: 300,
                              damping: 20,
                            }}
                            style={{
                              backgroundColor: isActive
                                ? "rgba(37, 99, 235, 0.15)"
                                : "rgba(219, 234, 254, 0)",
                              border: isActive
                                ? "1px solid rgba(37, 99, 235, 0.3)"
                                : "1px solid rgba(219, 234, 254, 0)",
                              position: "relative",
                              overflow: "visible",
                              padding: "0.5rem",
                              borderRadius: "0.5rem",
                              cursor: isDeleting ? "wait" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "0.3rem",
                              marginBottom: "0.25rem",
                              opacity: isDeleting ? 0.8 : 1,
                            }}
                          >
                            <div
                              className="chat-text-wrapper"
                              style={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                              }}
                            >
                              {isDeleting ? (
                                <>
                                  <Loader
                                    size={18}
                                    style={{
                                      flexShrink: 0,
                                      animation: "spin 1s linear infinite",
                                    }}
                                  />
                                  <span
                                    style={{
                                      flex: 1,
                                      fontSize: "0.875rem",
                                      color: darkMode ? "#94a3b8" : "#64748b",
                                    }}
                                  >
                                    Deleting…
                                  </span>
                                </>
                              ) : (
                                <>
                                  {isCurrentlyProcessing ? (
                                    <Loader
                                      size={16}
                                      style={{
                                        flexShrink: 0,
                                        animation: "spin 1s linear infinite",
                                        color: darkMode ? "#94a3b8" : "#2563eb",
                                      }}
                                    />
                                  ) : isStoppedInBetween ? (
                                    <XCircle
                                      size={16}
                                      style={{
                                        flexShrink: 0,
                                        color: "#ef4444",
                                      }}
                                    />
                                  ) : (
                                    <Check
                                      size={16}
                                      style={{
                                        flexShrink: 0,
                                        color: "#22c55e",
                                      }}
                                    />
                                  )}
                                  <span
                                    title={
                                      chat.title || chat.query || "Untitled Chat"
                                    }
                                    style={{
                                      flex: 1,
                                      fontSize: "0.875rem",
                                      fontWeight: "bold",
                                      color: isActive
                                        ? "#2563eb"
                                        : darkMode
                                          ? "#f1f5f9"
                                          : "#1e3a8a",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {chat.title || chat.query || "Untitled Chat"}
                                  </span>
                                </>
                              )}
                            </div>
                            <div
                              className="chat-menu-container"
                              data-menu-open={openMenuId === chat._id}
                              style={{
                                position: "relative",
                                marginLeft: "0.125rem",
                                flexShrink: 0,
                              }}
                            >
                              {(() => {
                                const isResearchChat =
                                  chat.chatType !== "debate" &&
                                  !(
                                    chat.debateRounds &&
                                    Array.isArray(chat.debateRounds) &&
                                    chat.debateRounds.length > 0
                                  );
                                const isCurrentlyProcessing =
                                  isResearchChat &&
                                  queryLoading &&
                                  currentChatId === chat._id;
                                const isMenuDisabled = isCurrentlyProcessing;
                                return (
                                  <motion.button
                                    className="chat-menu-button"
                                    data-chat-id={chat._id}
                                    onClick={(e) => {
                                      if (!isMenuDisabled)
                                        handleMenuToggle(chat._id, e);
                                    }}
                                    disabled={isMenuDisabled}
                                    whileHover={
                                      !isMenuDisabled
                                        ? {
                                          scale: 1.1,
                                          backgroundColor: darkMode
                                            ? "#334155"
                                            : "rgba(191, 219, 254, 0.5)",
                                        }
                                        : {}
                                    }
                                    whileTap={
                                      !isMenuDisabled ? { scale: 0.9 } : {}
                                    }
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      cursor: isMenuDisabled
                                        ? "not-allowed"
                                        : "pointer",
                                      padding: "0.125rem",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: isMenuDisabled
                                        ? darkMode
                                          ? "#475569"
                                          : "#9ca3af"
                                        : darkMode
                                          ? "#94a3b8"
                                          : "#1e3a8a",
                                      borderRadius: "4px",
                                      transition: "background-color 0.2s",
                                      opacity: isMenuDisabled ? 0.5 : 1,
                                    }}
                                    title={
                                      isMenuDisabled
                                        ? "Chat is processing, options unavailable"
                                        : "More options"
                                    }
                                  >
                                    <MoreHorizontal size={16} />
                                  </motion.button>
                                );
                              })()}
                            </div>
                          </motion.div>
                        );
                      })
                    )
                  ) : filteredChats.length === 0 ? (
                    <div className="no-chats-message">
                      {searchQuery ? "No chats found" : "No chats yet"}
                    </div>
                  ) : (
                    projectSections.map((section) =>
                      section.chats.length === 0 ? null : (
                        <div key={section.key} style={{ marginBottom: "1rem" }}>
                          {section.label ? (
                            <p
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: darkMode ? "#94a3b8" : "#64748b",
                                marginBottom: "0.5rem",
                                paddingLeft: "0.5rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              {section.label}
                            </p>
                          ) : null}
                          {section.chats.map((chat) => {
                            const chatIdForState = chat.chatId || chat._id; // Keep in sync with loadChat/chat navigation
                            const chatId16 =
                              chat.chatId16 || chat.chatId || chat._id;
                            // Check all possible ID formats to determine if this chat is active
                            const isActive =
                              currentChatId === chat.chatId ||
                              currentChatId === chat._id ||
                              currentChatId === chatIdForState ||
                              currentChatId === chatId16 ||
                              (currentChatId &&
                                String(currentChatId) ===
                                String(chat.chatId)) ||
                              (currentChatId &&
                                String(currentChatId) === String(chat._id)) ||
                              (currentChatId &&
                                String(currentChatId) === String(chatId16));
                            const isDeleting =
                              deletingChatId === chat._id ||
                              deletingChatId === chat.chatId ||
                              deletingChatId === chatId16;
                            const isResearchChat =
                              chat.chatType !== "debate" &&
                              !(
                                chat.debateRounds &&
                                Array.isArray(chat.debateRounds) &&
                                chat.debateRounds.length > 0
                              );
                            const isCurrentlyProcessing =
                              isResearchChat &&
                              queryLoading &&
                              (currentChatId === chat._id ||
                                currentChatId === chat.chatId ||
                                currentChatId === chatId16);
                            const isStoppedInBetween =
                              isResearchChat &&
                              !isCurrentlyProcessing &&
                              ((isActive && queryLockedAfterStop) ||
                                isChatStoppedPersisted(chat));
                            return (
                              <motion.div
                                key={chat._id}
                                className={`sidebar-chat-item ${isActive ? "sidebar-chat-item-active" : ""} ${isDeleting ? "sidebar-chat-item-deleting" : ""}`}
                                onClick={() => {
                                  if (!isDeleting) {
                                    handleChatSelect(chat.chatId || chat._id);
                                  }
                                }}
                                whileHover={
                                  !isDeleting
                                    ? {
                                      backgroundColor: isActive
                                        ? "rgba(37, 99, 235, 0.35)"
                                        : darkMode
                                          ? "rgba(71, 85, 105, 0.6)"
                                          : "rgba(191, 219, 254, 0.7)",
                                    }
                                    : {}
                                }
                                transition={{
                                  type: "spring",
                                  stiffness: 300,
                                  damping: 20,
                                }}
                                style={{
                                  backgroundColor: isActive
                                    ? "rgba(37, 99, 235, 0.15)"
                                    : "rgba(219, 234, 254, 0)",
                                  border: isActive
                                    ? "1px solid rgba(37, 99, 235, 0.3)"
                                    : "1px solid rgba(219, 234, 254, 0)",
                                  position: "relative",
                                  overflow: "visible",
                                  padding: "0.5rem",
                                  borderRadius: "0.5rem",
                                  cursor: isDeleting ? "wait" : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "0.3rem",
                                  marginBottom: "0.25rem",
                                  opacity: isDeleting ? 0.8 : 1,
                                }}
                              >
                                <div
                                  className="chat-text-wrapper"
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.75rem",
                                  }}
                                >
                                  {isDeleting ? (
                                    <>
                                      <Loader
                                        size={18}
                                        style={{
                                          flexShrink: 0,
                                          animation: "spin 1s linear infinite",
                                        }}
                                      />
                                      <span
                                        style={{
                                          flex: 1,
                                          fontSize: "0.875rem",
                                          color: darkMode
                                            ? "#94a3b8"
                                            : "#64748b",
                                        }}
                                      >
                                        Deleting…
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      {isCurrentlyProcessing ? (
                                        <Loader
                                          size={16}
                                          style={{
                                            flexShrink: 0,
                                            animation:
                                              "spin 1s linear infinite",
                                            color: darkMode
                                              ? "#94a3b8"
                                              : "#2563eb",
                                          }}
                                        />
                                      ) : isStoppedInBetween ? (
                                        <XCircle
                                          size={16}
                                          style={{
                                            flexShrink: 0,
                                            color: "#ef4444",
                                          }}
                                        />
                                      ) : (
                                        <Check
                                          size={16}
                                          style={{
                                            flexShrink: 0,
                                            color: "#22c55e",
                                          }}
                                        />
                                      )}
                                      <span
                                        title={
                                          chat.title ||
                                          chat.query ||
                                          "Untitled Chat"
                                        }
                                        style={{
                                          flex: 1,
                                          fontSize: "0.875rem",
                                          fontWeight: "bold",
                                          color: isActive
                                            ? "#2563eb"
                                            : darkMode
                                              ? "#f1f5f9"
                                              : "#1e3a8a",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {chat.title ||
                                          chat.query ||
                                          "Untitled Chat"}
                                      </span>
                                    </>
                                  )}
                                </div>
                                <div
                                  className="chat-menu-container"
                                  data-menu-open={openMenuId === chat._id}
                                  style={{
                                    position: "relative",
                                    marginLeft: "0.125rem",
                                    flexShrink: 0,
                                  }}
                                >
                                  {(() => {
                                    const isResearchChat =
                                      chat.chatType !== "debate" &&
                                      !(
                                        chat.debateRounds &&
                                        Array.isArray(chat.debateRounds) &&
                                        chat.debateRounds.length > 0
                                      );
                                    const isCurrentlyProcessing =
                                      isResearchChat &&
                                      queryLoading &&
                                      currentChatId === chat._id;
                                    const isMenuDisabled =
                                      isCurrentlyProcessing;

                                    return (
                                      <motion.button
                                        className="chat-menu-button"
                                        data-chat-id={chat._id}
                                        onClick={(e) => {
                                          if (!isMenuDisabled) {
                                            handleMenuToggle(chat._id, e);
                                          }
                                        }}
                                        disabled={isMenuDisabled}
                                        whileHover={
                                          !isMenuDisabled
                                            ? {
                                              scale: 1.1,
                                              backgroundColor: darkMode
                                                ? "#334155"
                                                : "rgba(191, 219, 254, 0.5)",
                                            }
                                            : {}
                                        }
                                        whileTap={
                                          !isMenuDisabled ? { scale: 0.9 } : {}
                                        }
                                        style={{
                                          background: "transparent",
                                          border: "none",
                                          cursor: isMenuDisabled
                                            ? "not-allowed"
                                            : "pointer",
                                          padding: "0.125rem",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: isMenuDisabled
                                            ? darkMode
                                              ? "#475569"
                                              : "#9ca3af"
                                            : darkMode
                                              ? "#94a3b8"
                                              : "#1e3a8a",
                                          borderRadius: "4px",
                                          transition: "background-color 0.2s",
                                          opacity: isMenuDisabled ? 0.5 : 1,
                                        }}
                                        title={
                                          isMenuDisabled
                                            ? "Chat is processing, options unavailable"
                                            : "More options"
                                        }
                                      >
                                        <MoreHorizontal size={16} />
                                      </motion.button>
                                    );
                                  })()}
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      ),
                    )
                  )}

                  {/* Progressive loading skeletons for paginated chats */}
                  {loadingMore && chatsHasMore && (
                    <div style={{ padding: "0.25rem 0.5rem 0.5rem 0.5rem" }}>
                      {[...Array(3)].map((_, index) => (
                        <SidebarChatSkeleton key={`chat-load-more-skeleton-${index}`} index={index} />
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!isLoggedIn && sidebarOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{
                    padding: "1.5rem 1rem",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      background: darkMode
                        ? "rgba(59, 130, 246, 0.1)"
                        : "#eff6ff",
                      color: darkMode ? "#60a5fa" : "#3b82f6",
                      padding: "1rem",
                      borderRadius: "50%",
                      marginBottom: "1rem",
                    }}
                  >
                    <MessageSquare size={32} />
                  </div>
                  <h3
                    style={{
                      fontSize: "0.95rem",
                      fontWeight: "600",
                      color: darkMode ? "#f8fafc" : "#0f172a",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Unlock More Power
                  </h3>
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: darkMode ? "#94a3b8" : "#64748b",
                      marginBottom: "1.5rem",
                      lineHeight: "1.5",
                    }}
                  >
                    More AI models. Extended debate rounds. Executive summaries.
                    Saved sessions.
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.75rem",
                      width: "100%",
                    }}
                  >
                    <Link
                      to="/pricing"
                      style={{
                        background: "linear-gradient(135deg, #193CB9 0%, #193CB9 100%)",
                        color: "white",
                        padding: "0.6rem",
                        borderRadius: "0.5rem",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        textDecoration: "none",
                        width: "100%",
                      }}
                    >
                      Start Free Trial
                    </Link>
                  </div>
                </motion.div>
              )}
            </motion.nav>

            <div className="sidebar-footer">
              {/* Usage card — only for logged-in users who have usage data */}
              {sidebarOpen && isLoggedIn && (
                <motion.div
                  className="usage-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <p className="usage-title">AI Actions</p>
                  <p className="usage-text">
                    AI Actions Used: {(aiUsage.aiActionsUsed || 0).toLocaleString()} /{" "}
                    {aiUsage.aiMonthlyLimit >= 1000000
                      ? "Unlimited"
                      : aiUsage.aiMonthlyLimit.toLocaleString()}
                  </p>
                  <div className="usage-progress-bar">
                    <motion.div
                      className="usage-progress-fill"
                      initial={{ width: 0 }}
                      animate={{ width: `${aiProgress}%` }}
                      transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
                    ></motion.div>
                  </div>
                </motion.div>
              )}

              {/* Trial upgrade CTA */}
              {sidebarOpen && isLoggedIn && user?.subscriptionStatus === "trialing" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  style={{
                    margin: "0.5rem 0.75rem 0",
                    padding: "0.75rem",
                    borderRadius: "0.5rem",
                    background: darkMode
                      ? "linear-gradient(135deg, rgba(37,99,235,0.15), rgba(26,61,190,0.1))"
                      : "linear-gradient(135deg, rgba(37,99,235,0.08), rgba(26,61,190,0.04))",
                    border: darkMode
                      ? "1px solid rgba(59,130,246,0.25)"
                      : "1px solid rgba(37,99,235,0.15)",
                  }}
                >
                  <p style={{
                    margin: 0,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: darkMode ? "#93c5fd" : "#1e40af",
                  }}>
                    Trial ends {user?.trialEnd
                      ? `in ${Math.max(0, Math.ceil((new Date(user.trialEnd) - new Date()) / (1000 * 60 * 60 * 24)))} days`
                      : "soon"}
                  </p>
                  <button
                    onClick={() => navigate("/pricing")}
                    style={{
                      marginTop: "0.5rem",
                      width: "100%",
                      padding: "0.4rem 0",
                      borderRadius: "0.375rem",
                      border: "none",
                      background: "#2563eb",
                      color: "#ffffff",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#1d4ed8"}
                    onMouseLeave={(e) => e.target.style.background = "#2563eb"}
                  >
                    Upgrade Now
                  </button>
                </motion.div>
              )}

              {sidebarOpen && isLoggedIn && (
                <div className="account-card">
                  <div
                    className="account-header"
                    onClick={() => setAccountOpen(!accountOpen)}
                  >
                    <div className="account-info">
                      <div className="account-avatar">{renderUserAvatar()}</div>
                      <div>
                        <p className="account-name">{user?.name || "User"}</p>
                        <p className="account-plan">{user?.subscriptionStatus === "trialing" ? "Free Trial" : user?.subscriptionPlan ? `${user.subscriptionPlan.charAt(0).toUpperCase() + user.subscriptionPlan.slice(1)} Plan` : "Essentials Plan"}</p>
                      </div>
                    </div>
                    <motion.span
                      animate={{ rotate: accountOpen ? 90 : 0 }}
                      className="account-arrow"
                    >
                      ▸
                    </motion.span>
                  </div>

                  <AnimatePresence>
                    {accountOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="account-menu"
                      >
                        {[
                          "💡 Request a Feature",
                          "🐞 Report a Bug",
                          "📧 Contact Us",
                        ].map((item) => (
                          <Link
                            to={`mailto:support@lawfusion.ai?subject=${item}`}
                            className="account-menu-item"
                            key={item}
                          >
                            {item}
                          </Link>
                        ))}
                        <button
                          className="account-menu-item logout-btn"
                          onClick={handleLogout}
                        >
                          ↪️ Log Out
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Settings Link */}
              {isLoggedIn && (
                <Link
                  to="/settings"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <motion.div
                    className="theme-toggle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    style={{ cursor: "pointer" }}
                    whileHover={{
                      x: 3,
                      scale: 1.02,
                      transition: { type: "spring", stiffness: 400, damping: 17 },
                    }}
                    whileTap={{ scale: 0.98 }}
                    title={!sidebarOpen ? "Settings" : undefined}
                  >
                    {sidebarOpen && (
                      <motion.span
                        className="theme-label"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        whileHover={{
                          x: 2,
                          color: darkMode ? "#60a5fa" : "#1d4ed8",
                          transition: { duration: 0.2 },
                        }}
                      >
                        Settings
                      </motion.span>
                    )}
                    <motion.div
                      className="theme-toggle-btn"
                      whileHover={{
                        rotate: 90,
                        scale: 1.1,
                        transition: {
                          type: "spring",
                          stiffness: 300,
                          damping: 15,
                        },
                      }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Settings size={18} />
                    </motion.div>
                  </motion.div>
                </Link>
              )}

              <div className="theme-toggle mobile-hide-theme-toggle" title={!sidebarOpen ? (darkMode ? "Light Mode" : "Dark Mode") : undefined}>
                {sidebarOpen && (
                  <span className="theme-label">
                    {darkMode ? "Light Mode" : "Dark Mode"}
                  </span>
                )}
                <button className="theme-toggle-btn" onClick={toggleDarkMode}>
                  {darkMode ? <Sun size={18} /> : <Moon size={18} />}
                </button>
              </div>
            </div>
          </div>
        </motion.aside>

        <motion.div
          ref={mainContentRef}
          className="main-content"
          initial={{
            opacity: 0,
            x: 50,
          }}
          animate={{
            opacity: 1,
            x: 0,
          }}
          transition={{
            opacity: { duration: 0.5, delay: 0.2 },
            x: { duration: 0.5, delay: 0.2 },
          }}
        >
          {loadingChat &&
            typeof document !== "undefined" &&
            createPortal(
              <div
                aria-live="polite"
                aria-busy="true"
                style={{
                  position: "fixed",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.75rem",
                  background: darkMode
                    ? "rgba(15, 23, 42, 0.92)"
                    : "rgba(248, 250, 252, 0.96)",
                  zIndex: 100000,
                }}
              >
                <Loader
                  size={32}
                  style={{
                    flexShrink: 0,
                    animation: "spin 1s linear infinite",
                  }}
                />
                <span
                  style={{
                    fontSize: "0.9375rem",
                    color: darkMode ? "#94a3b8" : "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Loading chat…
                </span>
              </div>,
              document.body,
            )}
          <motion.section
            className={`content-section${composerCentered ? " rw-composer-centered" : ""}`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ position: "relative", minHeight: "200px" }}
          >
            {/* ── Workspace header: query identity + models in play ── */}
            <header className="rw-head">
              <div className="rw-head-main">
                {/* Drawer opener, shown at ≤900px only (research-workspace.css).
                    There the chat sidebar is an off-canvas drawer with no rail
                    on screen, so this is the one way to reach chats on a phone. */}
                <button
                  type="button"
                  className="rw-drawer-toggle"
                  onClick={() => setSidebarOpen(true)}
                  aria-label="Open chats"
                  title="Chats"
                >
                  <PanelLeft size={18} aria-hidden="true" />
                </button>
                <h1 className="dashboard-mode-title">AI Research Mode</h1>
                {researchAnalysesFetchFailed ? (
                  <div className="dashboard-research-stat-error">
                    failed to fetch
                  </div>
                ) : (
                  <div className="dashboard-research-stat">
                    <div className="dashboard-research-stat-badge">
                      <div className="dashboard-research-stat-icon" aria-hidden="true">
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                      <span className="dashboard-research-stat-count">
                        {researchAnalysesCount === null
                          ? "…"
                          : (researchAnalysesCount + 840).toLocaleString("en-US")}
                        {researchAnalysesIsMock && (
                          <span className="dashboard-research-stat-mock">
                            {" "}
                            (mock)
                          </span>
                        )}
                      </span>
                      <div
                        className="dashboard-research-stat-divider"
                        aria-hidden="true"
                      />
                      <span className="dashboard-research-stat-label">
                        Multi-Model Legal Analyses Completed
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <ResearchModelAvatars models={selectedModels} />
            </header>

            {/* ── Stage pipeline: replaces the old below-the-fold tab strip ── */}
            <ResearchStageNav
              activeStage={activeStage}
              onChange={(stage) => {
                setActiveStage(stage);
                if (stage === "comparison" || stage === "final") setActiveTab(stage);
              }}
              hasResults={hasAnyResponse}
              locked={isAnyAILoading || queryLockedAfterStop}
              comparisonReady={!!comparisonResult}
              synthesisReady={!!synthesisResult}
            />

            {/* ── Stage body: owns its own scroll ── */}
            <div className="rw-body ds-scroll-slim">
            {activeStage === "initial" && (
              <>
            {queryLockedAfterStop && (
              <section
                style={{
                  marginTop: "1.5rem",
                  marginBottom: "1.5rem",
                }}
              >
                <div
                  style={{
                    borderRadius: "0.75rem",
                    border: `1px solid ${darkMode ? "#334155" : "#e5e7eb"}`,
                    padding: "1.5rem 1.75rem",
                    background: darkMode ? "#020617" : "#f9fafb",
                    boxShadow: darkMode
                      ? "0 18px 45px rgba(0,0,0,0.65)"
                      : "0 18px 45px rgba(15,23,42,0.12)",
                  }}
                >
                  <div style={{ marginBottom: "1rem" }}>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "0.35rem 0.8rem",
                        borderRadius: "999px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        border: `1px solid ${darkMode ? "#fca5a5" : "#fecaca"}`,
                        backgroundColor: darkMode ? "#451a1a" : "#fee2e2",
                        color: "#ef4444",
                      }}
                    >
                      <span>Query Stopped</span>
                    </div>

                    <h2
                      style={{
                        margin: "0.75rem 0 0.3rem",
                        fontSize: "1.1rem",
                        fontWeight: 700,
                        color: darkMode ? "#f9fafb" : "#0f172a",
                      }}
                    >
                      Query Stopped Incomplete
                    </h2>

                    <p
                      style={{
                        margin: 0,
                        fontSize: "0.95rem",
                        color: darkMode ? "#e5e7eb" : "#111827",
                      }}
                    >
                      The initial query was stopped before completion.
                    </p>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "1.5rem",
                      marginBottom: "1rem",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          display: "block",
                          fontSize: "0.8rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          fontWeight: 600,
                          color: darkMode ? "#94a3b8" : "#6b7280",
                          marginBottom: "0.2rem",
                        }}
                      >
                        Models
                      </span>
                      <span
                        style={{
                          fontSize: "0.9rem",
                          fontWeight: 500,
                          color: darkMode ? "#e5e7eb" : "#1f2933",
                        }}
                      >
                        {Array.isArray(selectedModels) &&
                          selectedModels.length > 0
                          ? selectedModels.join(" · ")
                          : "Selected models"}
                      </span>
                    </div>
                  </div>

                  <div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "0.9rem",
                        color: darkMode ? "#e5e7eb" : "#111827",
                        lineHeight: 1.5,
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>
                        This query cannot be resumed.
                      </span>{" "}
                      To continue analysis,{" "}
                      <span
                        onClick={() => {
                          resetQueryLockAfterStop();
                          createNewChat();
                        }}
                        style={{
                          color: "#2563eb",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        start a new chat
                      </span>
                      .
                    </p>
                  </div>
                </div>
              </section>
            )}
            <ResponsesGrid
              selectedModels={selectedModels}
              responses={responses}
              isLoading={queryLoading}
              disabled={isAnyAILoading}
              getResponseHtml={getResponseHtml}
              getResponsePlain={getResponsePlain}
              onEdit={(model) =>
                handleOpenEditor(
                  "model",
                  `${model} Response`,
                  getResponseHtml(model),
                  { model },
                )
              }
              onRemove={removeModel}
              responseRefs={modelResponseRefs}
              variants={{ item: itemVariants, container: containerVariants }}
            />
              </>
            )}

            {activeStage !== "initial" && (
              <>
              <LazySection
                options={{ threshold: 0.1, rootMargin: "150px" }}
                fallback={
                  <div className="tabs-container">
                    <div className="tabs-list" style={{ marginBottom: "1.5rem" }}>
                      <div
                        style={{
                          width: "200px",
                          height: "3rem",
                          borderRadius: "0.5rem",
                          marginRight: "1rem",
                          display: "inline-block",
                        }}
                      >
                        <SkeletonLoader
                          width="100%"
                          height="100%"
                          style={{ borderRadius: "0.5rem" }}
                        />
                      </div>
                      <div
                        style={{
                          width: "150px",
                          height: "3rem",
                          borderRadius: "0.5rem",
                          display: "inline-block",
                        }}
                      >
                        <SkeletonLoader
                          width="100%"
                          height="100%"
                          style={{ borderRadius: "0.5rem" }}
                        />
                      </div>
                    </div>
                    <TabContentSkeleton />
                  </div>
                }
              >
                <motion.div className="tabs-container" variants={itemVariants}>
                  <div className="tab-container--info">
                    ⚖️{" "}
                    <span className="tab-container--info-title">
                      Attorney Ready Synthesis -
                    </span>{" "}
                    Comparative Analysis and Final Synthesis are designed for
                    litigation planning.
                  </div>
                  <div className="tabs-list" ref={tabsInfoRef}>
                    <motion.button
                      className={`tab-trigger ${activeTab === "comparison" ? "tab-active" : ""}`}
                      onClick={() =>
                        !isAnyAILoading && !queryLockedAfterStop && setActiveTab("comparison")
                      }
                      onMouseEnter={() => setHoverComparison(true)}
                      onMouseLeave={() => setHoverComparison(false)}
                      whileHover={isAnyAILoading || queryLockedAfterStop ? {} : { scale: 1.02 }}
                      whileTap={isAnyAILoading || queryLockedAfterStop ? {} : { scale: 0.98 }}
                      disabled={isAnyAILoading || queryLockedAfterStop}
                      style={{
                        opacity: isAnyAILoading || queryLockedAfterStop ? 0.6 : 1,
                        cursor: isAnyAILoading || queryLockedAfterStop ? "not-allowed" : "pointer",
                      }}
                    >
                      <span>
                        Comparative Analysis
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label="About Comparative Analysis"
                        className="info-icon-wrapper"
                        onMouseEnter={() => setHoverComparison(true)}
                        onMouseLeave={() => setHoverComparison(false)}
                        onClick={toggleTabInfo("comparison")}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            toggleTabInfo("comparison")(event);
                          }
                        }}
                      >
                        <motion.span
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.5 }}
                          style={{ display: "inline-flex" }}
                        >
                          <Info size={14} className="info-icon" />
                        </motion.span>
                      </span>
                      <AnimatePresence>
                        {hoverComparison && (
                          <motion.div
                            initial={{ opacity: 0, y: 5, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 5, scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                            className="tooltip tooltip-tab"
                          >
                            Summarize and compare key points from multiple AI
                            responses.
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.button>
                    <motion.button
                      className={`tab-trigger ${activeTab === "final" ? "tab-active" : ""}`}
                      onClick={() => !isAnyAILoading && !queryLockedAfterStop && setActiveTab("final")}
                      onMouseEnter={() => setHoverSynthesis(true)}
                      onMouseLeave={() => setHoverSynthesis(false)}
                      whileHover={isAnyAILoading || queryLockedAfterStop ? {} : { scale: 1.02 }}
                      whileTap={isAnyAILoading || queryLockedAfterStop ? {} : { scale: 0.98 }}
                      disabled={isAnyAILoading || queryLockedAfterStop}
                      style={{
                        opacity: isAnyAILoading || queryLockedAfterStop ? 0.6 : 1,
                        cursor: isAnyAILoading || queryLockedAfterStop ? "not-allowed" : "pointer",
                      }}
                    >
                      <span>
                        {(!isLoggedIn || isTrial) && (
                          <Lock
                            size={14}
                            style={{ marginRight: "6px", display: "inline" }}
                          />
                        )}
                        Final Synthesis
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label="About Final Synthesis"
                        className="info-icon-wrapper"
                        onMouseEnter={() => setHoverSynthesis(true)}
                        onMouseLeave={() => setHoverSynthesis(false)}
                        onClick={toggleTabInfo("final")}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            toggleTabInfo("final")(event);
                          }
                        }}
                      >
                        <motion.span
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.5 }}
                          style={{ display: "inline-flex" }}
                        >
                          <Info size={14} className="info-icon" />
                        </motion.span>
                      </span>
                      <AnimatePresence>
                        {hoverSynthesis && (
                          <motion.div
                            initial={{ opacity: 0, y: 5, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 5, scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                            className="tooltip tooltip-tab"
                          >
                            Create one cohesive summary based on the best AI
                            responses.
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.button>
                  </div>
                  <p className="tab-description">
                    AI-to-AI strategy comparison & risk analysis IRAC +
                    attorney-ready assessment & recommended actions
                  </p>

                  <AnimatePresence mode="wait">
                    {activeTab === "comparison" && (
                      <motion.div
                        className="tab-content"
                        key="comparison"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <motion.div
                          className="tab-card tab-card-blue"
                          initial={{ scale: 0.95 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="tab-card-content">
                            {/* History Dropdown */}
                            {comparisonHistory.length > 0 && (
                              <div style={{ marginBottom: "1rem" }}>
                                <p
                                  style={{
                                    marginBottom: "0.5rem",
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    color: darkMode ? "#cbd5e1" : "#374151",
                                  }}
                                >
                                  Previous comparative analysis
                                </p>
                                <div style={{ position: "relative" }}>
                                  <button
                                    type="button"
                                    onClick={() => !isAnyAILoading && setComparisonHistoryOpen((prev) => !prev)}
                                    style={{
                                      width: "100%",
                                      padding: "0.5rem 2rem 0.5rem 0.5rem",
                                      borderRadius: "0.5rem",
                                      border: darkMode ? "1px solid #4b5563" : "1px solid #d1d5db",
                                      backgroundColor: darkMode ? "#1f2937" : "#ffffff",
                                      color: darkMode ? "#f3f4f6" : "#111827",
                                      fontSize: "0.875rem",
                                      cursor: isAnyAILoading ? "not-allowed" : "pointer",
                                      opacity: isAnyAILoading ? 0.6 : 1,
                                      textAlign: "left",
                                      position: "relative",
                                    }}
                                  >
                                    {selectedComparisonHistoryIndex !== null
                                      ? (() => {
                                        const entry = comparisonHistory[selectedComparisonHistoryIndex];
                                        if (!entry) return "Select previous analysis";
                                        return entry.type === "executive_summary"
                                          ? `Executive Summary (${getHistoryDateStr(entry.timestamp)})`
                                          : `${entry.model || "Analysis"} — ${getHistoryDateStr(entry.timestamp)}`;
                                      })()
                                      : "Select previous analysis"}
                                    <ChevronDown size={14} style={{ position: "absolute", right: "0.5rem", top: "50%", transform: "translateY(-50%)" }} />
                                  </button>
                                  {comparisonHistoryOpen && (
                                    <>
                                      <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setComparisonHistoryOpen(false)} />
                                      <div style={{
                                        position: "absolute",
                                        top: "calc(100% + 4px)",
                                        left: 0,
                                        right: 0,
                                        zIndex: 100,
                                        maxHeight: "240px",
                                        overflowY: "auto",
                                        borderRadius: "0.5rem",
                                        border: darkMode ? "1px solid #4b5563" : "1px solid #d1d5db",
                                        backgroundColor: darkMode ? "#1f2937" : "#ffffff",
                                        boxShadow: darkMode ? "0 8px 24px rgba(0,0,0,0.5)" : "0 8px 24px rgba(0,0,0,0.12)",
                                      }}>
                                        {(() => {
                                          const runs = comparisonHistory
                                            .map((e, i) => ({ ...e, _idx: i }))
                                            .filter((e) => e.type !== "executive_summary");
                                          return runs.map((run) => {
                                            const execSummary = comparisonHistory.find(
                                              (e) => e.type === "executive_summary" && e.sourceModel === run.model,
                                            );
                                            const execIdx = execSummary ? comparisonHistory.indexOf(execSummary) : -1;
                                            const selectItem = (index) => {
                                              const selectedHistory = comparisonHistory[index];
                                              if (!selectedHistory) return;
                                              if (selectedHistory.type === "executive_summary") {
                                                setComparisonResult(removeCitationMarkers(selectedHistory.result || ""));
                                              } else {
                                                setComparisonQuery(selectedHistory.query || DEFAULT_COMPARISON_QUERY);
                                                setComparisonResult(removeCitationMarkers(selectedHistory.result || ""));
                                              }
                                              const historyModel = selectedHistory.type === "executive_summary"
                                                ? selectedHistory.sourceModel || selectedHistory.model
                                                : selectedHistory.model;
                                              if (historyModel) setComparisonModel(historyModel);
                                              setComparisonGenerated(false);
                                              setSelectedComparisonHistoryIndex(index);
                                              setComparisonHistoryOpen(false);
                                            };
                                            return (
                                              <div key={run._idx}>
                                                <div style={{
                                                  padding: "0.4rem 0.6rem",
                                                  fontSize: "0.75rem",
                                                  fontWeight: 600,
                                                  color: darkMode ? "#93c5fd" : "#2563eb",
                                                  backgroundColor: darkMode ? "#111827" : "#f1f5f9",
                                                  borderBottom: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",
                                                }}>
                                                  {formatHistoryTimestamp(run.timestamp, run.model)}
                                                </div>
                                                <div
                                                  onClick={() => selectItem(run._idx)}
                                                  style={{
                                                    padding: "0.45rem 0.6rem 0.45rem 1.2rem",
                                                    fontSize: "0.85rem",
                                                    cursor: "pointer",
                                                    color: darkMode ? "#e5e7eb" : "#1f2937",
                                                    backgroundColor: selectedComparisonHistoryIndex === run._idx
                                                      ? (darkMode ? "#1e3a5f" : "#eff6ff")
                                                      : "transparent",
                                                  }}
                                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = darkMode ? "#374151" : "#f3f4f6"; }}
                                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = selectedComparisonHistoryIndex === run._idx ? (darkMode ? "#1e3a5f" : "#eff6ff") : "transparent"; }}
                                                >
                                                  Full Comparative Analysis
                                                </div>
                                                {execIdx >= 0 && (
                                                  <div
                                                    onClick={() => selectItem(execIdx)}
                                                    style={{
                                                      padding: "0.45rem 0.6rem 0.45rem 1.2rem",
                                                      fontSize: "0.85rem",
                                                      cursor: "pointer",
                                                      color: darkMode ? "#cbd5e1" : "#4b5563",
                                                      backgroundColor: selectedComparisonHistoryIndex === execIdx
                                                        ? (darkMode ? "#1e3a5f" : "#eff6ff")
                                                        : "transparent",
                                                      borderBottom: darkMode ? "1px solid #374151" : "1px solid #f3f4f6",
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = darkMode ? "#374151" : "#f3f4f6"; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = selectedComparisonHistoryIndex === execIdx ? (darkMode ? "#1e3a5f" : "#eff6ff") : "transparent"; }}
                                                  >
                                                    Executive Summary ({getHistoryDateStr(execSummary.timestamp)})
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          });
                                        })()}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "1rem",
                              }}
                            >
                              {/* Query Field */}
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "0.5rem",
                                }}
                              >
                                <label className="rs-stage-label">
                                  Comparative Analysis Prompt
                                </label>
                                <div className="tab-input-container">
                                  {/* File input (hidden) */}
                                  <input
                                    type="file"
                                    ref={comparisonFileInputRef}
                                    style={{ display: "none" }}
                                    accept=".pdf,.docx,.doc,.txt"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        handleFileUpload(file, "comparisonFile");
                                      }
                                      e.target.value = ""; // Reset input
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: "relative",
                                      flex: "1 1 auto",
                                      width: "100%",
                                      minWidth: 0,
                                    }}
                                  >
                                    <motion.textarea
                                      ref={setComparisonQueryTextareaRef}
                                      placeholder="Analyze the AI responses and provide a tactical comparative analysis for litigation planning. Identify where the models agree and disagree on legal strategy, risk assessment, and recommended actions. Rank critical vulnerabilities by litigation impact and recommend which position is strongest based on legal principles and factual support. Be direct and actionable—focus on what needs to be strengthened, corrected, or mitigated. Identify which AI provided the most useful analysis for this specific legal issue."
                                      className="tab-textarea tab-textarea-blue"
                                      value={comparisonQuery}
                                      onChange={(e) => {
                                        setComparisonQuery(e.target.value);
                                      }}
                                      disabled={isAnyAILoading}
                                      style={{
                                        opacity: isAnyAILoading ? 0.7 : 1,
                                        cursor: isAnyAILoading
                                          ? "not-allowed"
                                          : "text",
                                      }}
                                    />
                                  </div>
                                  <motion.button
                                    className="attach-btn attach-btn-blue"
                                    onClick={() =>
                                      comparisonFileInputRef.current?.click()
                                    }
                                    whileHover={{ scale: 1.1, rotate: 15 }}
                                    whileTap={{ scale: 0.9 }}
                                    disabled={loadingChats || isAnyAILoading}
                                    title="Attach file (PDF, DOCX, TXT)"
                                  >
                                    <Paperclip size={18} />
                                  </motion.button>
                                </div>
                              </div>
                              {/* Uploaded file display - below input fields */}
                              {fileUploading.comparisonFile ? (
                                <motion.div
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className="uploaded-file-display"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    padding: "0.4rem 0.6rem",
                                    background: "#dbeafe",
                                    borderRadius: "0.5rem",
                                    fontSize: "0.8rem",
                                    width: "fit-content",
                                    maxWidth: "100%",
                                  }}
                                >
                                  <Loader
                                    size={14}
                                    className="spinning"
                                    color="#2563eb"
                                  />
                                  <span
                                    className="upload-status-text"
                                    style={{ color: "#2563eb" }}
                                  >
                                    Uploading file...
                                  </span>
                                </motion.div>
                              ) : (
                                getCurrentChatFiles().comparisonFile &&
                                (() => {
                                  const comparisonFile =
                                    getCurrentChatFiles().comparisonFile;
                                  return (
                                    <motion.div
                                      initial={{ opacity: 0, y: -10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      className="uploaded-file-display"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.5rem",
                                        padding: "0.4rem 0.6rem",
                                        background: "#dbeafe",
                                        borderRadius: "0.5rem",
                                        fontSize: "0.8rem",
                                        width: "fit-content",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      <FileText size={14} color="#2563eb" />
                                      <a
                                        href={
                                          comparisonFile.fileUrl ||
                                          comparisonFile.s3Url
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          color: "#2563eb",
                                          textDecoration: "none",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: "300px",
                                        }}
                                        title={comparisonFile.fileName}
                                      >
                                        {comparisonFile.fileName}
                                      </a>
                                      <button
                                        onClick={() =>
                                          !isAnyAILoading &&
                                          handleFileRemove("comparisonFile")
                                        }
                                        disabled={isAnyAILoading}
                                        style={{
                                          background: "transparent",
                                          border: "none",
                                          cursor: isAnyAILoading
                                            ? "not-allowed"
                                            : "pointer",
                                          padding: "0.2rem",
                                          display: "flex",
                                          alignItems: "center",
                                          flexShrink: 0,
                                          opacity: isAnyAILoading ? 0.6 : 1,
                                        }}
                                      >
                                        <X size={14} color="#dc2626" />
                                      </button>
                                    </motion.div>
                                  );
                                })()
                              )}
                              <p className="tab-tip">
                                💡 Tip: Feel free to edit or use your own prompt.
                                The response will appear in the Response field
                                below.
                              </p>
                              {/* Response Field */}
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "0.5rem",
                                }}
                              >
                                <label className="rs-stage-label">
                                  Response
                                </label>
                                {executiveSummaryLoading && (
                                  <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    style={{
                                      marginBottom: "0.5rem",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                      color: darkMode ? "#93c5fd" : "#2563eb",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    <Loader size={16} className="spinning" />
                                    <span>Generating Executive Summary</span>
                                  </motion.div>
                                )}
                                <div
                                  style={{ position: "relative", width: "100%" }}
                                >
                                  {comparisonResult.trim() === "" &&
                                    !executiveSummaryLoading ? (
                                    <div
                                      className="tab-textarea tab-textarea-blue"
                                      style={{
                                        minHeight: "200px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: darkMode ? "#64748b" : "#94a3b8",
                                        fontStyle: "italic",
                                        opacity: comparisonLoading ? 1 : 0.7,
                                      }}
                                    >
                                      Comparative analysis response will appear
                                      here...
                                    </div>
                                  ) : executiveSummaryLoading &&
                                    comparisonResult.trim() === "" ? (
                                    <div
                                      className="tab-textarea tab-textarea-blue"
                                      style={{
                                        minHeight: "200px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "0.5rem",
                                        color: darkMode ? "#93c5fd" : "#2563eb",
                                        fontSize: "0.875rem",
                                      }}
                                    >
                                      <Loader size={20} className="spinning" />
                                      <span>Generating Executive Summary</span>
                                    </div>
                                  ) : comparisonResult.trim() !== "" ? (
                                    <SectionedHtml
                                      className="tab-textarea tab-textarea-blue response-html"
                                      style={{
                                        minHeight: "200px",
                                        maxHeight: "600px",
                                        overflow: "auto",
                                        cursor: "default",
                                        padding: "0.75rem",
                                      }}
                                      ref={comparisonResponseRef}
                                      isLoading={comparisonLoading}
                                      html={convertTextToHtml(comparisonResult)}
                                    />
                                  ) : null}
                                </div>
                                {/* Processing indicator - exactly below response field */}
                                {comparisonLoading && (
                                  <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    style={{
                                      marginTop: "0.5rem",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                      color: darkMode ? "#93c5fd" : "#2563eb",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    <Loader size={16} className="spinning" />
                                    <span>
                                      Processing... please allow up to one minute
                                    </span>
                                  </motion.div>
                                )}
                              </div>
                            </div>

                            {/* Disclaimer removed from here - moved inside text field */}
                            <div className="tab-edit-actions">
                              <div
                                className="comparison-edit-export-tooltip-wrap"
                                ref={comparisonEditExportWrapRef}
                                onMouseEnter={() => {
                                  if (isMobileScreen) return;
                                  setShowComparisonEditTooltipDesktop(true);
                                }}
                                onMouseLeave={() => {
                                  if (isMobileScreen) return;
                                  setShowComparisonEditTooltipDesktop(false);
                                }}
                                onPointerDownCapture={() => {
                                  if (!isMobileScreen) return;
                                  const isMobileAnonEditExportLocked =
                                    !isLoggedIn && isMobileScreen;
                                  const isComparisonEditExportDisabled =
                                    isMobileAnonEditExportLocked ||
                                    comparisonLoading ||
                                    executiveSummaryLoading ||
                                    !comparisonResult?.trim();
                                  if (!isComparisonEditExportDisabled) return;

                                  setShowComparisonEditTooltipMobile(true);
                                }}
                              >
                                {(() => {
                                  const isMobileAnonEditExportLocked =
                                    !isLoggedIn && isMobileScreen;
                                  const isComparisonEditExportDisabled =
                                    isMobileAnonEditExportLocked ||
                                    comparisonLoading ||
                                    executiveSummaryLoading ||
                                    !comparisonResult?.trim();
                                  const shouldRenderComparisonEditTooltip =
                                    (isMobileAnonEditExportLocked ||
                                      !comparisonResult?.trim()) &&
                                    !executiveSummaryLoading &&
                                    !isAnyAILoading;
                                  const shouldShowComparisonEditTooltip =
                                    isMobileScreen
                                      ? showComparisonEditTooltipMobile
                                      : showComparisonEditTooltipDesktop;

                                  return (
                                    <>
                                      <motion.button
                                        className="response-btn tab-edit-btn"
                                        whileHover={
                                          isComparisonEditExportDisabled
                                            ? {}
                                            : { scale: 1.02 }
                                        }
                                        whileTap={
                                          isComparisonEditExportDisabled
                                            ? {}
                                            : { scale: 0.97 }
                                        }
                                        onClick={() => {
                                          if (isMobileAnonEditExportLocked) return;
                                          const selectedEntry =
                                            selectedComparisonHistoryIndex !== null
                                              ? comparisonHistory[
                                              selectedComparisonHistoryIndex
                                              ]
                                              : null;
                                          const isExecutiveSummary =
                                            selectedEntry?.type ===
                                            "executive_summary";
                                          const title = isExecutiveSummary
                                            ? `Executive Summary (${selectedEntry?.sourceModel ?? comparisonHistory[0]?.sourceModel ?? "Unknown"}) – ${getHistoryDateStr(selectedEntry?.timestamp ?? new Date().toISOString())}`
                                            : `Comparative Analysis ${selectedEntry?.model ||
                                            comparisonHistory[0]?.model ||
                                            comparisonModel}`;
                                          const editorType = isExecutiveSummary
                                            ? "executive_summary"
                                            : "comparison";
                                          handleOpenEditor(
                                            editorType,
                                            title,
                                            comparisonResult,
                                          );
                                        }}
                                        disabled={isComparisonEditExportDisabled}
                                        style={{
                                          opacity:
                                            isComparisonEditExportDisabled
                                              ? 0.6
                                              : 1,
                                          cursor:
                                            isComparisonEditExportDisabled
                                              ? "not-allowed"
                                              : "pointer",
                                        }}
                                      >
                                        Edit / Export
                                      </motion.button>

                                      {shouldRenderComparisonEditTooltip && (
                                        isMobileScreen ? (
                                          <div
                                            className="locked-tooltip locked-tooltip-muted"
                                            data-align="left-mobile"
                                            style={{
                                              display: shouldShowComparisonEditTooltip
                                                ? "block"
                                                : "none",
                                              opacity: shouldShowComparisonEditTooltip ? 1 : 0,
                                              visibility: shouldShowComparisonEditTooltip ? "visible" : "hidden",
                                            }}
                                          >
                                            {isMobileAnonEditExportLocked
                                              ? "Start your Free Trial to unlock Edit / Export"
                                              : "Run Comparative Analysis first"}
                                          </div>
                                        ) : (
                                          <div
                                            className="comparison-edit-export-tooltip-fresh"
                                            style={{
                                              display: shouldShowComparisonEditTooltip
                                                ? "block"
                                                : "none",
                                              position: "absolute",
                                              // Above the button: the stage
                                              // body clips below its last row.
                                              top: "auto",
                                              bottom: "calc(100% + 10px)",
                                              left: "auto",
                                              right: 0,
                                              transform: "none",
                                              background: "#193CB9",
                                              border: "1px solid #2563eb",
                                              color: "#ffffff",
                                              padding: "4px 6px",
                                              borderRadius: "5px",
                                              fontSize: "0.80rem",
                                              lineHeight: 1.15,
                                              whiteSpace: "normal",
                                              width: "max-content",
                                              maxWidth: "min(138px, calc(100vw - 32px))",
                                              textAlign: "left",
                                              zIndex: 1600,
                                              boxShadow: "0 5px 12px rgba(25, 60, 185, 0.35)",
                                              pointerEvents: "none",
                                            }}
                                          >
                                            <span
                                              aria-hidden="true"
                                              style={{
                                                position: "absolute",
                                                top: "auto",
                                                bottom: "100%",
                                                right: "10px",
                                                left: "auto",
                                                transform: "none",
                                                width: 0,
                                                height: 0,
                                                borderLeft: "4.5px solid transparent",
                                                borderRight: "4.5px solid transparent",
                                                borderTop: "none",
                                                borderBottom: "4.5px solid #193CB9",
                                              }}
                                            />
                                            {isMobileAnonEditExportLocked ? (
                                              <>
                                                Start your Free
                                                <br />
                                                Trial to unlock
                                                <br />
                                                Edit / Export
                                              </>
                                            ) : (
                                              <>
                                                Run Comparative Analysis
                                                <br />
                                                first
                                              </>
                                            )}
                                          </div>
                                        )
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                              <div
                                className="comparison-exec-summary-tooltip-wrap"
                                ref={comparisonExecSummaryWrapRef}
                                onPointerDownCapture={() => {
                                  if (!isMobileScreen) return;
                                  const shouldRender =
                                    !isLoggedIn ||
                                    (!comparisonResult?.trim() &&
                                      !executiveSummaryLoading &&
                                      !isAnyAILoading) ||
                                    aiActionsExhausted ||
                                    comparisonHasExecutiveSummary;

                                  if (!shouldRender) return;

                                  setShowComparisonExecSummaryTooltipMobile(true);
                                }}
                              >
                                <motion.button
                                  className="response-btn tab-edit-btn"
                                  whileHover={
                                    !isLoggedIn ||
                                      isAnyAILoading ||
                                      loadingChats ||
                                      executiveSummaryLoading ||
                                      !comparisonResult?.trim() ||
                                      comparisonHasExecutiveSummary
                                      ? {}
                                      : { scale: 1.02 }
                                  }
                                  whileTap={
                                    !isLoggedIn ||
                                      isAnyAILoading ||
                                      loadingChats ||
                                      executiveSummaryLoading ||
                                      !comparisonResult?.trim() ||
                                      comparisonHasExecutiveSummary
                                      ? {}
                                      : { scale: 0.97 }
                                  }
                                  onClick={handleCreateExecutiveSummary}
                                  disabled={
                                    !isLoggedIn ||
                                    isAnyAILoading ||
                                    loadingChats ||
                                    executiveSummaryLoading ||
                                    !comparisonResult?.trim() ||
                                    comparisonHasExecutiveSummary
                                  }
                                  style={{
                                    backgroundColor: "#ffffff",
                                    opacity:
                                      !isLoggedIn ||
                                        isAnyAILoading ||
                                        loadingChats ||
                                        executiveSummaryLoading ||
                                        !comparisonResult?.trim() ||
                                        comparisonHasExecutiveSummary
                                        ? 0.6
                                        : 1,
                                    cursor:
                                      !isLoggedIn ||
                                        isAnyAILoading ||
                                        loadingChats ||
                                        executiveSummaryLoading ||
                                        !comparisonResult?.trim() ||
                                        comparisonHasExecutiveSummary
                                        ? "not-allowed"
                                        : "pointer",
                                  }}
                                >
                                  {!isLoggedIn && (
                                    <Lock
                                      size={12}
                                      style={{
                                        marginRight: "6px",
                                        verticalAlign: "middle",
                                      }}
                                    />
                                  )}
                                  {executiveSummaryLoading ? (
                                    <>
                                      <Loader
                                        size={14}
                                        style={{
                                          marginRight: "6px",
                                          verticalAlign: "middle",
                                        }}
                                      />
                                      Generating Executive Summary
                                    </>
                                  ) : (
                                    "Create Executive Summary"
                                  )}
                                </motion.button>
                                {(!isLoggedIn ||
                                  aiActionsExhausted ||
                                  (!comparisonResult?.trim() &&
                                    !executiveSummaryLoading &&
                                    !isAnyAILoading) ||
                                  comparisonHasExecutiveSummary) && (
                                    <div
                                      className="locked-tooltip locked-tooltip-muted"
                                      data-align="center"
                                      style={{
                                        display: isMobileScreen
                                          ? showComparisonExecSummaryTooltipMobile
                                            ? "block"
                                            : "none"
                                          : undefined,
                                        opacity: isMobileScreen
                                          ? showComparisonExecSummaryTooltipMobile
                                            ? 1
                                            : 0
                                          : undefined,
                                        visibility: isMobileScreen
                                          ? showComparisonExecSummaryTooltipMobile
                                            ? "visible"
                                            : "hidden"
                                          : undefined,
                                      }}
                                    >
                                      {!isLoggedIn
                                        ? "Start your Free Trial to unlock Executive Summary"
                                        : aiActionsExhausted
                                          ? AI_ACTIONS_EXHAUSTED_MESSAGE
                                          : (!comparisonResult?.trim() && !executiveSummaryLoading)
                                            ? "Run Comparative Analysis first"
                                            : "Executive Summary already generated for this result"}
                                    </div>
                                  )}
                              </div>
                            </div>
                            <div className="tab-model-selection">
                              <div className="tab-model-selection--header">
                                <p className="tab-model-label">
                                  {!isLoggedIn && isPhoneScreen
                                    ? "Free: compare 2 of 5 models"
                                    : (
                                      <>
                                        Select which AI should perform the Comparative
                                        Analysis <br />
                                      </>
                                    )}
                                </p>
                                <p className="tab-model-selection--header--recommendation">
                                  Grok or Claude recommended
                                </p>
                              </div>
                              <div className={`tab-model-buttons ${!isLoggedIn ? "tab-model-buttons-anon-compact" : ""} ${!isLoggedIn && isPhoneScreen ? "tab-model-buttons-anon-mobile" : ""}`}>
                                {(() => {
                                  const comparisonModelOptions = [
                                    "ChatGPT",
                                    "Claude",
                                    "Gemini",
                                    "Perplexity",
                                    "Grok",
                                  ];

                                  const renderModelButton = (model) => {
                                    const completed =
                                      comparisonModelsCompleted.has(model);
                                    // Trial users: disable buttons after first run (no lock, no tooltip)
                                    const trialDisabled =
                                      isTrial &&
                                      comparisonModelsCompleted.size > 0 &&
                                      !completed;
                                    // Anon users can only use Claude (show lock + tooltip)
                                    const anonLocked =
                                      !isLoggedIn &&
                                      !availableComparisonModels.includes(model);
                                    const isDisabledBtn = completed || trialDisabled;
                                    const isLocked = anonLocked;

                                    return (
                                      <motion.button
                                        key={model}
                                        className={`tab-model-btn ${comparisonModel === model ? "tab-model-btn-active" : "tab-model-btn-outline"}${completed ? " tab-model-btn-completed" : ""}${isLocked ? " model-btn-locked" : ""}`}
                                        onClick={() => {
                                          if (isDisabledBtn) return;
                                          setComparisonModel(model);
                                          setComparisonQuery(
                                            DEFAULT_COMPARISON_QUERY,
                                          );
                                          setComparisonResult("");
                                          setComparisonGenerated(false);
                                          setSelectedComparisonHistoryIndex(null);
                                          if (shouldAutoFocusQueryInput()) {
                                            setTimeout(() => {
                                              comparisonQueryTextareaRef.current?.focus();
                                            }, 0);
                                          }
                                        }}
                                        whileHover={
                                          isDisabledBtn
                                            ? {}
                                            : { scale: 1.05, y: -2 }
                                        }
                                        whileTap={
                                          isDisabledBtn
                                            ? {}
                                            : { scale: 0.95 }
                                        }
                                        transition={{
                                          type: "spring",
                                          stiffness: 400,
                                          damping: 17,
                                        }}
                                        disabled={
                                          loadingChats ||
                                          isAnyAILoading ||
                                          isDisabledBtn
                                        }
                                        style={{
                                          cursor: isDisabledBtn || isAnyAILoading
                                            ? "not-allowed"
                                            : "pointer",
                                          filter: isDisabledBtn || isAnyAILoading
                                            ? "grayscale(0.2) brightness(0.75)"
                                            : "none",
                                          position: "relative",
                                        }}
                                        title={
                                          completed
                                            ? "Already completed for this query"
                                            : ""
                                        }
                                      >
                                        {anonLocked && (
                                          <Lock
                                            size={14}
                                            className="lock-icon"
                                            style={{
                                              display: "inline",
                                              marginRight: "4px",
                                              verticalAlign: "text-bottom",
                                            }}
                                          />
                                        )}
                                        {model}

                                        {anonLocked && (
                                          <div
                                            className="locked-tooltip locked-tooltip-muted comparison-anon-locked-tooltip"
                                            style={{ maxWidth: "160px" }}
                                            data-align={((model === "Grok" || model === "Claude" || model === "Gemini") && !(model === "Gemini" && !isLoggedIn)) ? "center" : ([0, 1, 2].includes([
                                              "ChatGPT",
                                              "Claude",
                                              "Gemini",
                                              "Perplexity",
                                              "Grok",
                                            ].indexOf(model)) ? "left" : "right")}
                                          >
                                            <Lock
                                              size={12}
                                              style={{
                                                display: "inline",
                                                marginRight: "4px",
                                                verticalAlign: "text-bottom",
                                              }}
                                            />
                                            Available on
                                            <br />
                                            <a href="/pricing" style={{ color: "#ffffff", textDecoration: "underline", fontWeight: 600 }}>Free Trial</a>
                                            <br />
                                            (up to
                                            <br />
                                            4 AI
                                            <br />
                                            models)
                                          </div>
                                        )}
                                      </motion.button>
                                    );
                                  };

                                  if (!isLoggedIn && isPhoneScreen) {
                                    return (
                                      <>
                                        <div className="tab-model-buttons-row">
                                          {comparisonModelOptions
                                            .slice(0, 3)
                                            .map(renderModelButton)}
                                        </div>
                                        <div className="tab-model-buttons-row tab-model-buttons-row-centered">
                                          {comparisonModelOptions
                                            .slice(3)
                                            .map(renderModelButton)}
                                        </div>
                                      </>
                                    );
                                  }

                                  return comparisonModelOptions.map(
                                    renderModelButton,
                                  );
                                })()}
                              </div>
                            </div>
                            <motion.button
                              className="tab-action-btn tab-action-btn-blue"
                              ref={comparisonRunBtnRef}
                              onClick={handleRunComparison}
                              onPointerDownCapture={() => {
                                if (!isMobileScreen) return;

                                const tooltipText =
                                  isAnyAILoading
                                    ? undefined
                                    : !availableComparisonModels.includes(comparisonModel)
                                      ? `Upgrade to unlock ${comparisonModel}`
                                      : (isTrial && comparisonModelsCompleted.size > 0)
                                        ? "Comparative Analysis can only be run once on the free trial"
                                        : comparisonModelsCompleted.has(comparisonModel)
                                          ? "Already completed for this query"
                                          : Object.entries(responses).filter(
                                            ([m, data]) =>
                                              selectedModels.includes(m) &&
                                              getResponsePlain(data).trim() !== "",
                                          ).length < 2
                                            ? "You need at least 2 AI models for comparison."
                                            : queryLoading
                                              ? "Please wait for the initial query to complete."
                                              : !query || query.trim() === ""
                                                ? "Please run an initial query before comparing responses."
                                                : undefined;

                                const isDisabled = !!(
                                  isAnyAILoading ||
                                  comparisonModelsCompleted.has(comparisonModel) ||
                                  (isTrial && comparisonModelsCompleted.size > 0) ||
                                  Object.entries(responses)
                                    .filter(
                                      ([m, data]) =>
                                        selectedModels.includes(m) &&
                                        getResponsePlain(data).trim() !== "",
                                    )
                                    .length < 2 ||
                                  queryLoading ||
                                  !query ||
                                  query.trim() === ""
                                );

                                if (!isDisabled || !tooltipText) return;

                                setShowComparisonRunTooltipMobile(true);
                              }}
                              disabled={
                                isAnyAILoading ||
                                comparisonModelsCompleted.has(comparisonModel) ||
                                (isTrial && comparisonModelsCompleted.size > 0) ||
                                Object.entries(responses)
                                  .filter(
                                    ([m, data]) =>
                                      selectedModels.includes(m) &&
                                      getResponsePlain(data).trim() !== "",
                                  )
                                  .length < 2 ||
                                queryLoading ||
                                !query ||
                                query.trim() === ""
                              }
                              data-tooltip={
                                isAnyAILoading
                                  ? undefined
                                  : !availableComparisonModels.includes(comparisonModel)
                                    ? `Upgrade to unlock ${comparisonModel}`
                                    : (isTrial && comparisonModelsCompleted.size > 0)
                                      ? "Comparative Analysis can only be run once on the free trial"
                                      : comparisonModelsCompleted.has(comparisonModel)
                                        ? "Already completed for this query"
                                        : Object.entries(responses).filter(
                                          ([m, data]) =>
                                            selectedModels.includes(m) &&
                                            getResponsePlain(data).trim() !== "",
                                        ).length < 2
                                          ? "You need at least 2 AI models for comparison."
                                          : queryLoading
                                            ? "Please wait for the initial query to complete."
                                            : !query || query.trim() === ""
                                              ? "Please run an initial query before comparing responses."
                                              : undefined
                              }
                              data-show-tooltip={
                                isMobileScreen && showComparisonRunTooltipMobile
                                  ? "true"
                                  : undefined
                              }
                              whileHover={{
                                scale:
                                  isAnyAILoading ||
                                    comparisonModelsCompleted.has(
                                      comparisonModel,
                                    ) ||
                                    (isTrial && comparisonModelsCompleted.size > 0) ||
                                    Object.values(responses).filter(
                                      (r) =>
                                        (typeof r === "string" &&
                                          r.trim() !== "") ||
                                        (r?.text && r.text.trim() !== ""),
                                    ).length < 2
                                    ? 1
                                    : 1.02,
                                boxShadow:
                                  isAnyAILoading ||
                                    comparisonModelsCompleted.has(
                                      comparisonModel,
                                    ) ||
                                    (isTrial && comparisonModelsCompleted.size > 0) ||
                                    Object.values(responses).filter(
                                      (r) =>
                                        (typeof r === "string" &&
                                          r.trim() !== "") ||
                                        (r?.text && r.text.trim() !== ""),
                                    ).length < 2
                                    ? "none"
                                    : "0 10px 25px rgba(37, 99, 235, 0.3)",
                              }}
                              whileTap={{ scale: 0.98 }}
                              style={{
                                cursor:
                                  isAnyAILoading ||
                                    comparisonModelsCompleted.has(
                                      comparisonModel,
                                    ) ||
                                    (isTrial && comparisonModelsCompleted.size > 0) ||
                                    Object.values(responses).filter(
                                      (r) =>
                                        (typeof r === "string" &&
                                          r.trim() !== "") ||
                                        (r?.text && r.text.trim() !== ""),
                                    ).length < 2
                                    ? "not-allowed"
                                    : "pointer",
                              }}
                            >
                              {!availableComparisonModels.includes(comparisonModel) ? (
                                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                                  <Lock size={16} />
                                  Upgrade to unlock {comparisonModel}
                                </span>
                              ) : (
                                `Run Comparative Analysis with ${comparisonModel}`
                              )}
                            </motion.button>

                            {(comparisonResult.trim() ||
                              comparisonHistory.length > 0) && (
                                <>
                                  {isTrial || !isLoggedIn ? (
                                    <div
                                      className="model-btn-locked"
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        marginTop: "1rem",
                                      }}
                                    >
                                        <button
                                          type="button"
                                          className="tab-model-btn"
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            padding: "8px 16px",
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                            marginTop: "1rem",
                                            background: "#ffffff",
                                            color: "#2563eb",
                                            border: "1px solid #2563eb",
                                            width: isPhoneScreen ? "100%" : "auto",
                                            justifyContent: "center",
                                          }}
                                          onClick={() => {
                                            navigate("/pricing");
                                          }}
                                        >
                                          <Lock size={14} color="#2563eb" />
                                          <span style={{ 
                                            fontSize: isPhoneScreen ? "12px" : "14px", 
                                            fontWeight: 600, 
                                            display: "flex", 
                                            flexDirection: isPhoneScreen ? "column" : "row",
                                            alignItems: "center", 
                                            gap: isPhoneScreen ? "2px" : "8px",
                                            color: "#3b82f6"
                                          }}>
                                            Re-run Comparative Analysis with another AI Model
                                            <span style={{ 
                                              textDecoration: "underline", 
                                              marginLeft: isPhoneScreen ? "0" : "8px", 
                                              fontSize: "11px",
                                              color: "#3b82f6" 
                                            }}>Upgrade to unlock</span>
                                          </span>
                                        </button>
                                    </div>
                                  ) : (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "1rem" }}>
                                      <button
                                        type="button"
                                        className="tab-model-btn tab-model-btn-active"
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "6px",
                                          fontSize: "14px",
                                          padding: "8px 16px",
                                          borderRadius: "8px",
                                          cursor: isAnyAILoading || loadingChats ? "not-allowed" : "pointer",
                                          opacity: isAnyAILoading || loadingChats ? 0.6 : 1,
                                          fontWeight: 600,
                                          border: "1px solid #2563eb",
                                          background: "#ffffff",
                                          color: "#2563eb",
                                        }}
                                        onClick={() => {
                                          if (!isAnyAILoading && !loadingChats) {
                                            setComparisonQuery(DEFAULT_COMPARISON_QUERY);
                                            setComparisonResult("");
                                            setComparisonGenerated(false);
                                            setSelectedComparisonHistoryIndex(null);
                                            if (shouldAutoFocusQueryInput()) {
                                              setTimeout(() => { comparisonQueryTextareaRef.current?.focus(); }, 0);
                                            }
                                          }
                                        }}
                                      >
                                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                                          <path d="M13.5 8A5.5 5.5 0 1 1 8 2.5a5.48 5.48 0 0 1 3.89 1.61L10 6h4V2l-1.46 1.46A7 7 0 1 0 15 8h-1.5z" fill="#2563eb" />
                                        </svg>
                                        Re-run Comparative Analysis with another AI Model
                                      </button>
                                      <span style={{ fontSize: "12px", color: darkMode ? "#94a3b8" : "#64748b", marginTop: "6px" }}>
                                        Compare results with a different model
                                      </span>
                                    </div>
                                  )}
                                </>
                              )}
                          </div>
                        </motion.div>
                      </motion.div>
                    )}

                    {activeTab === "final" && (
                      <motion.div
                        className="tab-content"
                        key="final"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <motion.div
                          className="tab-card tab-card-green"
                          initial={{ scale: 0.95 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="tab-card-content">
                            {/* History Dropdown */}
                            {synthesisHistory.length > 0 && (
                              <div style={{ marginBottom: "1rem" }}>
                                <p
                                  style={{
                                    marginBottom: "0.5rem",
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    color: darkMode ? "#cbd5e1" : "#374151",
                                  }}
                                >
                                  Previous final synthesis
                                </p>
                                <div style={{ position: "relative" }}>
                                  <button
                                    type="button"
                                    onClick={() => !isAnyAILoading && setSynthesisHistoryOpen((prev) => !prev)}
                                    style={{
                                      width: "100%",
                                      padding: "0.5rem 2rem 0.5rem 0.5rem",
                                      borderRadius: "0.5rem",
                                      border: darkMode ? "1px solid #4b5563" : "1px solid #d1d5db",
                                      backgroundColor: darkMode ? "#1f2937" : "#ffffff",
                                      color: darkMode ? "#f3f4f6" : "#111827",
                                      fontSize: "0.875rem",
                                      cursor: isAnyAILoading ? "not-allowed" : "pointer",
                                      opacity: isAnyAILoading ? 0.6 : 1,
                                      textAlign: "left",
                                      position: "relative",
                                    }}
                                  >
                                    {selectedSynthesisHistoryIndex !== null
                                      ? (() => {
                                        const entry = synthesisHistory[selectedSynthesisHistoryIndex];
                                        if (!entry) return "Select previous synthesis";
                                        return entry.type === "executive_summary"
                                          ? `Executive Summary (${getHistoryDateStr(entry.timestamp)})`
                                          : `${entry.model || "Synthesis"} — ${getHistoryDateStr(entry.timestamp)}`;
                                      })()
                                      : "Select previous synthesis"}
                                    <ChevronDown size={14} style={{ position: "absolute", right: "0.5rem", top: "50%", transform: "translateY(-50%)" }} />
                                  </button>
                                  {synthesisHistoryOpen && (
                                    <>
                                      <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setSynthesisHistoryOpen(false)} />
                                      <div style={{
                                        position: "absolute",
                                        top: "calc(100% + 4px)",
                                        left: 0,
                                        right: 0,
                                        zIndex: 100,
                                        maxHeight: "240px",
                                        overflowY: "auto",
                                        borderRadius: "0.5rem",
                                        border: darkMode ? "1px solid #4b5563" : "1px solid #d1d5db",
                                        backgroundColor: darkMode ? "#1f2937" : "#ffffff",
                                        boxShadow: darkMode ? "0 8px 24px rgba(0,0,0,0.5)" : "0 8px 24px rgba(0,0,0,0.12)",
                                      }}>
                                        {(() => {
                                          const runs = synthesisHistory
                                            .map((e, i) => ({ ...e, _idx: i }))
                                            .filter((e) => e.type !== "executive_summary");
                                          return runs.map((run) => {
                                            const execSummary = synthesisHistory.find(
                                              (e) => e.type === "executive_summary" && e.sourceModel === run.model,
                                            );
                                            const execIdx = execSummary ? synthesisHistory.indexOf(execSummary) : -1;
                                            const selectItem = (index) => {
                                              const selectedHistory = synthesisHistory[index];
                                              if (!selectedHistory) return;
                                              setSynthesisQuery(selectedHistory.query || DEFAULT_SYNTHESIS_QUERY);
                                              setSynthesisResult(removeCitationMarkers(selectedHistory.result || ""));
                                              setSynthesisGenerated(false);
                                              const historyModel = selectedHistory.type === "executive_summary"
                                                ? selectedHistory.sourceModel || selectedHistory.model
                                                : selectedHistory.model;
                                              if (historyModel) setSynthesisModel(historyModel);
                                              setSelectedSynthesisHistoryIndex(index);
                                              setSynthesisHistoryOpen(false);
                                            };
                                            return (
                                              <div key={run._idx}>
                                                <div style={{
                                                  padding: "0.4rem 0.6rem",
                                                  fontSize: "0.75rem",
                                                  fontWeight: 600,
                                                  color: darkMode ? "#93c5fd" : "#2563eb",
                                                  backgroundColor: darkMode ? "#111827" : "#f1f5f9",
                                                  borderBottom: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",
                                                }}>
                                                  {formatHistoryTimestamp(run.timestamp, run.model)}
                                                </div>
                                                <div
                                                  onClick={() => selectItem(run._idx)}
                                                  style={{
                                                    padding: "0.45rem 0.6rem 0.45rem 1.2rem",
                                                    fontSize: "0.85rem",
                                                    cursor: "pointer",
                                                    color: darkMode ? "#e5e7eb" : "#1f2937",
                                                    backgroundColor: selectedSynthesisHistoryIndex === run._idx
                                                      ? (darkMode ? "#1e3a5f" : "#eff6ff")
                                                      : "transparent",
                                                  }}
                                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = darkMode ? "#374151" : "#f3f4f6"; }}
                                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = selectedSynthesisHistoryIndex === run._idx ? (darkMode ? "#1e3a5f" : "#eff6ff") : "transparent"; }}
                                                >
                                                  Full Final Synthesis
                                                </div>
                                                {execIdx >= 0 && (
                                                  <div
                                                    onClick={() => selectItem(execIdx)}
                                                    style={{
                                                      padding: "0.45rem 0.6rem 0.45rem 1.2rem",
                                                      fontSize: "0.85rem",
                                                      cursor: "pointer",
                                                      color: darkMode ? "#cbd5e1" : "#4b5563",
                                                      backgroundColor: selectedSynthesisHistoryIndex === execIdx
                                                        ? (darkMode ? "#1e3a5f" : "#eff6ff")
                                                        : "transparent",
                                                      borderBottom: darkMode ? "1px solid #374151" : "1px solid #f3f4f6",
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = darkMode ? "#374151" : "#f3f4f6"; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = selectedSynthesisHistoryIndex === execIdx ? (darkMode ? "#1e3a5f" : "#eff6ff") : "transparent"; }}
                                                  >
                                                    Executive Summary ({getHistoryDateStr(execSummary.timestamp)})
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          });
                                        })()}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "1rem",
                              }}
                            >
                              {/* Query Field */}
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "0.5rem",
                                }}
                              >
                                <label className="rs-stage-label">
                                  Final Synthesis Prompt
                                </label>
                                <div className="tab-input-container">
                                  {/* File input (hidden) */}
                                  <input
                                    type="file"
                                    ref={synthesisFileInputRef}
                                    style={{ display: "none" }}
                                    accept=".pdf,.docx,.doc,.txt"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        handleFileUpload(file, "synthesisFile");
                                      }
                                      e.target.value = ""; // Reset input
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: "relative",
                                      flex: "1 1 auto",
                                      width: "100%",
                                      minWidth: 0,
                                    }}
                                  >
                                    <motion.textarea
                                      ref={setSynthesisQueryTextareaRef}
                                      placeholder="Produce a final legal synthesis for litigation planning using ONLY the AI responses from the Initial Query and, if available, the Comparative Analysis. Do not introduce new facts, cases, statutes, or legal theories not mentioned by the models. Analyze the issues, apply the relevant legal principles, and provide a clear, attorney-ready assessment of the client's position. Identify the primary and secondary vulnerabilities, assess whether the position is STRONG, MODERATE, or WEAK, and provide specific corrective actions to strengthen the case. Be direct and practical—focus on what will materially impact litigation outcome. Structure the response using IRAC+ format with clear section headings and lawyer-friendly language."
                                      className="tab-textarea tab-textarea-green"
                                      value={synthesisQuery}
                                      onChange={(e) => {
                                        setSynthesisQuery(e.target.value);
                                      }}
                                      disabled={isAnyAILoading}
                                      style={{
                                        opacity: isAnyAILoading ? 0.7 : 1,
                                        cursor: isAnyAILoading
                                          ? "not-allowed"
                                          : "text",
                                      }}
                                    />
                                  </div>
                                  <motion.button
                                    className="attach-btn attach-btn-green"
                                    onClick={() =>
                                      synthesisFileInputRef.current?.click()
                                    }
                                    whileHover={{ scale: 1.1, rotate: 15 }}
                                    whileTap={{ scale: 0.9 }}
                                    disabled={loadingChats || isAnyAILoading}
                                    title="Attach file (PDF, DOCX, TXT)"
                                  >
                                    <Paperclip size={18} />
                                  </motion.button>
                                </div>
                              </div>
                              {/* Uploaded file display - below input fields */}
                              {fileUploading.synthesisFile ? (
                                <motion.div
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className="uploaded-file-display"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    padding: "0.4rem 0.6rem",
                                    background: "#d1fae5",
                                    borderRadius: "0.5rem",
                                    fontSize: "0.8rem",
                                    width: "fit-content",
                                    maxWidth: "100%",
                                  }}
                                >
                                  <Loader
                                    size={14}
                                    className="spinning"
                                    color="#16a34a"
                                  />
                                  <span
                                    className="upload-status-text"
                                    style={{ color: "#16a34a" }}
                                  >
                                    Uploading file...
                                  </span>
                                </motion.div>
                              ) : (
                                getCurrentChatFiles().synthesisFile &&
                                (() => {
                                  const synthesisFile =
                                    getCurrentChatFiles().synthesisFile;
                                  return (
                                    <motion.div
                                      initial={{ opacity: 0, y: -10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      className="uploaded-file-display"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.5rem",
                                        padding: "0.4rem 0.6rem",
                                        background: "#d1fae5",
                                        borderRadius: "0.5rem",
                                        fontSize: "0.8rem",
                                        width: "fit-content",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      <FileText size={14} color="#16a34a" />
                                      <a
                                        href={
                                          synthesisFile.fileUrl ||
                                          synthesisFile.s3Url
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          color: "#16a34a",
                                          textDecoration: "none",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: "300px",
                                        }}
                                        title={synthesisFile.fileName}
                                      >
                                        {synthesisFile.fileName}
                                      </a>
                                      <button
                                        onClick={() =>
                                          !isAnyAILoading &&
                                          handleFileRemove("synthesisFile")
                                        }
                                        disabled={isAnyAILoading}
                                        style={{
                                          background: "transparent",
                                          border: "none",
                                          cursor: isAnyAILoading
                                            ? "not-allowed"
                                            : "pointer",
                                          padding: "0.2rem",
                                          display: "flex",
                                          alignItems: "center",
                                          flexShrink: 0,
                                          opacity: isAnyAILoading ? 0.6 : 1,
                                        }}
                                      >
                                        <X size={14} color="#dc2626" />
                                      </button>
                                    </motion.div>
                                  );
                                })()
                              )}
                              <p className="tab-tip">
                                💡 Tip: Feel free to edit or use your own prompt.
                                The response will appear in the Response field
                                below.
                              </p>
                              {/* Response Field */}
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "0.5rem",
                                }}
                              >
                                <label className="rs-stage-label">
                                  Response
                                </label>
                                {synthesisExecutiveSummaryLoading && (
                                  <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    style={{
                                      marginBottom: "0.5rem",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                      color: darkMode ? "#93c5fd" : "#2563eb",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    <Loader size={16} className="spinning" />
                                    <span>Generating Executive Summary</span>
                                  </motion.div>
                                )}
                                <div
                                  style={{ position: "relative", width: "100%" }}
                                >
                                  {synthesisResult.trim() === "" &&
                                    !synthesisExecutiveSummaryLoading ? (
                                    <div
                                      className="tab-textarea tab-textarea-green"
                                      style={{
                                        minHeight: "200px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: darkMode ? "#64748b" : "#94a3b8",
                                        fontStyle: "italic",
                                        opacity: synthesisLoading ? 1 : 0.7,
                                      }}
                                    >
                                      Final synthesis response will appear here...
                                    </div>
                                  ) : (
                                    <SectionedHtml
                                      className="tab-textarea tab-textarea-green response-html"
                                      style={{
                                        minHeight: "200px",
                                        maxHeight: "600px",
                                        overflow: "auto",
                                        cursor: "default",
                                        padding: "0.75rem",
                                      }}
                                      ref={synthesisResponseRef}
                                      isLoading={
                                        synthesisLoading ||
                                        synthesisExecutiveSummaryLoading
                                      }
                                      html={convertTextToHtml(
                                        synthesisResult ||
                                        (synthesisExecutiveSummaryLoading
                                          ? "Generating..."
                                          : ""),
                                      )}
                                    />
                                  )}
                                </div>
                                {/* Processing indicator - exactly below response field */}
                                {synthesisLoading && (
                                  <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    style={{
                                      marginTop: "0.5rem",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                      color: darkMode ? "#93c5fd" : "#2563eb",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    <Loader size={16} className="spinning" />
                                    <span>
                                      Processing... please allow up to one minute
                                    </span>
                                  </motion.div>
                                )}
                              </div>
                            </div>

                            {/* Disclaimer removed from here - moved inside text field */}
                            <div className="tab-edit-actions">
                              <div className="synthesis-edit-export-tooltip-wrap">
                                <motion.button
                                  className="response-btn tab-edit-btn"
                                  whileHover={
                                    queryLoading ||
                                      comparisonLoading ||
                                      synthesisLoading ||
                                      synthesisExecutiveSummaryLoading ||
                                      loadingChats ||
                                      !synthesisResult?.trim()
                                      ? {}
                                      : { scale: 1.02 }
                                  }
                                  whileTap={
                                    queryLoading ||
                                      comparisonLoading ||
                                      synthesisLoading ||
                                      synthesisExecutiveSummaryLoading ||
                                      loadingChats ||
                                      !synthesisResult?.trim()
                                      ? {}
                                      : { scale: 0.97 }
                                  }
                                  onClick={() => {
                                    const selectedEntry =
                                      selectedSynthesisHistoryIndex !== null &&
                                        synthesisHistory[
                                        selectedSynthesisHistoryIndex
                                        ]
                                        ? synthesisHistory[
                                        selectedSynthesisHistoryIndex
                                        ]
                                        : null;
                                    const isExecutiveSummary =
                                      selectedEntry?.type === "executive_summary";
                                    const title = isExecutiveSummary
                                      ? `Executive Summary (${selectedEntry?.sourceModel ?? synthesisHistory[0]?.sourceModel ?? "Unknown"}) – ${getHistoryDateStr(selectedEntry?.timestamp ?? new Date().toISOString())}`
                                      : `Final Synthesis ${selectedEntry?.model ?? synthesisHistory[0]?.model ?? synthesisModel}`;
                                    handleOpenEditor(
                                      isExecutiveSummary
                                        ? "executive_summary"
                                        : "synthesis",
                                      title,
                                      synthesisResult,
                                    );
                                  }}
                                  disabled={
                                    isAnyAILoading ||
                                    synthesisExecutiveSummaryLoading ||
                                    loadingChats ||
                                    !synthesisResult?.trim()
                                  }
                                  style={{
                                    opacity:
                                      isAnyAILoading ||
                                        synthesisExecutiveSummaryLoading ||
                                        loadingChats ||
                                        !synthesisResult?.trim()
                                        ? 0.6
                                        : 1,
                                    cursor:
                                      isAnyAILoading ||
                                        synthesisExecutiveSummaryLoading ||
                                        loadingChats ||
                                        !synthesisResult?.trim()
                                        ? "not-allowed"
                                        : "pointer",
                                  }}
                                >
                                  Edit / Export
                                </motion.button>
                                {!synthesisResult?.trim() && !synthesisExecutiveSummaryLoading && !isAnyAILoading && !loadingChats && (
                                  <div className="locked-tooltip locked-tooltip-muted" data-align="right">
                                    Run Final Synthesis first
                                  </div>
                                )}
                              </div>

                              <div
                                className="synthesis-exec-summary-tooltip-wrap"
                                ref={synthesisExecSummaryWrapRef}
                                onPointerDownCapture={() => {
                                  if (!isMobileScreen) return;
                                  const shouldRender =
                                    !isLoggedIn ||
                                    isTrial ||
                                    (!synthesisResult?.trim() &&
                                      !synthesisExecutiveSummaryLoading &&
                                      !isAnyAILoading) ||
                                    synthesisHasExecutiveSummary;

                                  if (!shouldRender) return;

                                  setShowSynthesisExecSummaryTooltipMobile(true);
                                }}
                              >
                                <motion.button
                                  className="response-btn tab-edit-btn"
                                  whileHover={
                                    !isLoggedIn ||
                                      isAnyAILoading ||
                                      loadingChats ||
                                      synthesisExecutiveSummaryLoading ||
                                      !synthesisResult?.trim() ||
                                      synthesisHasExecutiveSummary
                                      ? {}
                                      : { scale: 1.02 }
                                  }
                                  whileTap={
                                    !isLoggedIn ||
                                      isAnyAILoading ||
                                      loadingChats ||
                                      synthesisExecutiveSummaryLoading ||
                                      !synthesisResult?.trim() ||
                                      synthesisHasExecutiveSummary
                                      ? {}
                                      : { scale: 0.97 }
                                  }
                                  onClick={handleCreateSynthesisExecutiveSummary}
                                  disabled={
                                    !isLoggedIn ||
                                    isTrial ||
                                    isAnyAILoading ||
                                    loadingChats ||
                                    synthesisExecutiveSummaryLoading ||
                                    !synthesisResult?.trim() ||
                                    synthesisHasExecutiveSummary
                                  }
                                  style={{
                                    opacity:
                                      !isLoggedIn ||
                                        isTrial ||
                                        isAnyAILoading ||
                                        loadingChats ||
                                        synthesisExecutiveSummaryLoading ||
                                        !synthesisResult?.trim() ||
                                        synthesisHasExecutiveSummary
                                        ? 0.5
                                        : 1,
                                    cursor:
                                      !isLoggedIn ||
                                        isTrial ||
                                        isAnyAILoading ||
                                        loadingChats ||
                                        synthesisExecutiveSummaryLoading ||
                                        !synthesisResult?.trim() ||
                                        synthesisHasExecutiveSummary
                                        ? "not-allowed"
                                        : "pointer",
                                  }}
                                  title={
                                    !isLoggedIn
                                      ? "Start your Free Trial to unlock Executive Summary"
                                      : isTrial
                                        ? "Executive Summary requires a paid plan"
                                        : (!synthesisResult?.trim() && !synthesisExecutiveSummaryLoading)
                                          ? "Run a final synthesis first"
                                          : synthesisHasExecutiveSummary
                                            ? "Executive summary already generated for this result. Run a new Final Synthesis with another model to create a new one."
                                            : ""
                                  }
                                >
                                  {!isLoggedIn && (
                                    <Lock
                                      size={12}
                                      style={{
                                        marginRight: "6px",
                                        verticalAlign: "middle",
                                      }}
                                    />
                                  )}
                                  {synthesisExecutiveSummaryLoading ? (
                                    <>
                                      <Loader
                                        size={14}
                                        style={{
                                          marginRight: "6px",
                                          verticalAlign: "middle",
                                        }}
                                      />
                                      Generating Executive Summary
                                    </>
                                  ) : (
                                    "Create Executive Summary"
                                  )}
                                </motion.button>
                                {(!isLoggedIn ||
                                  isTrial ||
                                  aiActionsExhausted ||
                                  (!synthesisResult?.trim() &&
                                    !synthesisExecutiveSummaryLoading &&
                                    !isAnyAILoading) ||
                                  synthesisHasExecutiveSummary) && (
                                    <div
                                      className="locked-tooltip locked-tooltip-muted"
                                      data-align="right"
                                      style={{
                                        display: isMobileScreen
                                          ? showSynthesisExecSummaryTooltipMobile
                                            ? "block"
                                            : "none"
                                          : undefined,
                                        opacity: isMobileScreen
                                          ? showSynthesisExecSummaryTooltipMobile
                                            ? 1
                                            : 0
                                          : undefined,
                                        visibility: isMobileScreen
                                          ? showSynthesisExecSummaryTooltipMobile
                                            ? "visible"
                                            : "hidden"
                                          : undefined,
                                      }}
                                    >
                                      {!isLoggedIn
                                        ? "Start your Free Trial to unlock Executive Summary"
                                        : isTrial
                                          ? "Premium Plans unlock Executive Summary"
                                          : aiActionsExhausted
                                            ? AI_ACTIONS_EXHAUSTED_MESSAGE
                                          : !synthesisResult?.trim()
                                            ? "Run Final Synthesis first"
                                            : synthesisHasExecutiveSummary
                                              ? "Executive Summary already exists"
                                              : ""}
                                    </div>
                                  )}
                              </div>
                            </div>
                            <div className="tab-model-selection">
                              <div>
                                <p className="tab-model-label">
                                  Select which AI should perform the Final
                                  Synthesis
                                </p>
                                <p className="tab-model-selection--header--recommendation">
                                  Claude or Grok recommended
                                </p>
                              </div>
                              <div className="tab-model-buttons">
                                {isTrial ? (
                                  [
                                    "ChatGPT",
                                    "Claude",
                                    "Gemini",
                                    "Perplexity",
                                    "Grok",
                                  ].map((model) => (
                                    <motion.button
                                      key={model}
                                      className="tab-model-btn tab-model-btn-green tab-model-btn-green-outline"
                                      disabled
                                      style={{
                                        opacity: 0.5,
                                        cursor: "not-allowed",
                                        position: "relative",
                                      }}
                                      title="Available on paid plans"
                                    >
                                      🔒 {model}
                                    </motion.button>
                                  ))
                                ) : (
                                  [
                                    "ChatGPT",
                                    "Claude",
                                    "Gemini",
                                    "Perplexity",
                                    "Grok",
                                  ].map((model) => {
                                    const completed =
                                      synthesisModelsCompleted.has(model);
                                    return (
                                      <motion.button
                                        key={model}
                                        className={`tab-model-btn tab-model-btn-green ${synthesisModel === model ? "tab-model-btn-green-active" : "tab-model-btn-green-outline"}${completed ? " tab-model-btn-completed" : ""}`}
                                        onClick={() => {
                                          if (completed) return;
                                          setSynthesisModel(model);
                                          setSynthesisQuery(
                                            DEFAULT_SYNTHESIS_QUERY,
                                          );
                                          setSynthesisResult("");
                                          setSynthesisGenerated(false);
                                          setSelectedSynthesisHistoryIndex(null);
                                          if (shouldAutoFocusQueryInput()) {
                                            setTimeout(() => {
                                              synthesisQueryTextareaRef.current?.focus();
                                            }, 0);
                                          }
                                        }}
                                        whileHover={
                                          completed ? {} : { scale: 1.05, y: -2 }
                                        }
                                        whileTap={
                                          completed ? {} : { scale: 0.95 }
                                        }
                                        transition={{
                                          type: "spring",
                                          stiffness: 400,
                                          damping: 17,
                                        }}
                                        disabled={
                                          loadingChats ||
                                          isAnyAILoading ||
                                          completed
                                        }
                                        title={
                                          completed
                                            ? "Already completed for this query"
                                            : ""
                                        }
                                      >
                                        {model}
                                      </motion.button>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                            {/* Anon locked info box OR trial locked info box */}
                            {!isLoggedIn ? (
                              <div
                                style={{
                                  padding: "10px 24px",
                                  background: "linear-gradient(135deg, #22c55e, #16a34a)",
                                  border: "none",
                                  borderRadius: "8px",
                                  fontSize: "14px",
                                  color: "#0369a1",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "10px",
                                  marginBottom: "1.5rem",
                                  boxShadow: "0 4px 12px rgba(22, 163, 74, 0.3)",
                                  width: "fit-content",
                                  margin: "0 auto 1.5rem",
                                  cursor: "pointer",
                                }}
                                onClick={() => navigate("/pricing")}
                              >
                                <Lock size={14} color="#0369a1" />
                                <span style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
                                  Final Synthesis
                                  <span style={{ fontWeight: 400, opacity: 0.9 }}>is included in paid plans.</span>
                                  <span style={{ textDecoration: "underline", marginLeft: "8px", fontSize: "12px" }}>Start free trial →</span>
                                </span>
                              </div>
                            ) : (
                              isTrial && (
                                <div
                                  style={{
                                    padding: "10px 24px",
                                    background: "linear-gradient(135deg, #22c55e, #16a34a)",
                                    border: "none",
                                    borderRadius: "8px",
                                    fontSize: "14px",
                                    color: "#eaeef0ff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "10px",
                                    marginBottom: "1.5rem",
                                    boxShadow: "0 4px 12px rgba(22, 163, 74, 0.3)",
                                    width: "fit-content",
                                    margin: "0 auto 1.5rem",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => navigate("/pricing")}
                                >
                                  <Lock size={14} color="#0369a1" />
                                  <span style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
                                    Final Synthesis
                                    <span style={{ fontWeight: 400, opacity: 0.9 }}>is available on paid plans.</span>
                                    <span style={{ textDecoration: "underline", marginLeft: "8px", fontSize: "12px" }}>Upgrade now →</span>
                                  </span>
                                </div>
                              )
                            )}
                            <motion.button
                              className="tab-action-btn tab-action-btn-green"
                              onClick={handleRunSynthesis}
                              disabled={
                                !isLoggedIn ||
                                isTrial ||
                                isAnyAILoading ||
                                loadingChats ||
                                synthesisModelsCompleted.has(synthesisModel) ||
                                Object.keys(responses).filter(
                                  (k) => responses[k]?.text,
                                ).length < 2
                              }
                              // No hover / tap animation while disabled: the
                              // stylesheet owns the disabled look (solid,
                              // readable) and an animated shadow or scale on
                              // top of it is what QA saw as the label vanishing.
                              whileHover={
                                !isLoggedIn ||
                                isTrial ||
                                isAnyAILoading ||
                                loadingChats ||
                                synthesisModelsCompleted.has(synthesisModel) ||
                                Object.keys(responses).filter(
                                  (k) => responses[k]?.text,
                                ).length < 2
                                  ? {}
                                  : {
                                      scale: 1.02,
                                      boxShadow: "0 10px 25px rgba(22, 163, 74, 0.3)",
                                    }
                              }
                              whileTap={
                                !isLoggedIn ||
                                isTrial ||
                                isAnyAILoading ||
                                loadingChats ||
                                synthesisModelsCompleted.has(synthesisModel) ||
                                Object.keys(responses).filter(
                                  (k) => responses[k]?.text,
                                ).length < 2
                                  ? {}
                                  : { scale: 0.98 }
                              }
                              style={{
                                cursor:
                                  isTrial ||
                                    isAnyAILoading ||
                                    loadingChats ||
                                    synthesisModelsCompleted.has(synthesisModel) ||
                                    Object.keys(responses).filter(
                                      (k) => responses[k]?.text,
                                    ).length < 2
                                    ? "not-allowed"
                                    : "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.4rem",
                                justifyContent: "center",
                              }}
                              title={
                                !isLoggedIn
                                  ? "Sign up to unlock Final Synthesis"
                                  : isAnyAILoading
                                    ? "Please wait for AI operation to complete"
                                    : synthesisModelsCompleted.has(synthesisModel)
                                      ? "This model has already completed Final Synthesis for this query"
                                      : Object.keys(responses).filter(
                                        (k) => responses[k]?.text,
                                      ).length < 2
                                        ? "You need at least 2 AI responses for synthesis"
                                        : ""
                              }
                            >
                              {!isLoggedIn && "🔒 "}
                              {`Run Final Synthesis & Fact-Check with ${synthesisModel}`}
                            </motion.button>

                            {(synthesisResult.trim() ||
                              synthesisHistory.length > 0) && (
                                <>
                                  {isTrial ? (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "1rem" }}>
                                      <button
                                        type="button"
                                        className="tab-model-btn tab-model-btn-green-active"
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "8px",
                                          padding: "8px 16px",
                                          border: "1px solid #16a34a",
                                          borderRadius: "8px",
                                          cursor: "pointer",
                                          background: "#ffffff",
                                          color: "#16a34a",
                                        }}
                                        onClick={() => {
                                          navigate("/pricing");
                                        }}
                                      >
                                        <Lock size={14} color="#16a34a" />
                                        <span style={{ fontSize: "14px", color: "#16a34a", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
                                          Re-run Final Synthesis with another AI Model
                                          <span style={{ textDecoration: "underline", marginLeft: "8px", fontSize: "12px", opacity: 0.9 }}>Upgrade to unlock</span>
                                        </span>
                                      </button>
                                    </div>
                                  ) : (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "1rem" }}>
                                      <button
                                        type="button"
                                        className="tab-model-btn tab-model-btn-green-active"
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "6px",
                                          fontSize: "14px",
                                          padding: "8px 16px",
                                          borderRadius: "8px",
                                          cursor: isAnyAILoading || loadingChats ? "not-allowed" : "pointer",
                                          opacity: isAnyAILoading || loadingChats ? 0.6 : 1,
                                          fontWeight: 600,
                                          border: "1px solid #16a34a",
                                          background: "#ffffff",
                                          color: "#16a34a",
                                        }}
                                        onClick={() => {
                                          if (!isAnyAILoading && !loadingChats) {
                                            setSynthesisQuery(DEFAULT_SYNTHESIS_QUERY);
                                            setSynthesisResult("");
                                            setSynthesisGenerated(false);
                                            setSelectedSynthesisHistoryIndex(null);
                                            if (shouldAutoFocusQueryInput()) {
                                              setTimeout(() => { synthesisQueryTextareaRef.current?.focus(); }, 0);
                                            }
                                          }
                                        }}
                                      >
                                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                                          <path d="M13.5 8A5.5 5.5 0 1 1 8 2.5a5.48 5.48 0 0 1 3.89 1.61L10 6h4V2l-1.46 1.46A7 7 0 1 0 15 8h-1.5z" fill="#16a34a" />
                                        </svg>
                                        Re-run Final Synthesis with another AI Model
                                      </button>
                                      <span style={{ fontSize: "12px", color: darkMode ? "#94a3b8" : "#64748b", marginTop: "6px" }}>
                                        Compare results with a different model
                                      </span>
                                    </div>
                                  )}
                                </>
                              )}
                          </div>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </LazySection>
              </>
            )}
            </div>

            {/* ── Composer, docked to the bottom of the workspace ── */}
            {/* The global query composer belongs to the Initial stage only.
                Comparison and Synthesis each have their own prompt field and
                generate button, so showing this as well was duplicate input
                that also covered the results. */}
            {activeStage === "initial" && (
            <div className="rw-dock">
              <div className="rw-dock-inner">
                <QueryInput
                  query={query}
                  setQuery={setQuery}
                  onSubmit={handleSubmitQuery}
                  isLoading={isAnyAILoading}
                  disabled={isAnyAILoading || mainQueryInputLocked}
                  files={getCurrentChatFiles().mainQueryFiles || []}
                  fileUploading={fileUploading.mainQueryFile}
                  onFileUpload={async (files) => {
                    const fileList = Array.isArray(files) ? files : [files];
                    const existingNames = new Set(
                      (getCurrentChatFiles().mainQueryFiles || []).map(
                        (f) => f.fileName,
                      ),
                    );
                    const seenInBatch = new Set();
                    for (const file of fileList) {
                      if (existingNames.has(file.name)) {
                        showError(
                          `The brief "${file.name}" has already been uploaded and is a duplicate.`,
                        );
                        continue;
                      }
                      if (seenInBatch.has(file.name)) {
                        showError(
                          `The brief "${file.name}" has already been uploaded and is a duplicate.`,
                        );
                        continue;
                      }
                      await handleFileUpload(file, "mainQueryFile");
                      seenInBatch.add(file.name);
                      existingNames.add(file.name);
                    }
                  }}
                  onFileRemove={(file) =>
                    handleFileRemove("mainQueryFile", file)
                  }
                  submitDisabled={
                    submitDisabled ||
                    selectedModels.length === 0 ||
                    submitLocked
                  }
                />
                <div className="query-stop-container">
                  {(queryLoading || queryLockedAfterStop) && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.3 }}
                      className="query-stop-inner"
                    >
                      <motion.button
                        type="button"
                        onClick={
                          !queryLockedAfterStop
                            ? () => {
                              stopInitialQuery();
                              showInfo(
                                "Query stopped. To continue, please enter a new query.",
                                "Query Stopped",
                              );
                            }
                            : undefined
                        }
                        disabled={queryLockedAfterStop}
                        className={`query-stop-button ${queryLockedAfterStop ? "stopped" : ""}`}
                        whileHover={
                          !queryLockedAfterStop
                            ? {
                              scale: 1.05,
                              boxShadow: "0 8px 16px rgba(239, 68, 68, 0.3)",
                            }
                            : {}
                        }
                        whileTap={!queryLockedAfterStop ? { scale: 0.98 } : {}}
                      >
                        <X size={20} strokeWidth={2.5} />
                        <span>
                          {queryLockedAfterStop ? "Stopped" : "Stop Query"}
                        </span>
                      </motion.button>
                    </motion.div>
                  )}
                </div>
              <motion.div variants={containerVariants}>
                <ModelSelector
                  selectedModels={selectedModels}
                  onToggleModel={toggleModel}
                  maxModels={!isLoggedIn ? 2 : 4}
                  // allowedModels={!isLoggedIn ? FREE_TIER_MODELS : null}
                  disabled={isAnyAILoading || queryLockedAfterStop || analysisStarted}
                  variants={{
                    item: itemVariants,
                    container: containerVariants,
                  }}
                />
              </motion.div>
              </div>
              {/* Demo video: only while the workspace is empty, right under
                  the composer a first-time visitor is looking at. Collapsed
                  it is a thumbnail row; clicking it opens and plays. It
                  leaves with the centered layout once a run starts. */}
              {composerCentered && (
                <div className="rw-dock-video">
                  <VideoWidget
                    mode="collapsible"
                    accentColor="blue"
                    badge="DEMO"
                    title="See How Research Mode Works"
                    subtitle="Watch it analyze a real issue — no login required"
                    videoSrc="/videos/research-mode-demo.mp4"
                  />
                </div>
              )}
            </div>
            )}
            <Footer variant="compact" disableLazy />
            </motion.section>
        </motion.div >

        {/* ── Context rail: live activity + run summary ── */}
        <aside className="rw-ctx ds-scroll-slim" aria-label="Research context">
          <section className="rw-ctx-sec">
            <h3 className="rw-ctx-title">
              {isAnyAILoading && (
                <span className="ds-dot ds-dot-live rw-ctx-live" aria-hidden="true" />
              )}
              Live activity
            </h3>
            {selectedModels.length > 0 ? (
              <ResearchProgress
                models={selectedModels}
                responses={responses}
                isLoading={isAnyAILoading}
                stageLabel={
                  comparisonLoading
                    ? "Running Comparative Analysis"
                    : synthesisLoading
                      ? "Running Final Synthesis"
                      : null
                }
              />
            ) : (
              <p className="rw-ctx-empty">Select a model to begin.</p>
            )}
          </section>

          <section className="rw-ctx-sec">
            <h3 className="rw-ctx-title">This run</h3>
            <dl className="rw-ctx-list">
              <div><dt>Models</dt><dd>{selectedModels.length || "—"}</dd></div>
              <div><dt>Responses</dt><dd>{completedResponseCount || "—"}</dd></div>
              <div>
                <dt>Comparative Analysis</dt>
                <dd className={comparisonResult ? "is-ready" : comparisonLoading ? "is-running" : ""}>
                  {comparisonLoading ? "Running…" : comparisonResult ? "Ready" : "Not run"}
                </dd>
              </div>
              <div>
                <dt>Final Synthesis</dt>
                <dd className={synthesisResult ? "is-ready" : synthesisLoading ? "is-running" : ""}>
                  {synthesisLoading ? "Running…" : synthesisResult ? "Ready" : "Not run"}
                </dd>
              </div>
            </dl>
          </section>
        </aside>
      </div >

      {/* Query Stopped Dialog */}
      < AnimatePresence >
        {showQueryStoppedDialog &&
          createPortal(
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowQueryStoppedDialog(false)}
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.55)",
                zIndex: 999999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "1rem",
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: darkMode ? "#1e293b" : "#ffffff",
                  padding: "2rem",
                  borderRadius: "1rem",
                  maxWidth: "380px",
                  width: "100%",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
                  border: `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`,
                  textAlign: "center",
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    width: "52px",
                    height: "52px",
                    borderRadius: "50%",
                    backgroundColor: darkMode ? "#1e3a5f" : "#eff6ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1.25rem",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={darkMode ? "#60a5fa" : "#3b82f6"}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>

                {/* Title */}
                <h3
                  style={{
                    margin: "0 0 0.25rem",
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    color: darkMode ? "#f1f5f9" : "#0f172a",
                  }}
                >
                  Query Stopped
                </h3>
                <p
                  style={{
                    margin: "0 0 0.75rem",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    color: darkMode ? "#f9fafb" : "#111827",
                  }}
                >
                  Query Stopped Incomplete
                </p>

                {/* Subtitle */}
                <p
                  style={{
                    margin: "0 0 0.75rem",
                    fontSize: "0.9rem",
                    color: darkMode ? "#94a3b8" : "#64748b",
                    lineHeight: 1.5,
                  }}
                >
                  The initial query was stopped before completion.
                </p>

                {/* Models */}
                <div
                  style={{
                    margin: "0 0 1rem",
                    fontSize: "0.85rem",
                    color: darkMode ? "#cbd5f5" : "#1e3a8a",
                  }}
                >
                  <span style={{ fontWeight: 600, marginRight: "0.25rem" }}>
                    Models
                  </span>
                  <span>
                    {Array.isArray(selectedModels) && selectedModels.length > 0
                      ? selectedModels.join(" · ")
                      : "Selected models"}
                  </span>
                </div>

                {/* Message */}
                <p
                  style={{
                    margin: "0 0 1.5rem",
                    fontSize: "0.9rem",
                    color: darkMode ? "#e5e7eb" : "#111827",
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    This query cannot be resumed.
                  </span>{" "}
                  To continue analysis,{" "}
                  <span
                    onClick={() => {
                      resetQueryLockAfterStop();
                      createNewChat();
                      setShowQueryStoppedDialog(false);
                    }}
                    style={{
                      color: "#2563eb",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    start a new chat
                  </span>
                  .
                </p>

                {/* OK Button */}
                <button
                  onClick={() => setShowQueryStoppedDialog(false)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0.6rem 2rem",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: "#3b82f6",
                    border: "none",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#2563eb")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#3b82f6")
                  }
                >
                  OK
                </button>
              </motion.div>
            </motion.div>,
            document.body,
          )
        }
      </AnimatePresence >

      {/* Anonymous Limit Reached Modal */}

      < AnimatePresence >
        {showAnonSignupModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowAnonSignupModal(false)}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15, 23, 42, 0.7)",
              backdropFilter: "blur(4px)",
              zIndex: 99999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1rem",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: darkMode ? "#1e293b" : "#ffffff",
                padding: "2rem",
                borderRadius: "1rem",
                maxWidth: "400px",
                width: "100%",
                boxShadow:
                  "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                border: `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  background: darkMode
                    ? "rgba(59, 130, 246, 0.1)"
                    : "#dbeafe",
                  color: darkMode ? "#60a5fa" : "#3b82f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1.25rem",
                }}
              >
                <Lock size={24} />
              </div>
              <h2
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                  color: darkMode ? "#f8fafc" : "#0f172a",
                  marginBottom: "0.75rem",
                }}
              >
                {anonSignupMessage
                  ? "Daily Question Limit Reached"
                  : "You've Hit Your Free Limit"}
              </h2>
              <p
                style={{
                  color: darkMode ? "#94a3b8" : "#64748b",
                  fontSize: "0.95rem",
                  lineHeight: "1.5",
                  marginBottom: "1.5rem",
                }}
              >
                {anonSignupMessage ||
                  "You've used your free demo for this session. Start your free trial to continue analyzing your case, running debates, and refining your arguments."}
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <Link
                  to="/pricing"
                  style={{
                    display: "block",
                    padding: "0.75rem",
                    background: "#3b82f6",
                    color: "white",
                    fontWeight: "600",
                    borderRadius: "0.5rem",
                    textDecoration: "none",
                    transition: "background 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.background = "#2563eb")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.background = "#3b82f6")
                  }
                >
                  Start Free Trial
                </Link>
                <Link
                  to="/login"
                  style={{
                    display: "block",
                    padding: "0.75rem",
                    background: darkMode ? "#334155" : "#f1f5f9",
                    color: darkMode ? "#f8fafc" : "#0f172a",
                    fontWeight: "600",
                    borderRadius: "0.5rem",
                    textDecoration: "none",
                    transition: "background 0.2s",
                  }}
                  onMouseOver={(e) =>
                  (e.currentTarget.style.background = darkMode
                    ? "#475569"
                    : "#e2e8f0")
                  }
                  onMouseOut={(e) =>
                  (e.currentTarget.style.background = darkMode
                    ? "#334155"
                    : "#f1f5f9")
                  }
                >
                  Log In (Already have an account?)
                </Link>
              </div>
              <button
                onClick={() => setShowAnonSignupModal(false)}
                style={{
                  marginTop: "1.25rem",
                  background: "transparent",
                  border: "none",
                  color: darkMode ? "#94a3b8" : "#64748b",
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Maybe later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence >

      {/* Search Dialog - Lazy loaded when opened */}
      {
        isSearchOpen &&
        searchDialogLoaded &&
        createPortal(
          <div
            className="search-dialog-overlay"
            onClick={() => {
              setIsSearchOpen(false);
              setSearchQuery("");
            }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 999999,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              paddingTop: "10vh",
              pointerEvents: "auto",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.2 }}
              className="search-dialog"
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: darkMode ? "rgba(30, 41, 59, 0.95)" : "white",
                borderRadius: "0.75rem",
                width: "90%",
                maxWidth: "640px",
                maxHeight: "80vh",
                display: "flex",
                flexDirection: "column",
                boxShadow:
                  "0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)",
                overflow: "hidden",
                border: darkMode ? "1px solid #475569" : "1px solid #bfdbfe",
              }}
            >
              {/* Search Input Header */}
              <div
                style={{
                  padding: "1.5rem 1rem 0 1rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    marginBottom: "0.75rem",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Search
                      size={20}
                      style={{
                        position: "absolute",
                        left: "0",
                        color: darkMode ? "#94a3b8" : "#1e3a8a",
                        pointerEvents: "none",
                      }}
                    />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search chats..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.75rem 0.75rem 0.75rem 2rem",
                        border: "none",
                        borderRadius: "0",
                        fontSize: "1rem",
                        outline: "none",
                        backgroundColor: "transparent",
                        color: darkMode ? "#f1f5f9" : "#1e3a8a",
                        fontFamily: "inherit",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && filteredChats.length > 0) {
                          const firstChat = filteredChats[0];
                          handleChatSelect(firstChat.chatId || firstChat._id);
                        }
                      }}
                    />
                    {searchQuery && (
                      <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchQuery("");
                          searchInputRef.current?.focus();
                        }}
                        style={{
                          position: "absolute",
                          right: "0",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "0.25rem",
                          display: "flex",
                          alignItems: "center",
                          color: darkMode ? "#94a3b8" : "#1e3a8a",
                        }}
                        whileHover={{
                          scale: 1.1,
                          color: darkMode ? "#cbd5e1" : "#1e40af",
                        }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <X size={16} />
                      </motion.button>
                    )}
                  </div>
                  <motion.button
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchQuery("");
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      color: darkMode ? "#94a3b8" : "#1e3a8a",
                      borderRadius: "0.25rem",
                    }}
                    whileHover={{
                      backgroundColor: darkMode
                        ? "#334155"
                        : "rgba(191, 219, 254, 0.5)",
                      scale: 1.1,
                    }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X size={20} />
                  </motion.button>
                </div>
                {/* Full width line below input */}
                <div
                  style={{
                    height: "1px",
                    backgroundColor: darkMode ? "#475569" : "#93c5fd",
                    marginLeft: "-1rem",
                    marginRight: "-1rem",
                    width: "calc(100% + 2rem)",
                  }}
                />
                {/* New Chat option below line */}
                <motion.div
                  onClick={() => {
                    handleNewChatClick({ closeSearch: true });
                  }}
                  whileHover={{
                    backgroundColor: darkMode
                      ? "#334155"
                      : "rgba(191, 219, 254, 0.5)",
                  }}
                  style={{
                    padding: "0.75rem",
                    marginTop: "0.5rem",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <MessageSquare
                    size={18}
                    style={{
                      color: darkMode ? "#94a3b8" : "#1e3a8a",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "0.875rem",
                      color: darkMode ? "#f1f5f9" : "#1e3a8a",
                    }}
                  >
                    New Chat
                  </span>
                </motion.div>
              </div>

              {/* Chat List */}
              <div
                className="search-dialog-chat-list"
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "0.5rem",
                }}
              >
                {loadingChats ? (
                  <div style={{ padding: "0.5rem" }}>
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        style={{
                          padding: "0.75rem",
                          marginBottom: "0.25rem",
                          borderRadius: "0.5rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                        }}
                      >
                        <SkeletonLoader
                          width="18px"
                          height="18px"
                          style={{ borderRadius: "4px", flexShrink: 0 }}
                        />
                        <SkeletonLoader width="70%" height="0.875rem" />
                      </div>
                    ))}
                  </div>
                ) : filteredChats.length === 0 ? (
                  <div
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      color: darkMode ? "#94a3b8" : "#374151",
                      fontSize: "0.875rem",
                    }}
                  >
                    {searchQuery ? "No chats found" : "No chats yet"}
                  </div>
                ) : (
                  (() => {
                    const sections = [
                      {
                        key: "none",
                        label: "Private / No Project",
                        chats: filteredChats.filter((c) => !c.projectId),
                      },
                      ...projects.map((p) => ({
                        key: p._id,
                        label: p.name,
                        chats: filteredChats.filter(
                          (c) => c.projectId === p._id,
                        ),
                      })),
                    ];

                    return (
                      <>
                        {sections.map(
                          (section) =>
                            section.chats.length > 0 && (
                              <div
                                key={section.key}
                                style={{ marginBottom: "1.5rem" }}
                              >
                                <h3
                                  style={{
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    color: darkMode ? "#94a3b8" : "#2563eb",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                    marginBottom: "0.75rem",
                                    padding: "0 0.75rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                  }}
                                >
                                  <Folder size={14} />
                                  {section.label}
                                </h3>
                                {section.chats.map((chat) => (
                                  <motion.div
                                    key={chat._id}
                                    whileHover={{
                                      backgroundColor: darkMode
                                        ? "#334155"
                                        : "rgba(191, 219, 254, 0.5)",
                                    }}
                                    onClick={() => {
                                      handleChatSelect(chat.chatId || chat._id);
                                    }}
                                    style={{
                                      padding: "0.75rem",
                                      borderRadius: "0.5rem",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.75rem",
                                      marginBottom: "0.25rem",
                                    }}
                                  >
                                    <MessageSquare
                                      size={18}
                                      style={{
                                        color: darkMode ? "#94a3b8" : "#1e3a8a",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <span
                                      style={{
                                        flex: 1,
                                        fontSize: "0.875rem",
                                        color: darkMode ? "#f1f5f9" : "#1e3a8a",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.5rem",
                                      }}
                                    >
                                      <span style={{ fontWeight: 600 }}>
                                        {chat.title ||
                                          chat.query ||
                                          "Untitled Chat"}
                                      </span>
                                    </span>
                                  </motion.div>
                                ))}
                              </div>
                            ),
                        )}
                      </>
                    );
                  })()
                )}
              </div>
            </motion.div>
          </div>,
          document.body,
        )
      }

      <EditorModal
        isOpen={editorOpen}
        title={editorTitle}
        content={editorContent}
        onClose={handleCloseEditor}
        onSave={handleEditorSave}
        darkMode={darkMode}
      />

      <NewProjectModal
        isOpen={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
        onCreate={handleCreateProject}
        existingProjects={projects}
        darkMode={darkMode}
      />



      <RenameChatDialog
        open={!!renameDialogChatId}
        anchorRect={renameDialogAnchor}
        chatId={renameDialogChatId}
        currentTitle={
          renameDialogChatId
            ? chats.find((c) => c._id === renameDialogChatId)?.title ||
            chats.find((c) => c._id === renameDialogChatId)?.query ||
            "Untitled Chat"
            : ""
        }
        onSave={(chatId, newTitle) => {
          saveChatTitle(chatId, newTitle);
          setRenameDialogChatId(null);
          setRenameDialogAnchor(null);
        }}
        onClose={() => {
          setRenameDialogChatId(null);
          setRenameDialogAnchor(null);
        }}
        darkMode={darkMode}
        label="Chat"
      />

      <AddToProjectDialog
        open={!!addToProjectDialogChatId}
        anchorRect={addToProjectDialogAnchor}
        chatId={addToProjectDialogChatId}
        currentProjectId={
          addToProjectDialogChatId
            ? (chats.find((c) => c._id === addToProjectDialogChatId)
              ?.projectId ?? null)
            : null
        }
        projects={projects}
        onSelect={(chatId, projectId) => {
          addChatToProject(chatId, projectId);
          setAddToProjectDialogChatId(null);
          setAddToProjectDialogAnchor(null);
        }}
        onClose={() => {
          setAddToProjectDialogChatId(null);
          setAddToProjectDialogAnchor(null);
        }}
        darkMode={darkMode}
        label="Chat"
      />

      {/* Project context menu */}
      <ProjectContextMenu
        open={!!projectContextMenu}
        position={projectContextMenu ? { x: projectContextMenu.x, y: projectContextMenu.y } : { x: 0, y: 0 }}
        project={projectContextMenu?.project}
        onRename={(project) => setRenameProjectDialog(project)}
        onArchive={(project) => handleArchiveProject(project)}
        onDelete={(project) => handleDeleteProject(project)}
        onClose={() => setProjectContextMenu(null)}
        darkMode={darkMode}
      />

      {/* Rename project dialog */}
      <RenameProjectDialog
        open={!!renameProjectDialog}
        project={renameProjectDialog}
        existingProjects={projects}
        onSave={handleRenameProject}
        onClose={() => setRenameProjectDialog(null)}
        darkMode={darkMode}
      />

      {/* Archived projects view */}
      <ArchivedProjectsView
        open={showArchivedProjects}
        onClose={() => setShowArchivedProjects(false)}
        onProjectRestored={async () => {
          const list = await fetchProjects("dashboard");
          setProjects(list || []);
        }}
        darkMode={darkMode}
      />

      {/* Portal for chat menu - renders outside sidebar to the right */}
      {
        openMenuId &&
        menuPosition[openMenuId] &&
        menuPosition[openMenuId].visible &&
        createPortal(
          <AnimatePresence mode="wait">
            {(() => {
              const chat = chats.find((c) => c._id === openMenuId);
              const isResearchChat =
                chat &&
                chat.chatType !== "debate" &&
                !(
                  chat.debateRounds &&
                  Array.isArray(chat.debateRounds) &&
                  chat.debateRounds.length > 0
                );
              const isCurrentlyProcessing =
                isResearchChat && queryLoading && currentChatId === openMenuId;
              const isMenuItemsDisabled = isCurrentlyProcessing;

              return (
                <motion.div
                  key={`menu-${openMenuId}`}
                  ref={(el) => {
                    if (el) {
                      menuRefs.current[openMenuId] = el;
                    }
                  }}
                  initial={{
                    opacity: 0,
                    y: -10, // Slide in from above
                    scale: 0.95,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                  }}
                  exit={{
                    opacity: 0,
                    y: -10,
                    scale: 0.95,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 25,
                    duration: 0.2,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  className="chat-menu-dropdown-portal"
                  style={{
                    position: "fixed",
                    top: `${menuPosition[openMenuId].top}px`,
                    left: `${menuPosition[openMenuId].left}px`,
                    backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                    border: `1px solid ${darkMode ? "#475569" : "#e5e7eb"}`,
                    borderRadius: "0.5rem",
                    boxShadow: darkMode
                      ? "0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 4px 10px -2px rgba(0, 0, 0, 0.3)"
                      : "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 4px 10px -2px rgba(0, 0, 0, 0.1)",
                    zIndex: 999999, // Very high z-index to ensure it's above everything
                    minWidth: "120px",
                    maxWidth: "150px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    pointerEvents: "auto",
                    isolation: "isolate", // Create new stacking context
                    willChange: "transform", // Optimize for animations
                    backfaceVisibility: "hidden", // Prevent flickering
                    WebkitBackfaceVisibility: "hidden",
                  }}
                >
                  <motion.button
                    type="button"
                    disabled={isMenuItemsDisabled}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (isMenuItemsDisabled) return;
                      setRenameDialogChatId(openMenuId);
                      setRenameDialogAnchor(menuPosition[openMenuId] || null);
                      setTimeout(() => setOpenMenuId(null), 50);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    whileHover={
                      !isMenuItemsDisabled
                        ? {
                          backgroundColor: darkMode ? "#334155" : "#f3f4f6",
                          x: 2,
                        }
                        : {}
                    }
                    whileTap={!isMenuItemsDisabled ? { scale: 0.98 } : {}}
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "transparent",
                      border: "none",
                      cursor: isMenuItemsDisabled ? "not-allowed" : "pointer",
                      fontSize: "0.875rem",
                      color: isMenuItemsDisabled
                        ? darkMode
                          ? "#64748b"
                          : "#9ca3af"
                        : darkMode
                          ? "#cbd5e1"
                          : "#374151",
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      transition: "all 0.2s",
                      opacity: isMenuItemsDisabled ? 0.5 : 1,
                    }}
                    title={
                      isMenuItemsDisabled ? "Chat is processing" : "Rename chat"
                    }
                  >
                    <Pencil size={16} style={{ flexShrink: 0 }} />
                    <span>Rename</span>
                  </motion.button>
                  <motion.button
                    type="button"
                    disabled={isMenuItemsDisabled}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (isMenuItemsDisabled) return;
                      setAddToProjectDialogChatId(openMenuId);
                      setAddToProjectDialogAnchor(
                        menuPosition[openMenuId] || null,
                      );
                      setTimeout(() => setOpenMenuId(null), 50);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    whileHover={
                      !isMenuItemsDisabled
                        ? {
                          backgroundColor: darkMode ? "#334155" : "#f3f4f6",
                          x: 2,
                        }
                        : {}
                    }
                    whileTap={!isMenuItemsDisabled ? { scale: 0.98 } : {}}
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "transparent",
                      border: "none",
                      cursor: isMenuItemsDisabled ? "not-allowed" : "pointer",
                      fontSize: "0.875rem",
                      color: isMenuItemsDisabled
                        ? darkMode
                          ? "#64748b"
                          : "#9ca3af"
                        : darkMode
                          ? "#cbd5e1"
                          : "#374151",
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      transition: "all 0.2s",
                      opacity: isMenuItemsDisabled ? 0.5 : 1,
                    }}
                    title={
                      isMenuItemsDisabled
                        ? "Chat is processing"
                        : "Add to project"
                    }
                  >
                    <FolderPlus size={16} style={{ flexShrink: 0 }} />
                    <span>Add to project</span>
                  </motion.button>
                  <div
                    style={{
                      height: "1px",
                      background: darkMode
                        ? "linear-gradient(to right, transparent, #475569, transparent)"
                        : "linear-gradient(to right, transparent, #e5e7eb, transparent)",
                      margin: "0 0.5rem",
                    }}
                  />
                  <motion.button
                    type="button"
                    disabled={isMenuItemsDisabled}
                    onMouseDown={async (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (isMenuItemsDisabled) return;

                      const chatIdToDelete = openMenuId;
                      if (!chatIdToDelete) {
                        console.error("No chat ID to delete");
                        return;
                      }
                      // Close menu first so only the confirmation dialog remains visible.
                      setOpenMenuId(null);
                      console.log(
                        "Delete button clicked for chat:",
                        chatIdToDelete,
                      );

                      try {
                        await deleteChat(chatIdToDelete, e);
                      } catch (error) {
                        console.error("Error in delete action:", error);
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    whileHover={
                      !isMenuItemsDisabled
                        ? {
                          backgroundColor: darkMode ? "#7f1d1d" : "#fee2e2",
                          x: 2,
                        }
                        : {}
                    }
                    whileTap={!isMenuItemsDisabled ? { scale: 0.98 } : {}}
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "transparent",
                      border: "none",
                      cursor: isMenuItemsDisabled ? "not-allowed" : "pointer",
                      fontSize: "0.875rem",
                      color: isMenuItemsDisabled
                        ? darkMode
                          ? "#64748b"
                          : "#9ca3af"
                        : darkMode
                          ? "#f87171"
                          : "#ef4444",
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      transition: "all 0.2s",
                      opacity: isMenuItemsDisabled ? 0.5 : 1,
                    }}
                    title={
                      isMenuItemsDisabled
                        ? "Chat is processing, delete unavailable"
                        : "Delete chat"
                    }
                  >
                    <Trash2 size={16} style={{ flexShrink: 0 }} />
                    <span>Delete</span>
                  </motion.button>
                </motion.div>
              );
            })()}
          </AnimatePresence>,
          document.body,
        )
      }
    </motion.div >
  );
}
